// lib/api/ehr-client.ts
// Hospital user API client — uses separate token storage from system admin client

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "/api";

// ============================================================================
// Token helpers — hospital-specific keys
// ============================================================================

export const getEhrAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("ehr_access_token");
};

export const getEhrRefreshToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("ehr_refresh_token");
};

export const setEhrTokens = (access: string, refresh: string): void => {
  localStorage.setItem("ehr_access_token", access);
  localStorage.setItem("ehr_refresh_token", refresh);
};

export const clearEhrTokens = (): void => {
  localStorage.removeItem("ehr_access_token");
  localStorage.removeItem("ehr_refresh_token");
  localStorage.removeItem("ehr_user");
};

// ============================================================================
// Headers
// ============================================================================

const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = { "Content-Type": "application/json" };
  const token = getEhrAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
};

// ============================================================================
// 401 handler — redirect to hospital login
// ============================================================================

const handleUnauthorized = (): void => {
  clearEhrTokens();
  if (typeof window !== "undefined") {
    window.location.href = "/login";
  }
};

// ============================================================================
// Token refresh
// ============================================================================

let isRefreshing = false;
let refreshCallbacks: Array<(token: string) => void> = [];

const refreshToken = async (): Promise<string | null> => {
  const refresh = getEhrRefreshToken();
  if (!refresh) return null;

  const response = await fetch(`${API_BASE_URL}/auth/hospital/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) return null;
  const data = await response.json();
  if (data.access) {
    localStorage.setItem("ehr_access_token", data.access);
    return data.access;
  }
  return null;
};

// ============================================================================
// Response handler with auto-refresh
// ============================================================================

async function handleResponse<T>(
  response: Response,
  retryFn?: () => Promise<Response>
): Promise<T> {
  // Auto-refresh on 401
  if (response.status === 401 && retryFn) {
    if (!isRefreshing) {
      isRefreshing = true;
      const newToken = await refreshToken();
      isRefreshing = false;
      refreshCallbacks.forEach((cb) => cb(newToken || ""));
      refreshCallbacks = [];

      if (newToken) {
        const retried = await retryFn();
        return handleResponse<T>(retried); // no retryFn to prevent loop
      }
    }
    handleUnauthorized();
    throw new Error("Session expired. Please login again.");
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));

    // DRF returns field-level errors as {"field": ["msg", ...]}
    // or ValidationError("msg") as ["msg"] (plain array)
    // Extract the first meaningful message from any field
    let fieldMessage: string | undefined;
    if (Array.isArray(error) && typeof error[0] === "string") {
      // Plain array from DRF ValidationError("message")
      fieldMessage = error[0];
    } else if (typeof error === "object" && error !== null) {
      for (const val of Object.values(error)) {
        if (Array.isArray(val) && typeof val[0] === "string") {
          fieldMessage = val[0];
          break;
        }
      }
    }

    const msg = error.detail || error.error || error.non_field_errors?.[0] || fieldMessage || `HTTP ${response.status}`;
    const err = new Error(msg) as any;
    err.response = { data: error, status: response.status };
    throw err;
  }

  if (response.status === 204) return {} as T;
  return response.json();
}

// ============================================================================
// EHR API Client class
// ============================================================================

class EhrApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async get<T>(endpoint: string): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    // cache: "no-store" — EHR data is real-time. Browser HTTP cache must not
    // serve stale GET responses (e.g. consultation worklist Refresh button
    // returning yesterday's queue).
    const response = await fetch(url, { method: "GET", headers: getHeaders(), cache: "no-store" });
    return handleResponse<T>(response, () =>
      fetch(url, { method: "GET", headers: getHeaders(), cache: "no-store" })
    );
  }

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<T>(response, () =>
      fetch(url, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(data),
      })
    );
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<T>(response, () =>
      fetch(url, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify(data),
      })
    );
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<T>(response, () =>
      fetch(url, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(data),
      })
    );
  }

  async delete<T>(endpoint: string): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: getHeaders(),
    });
    return handleResponse<T>(response, () =>
      fetch(url, { method: "DELETE", headers: getHeaders() })
    );
  }

  /**
   * Authenticated GET returning a binary Blob (PDFs, images), with the SAME
   * 401-refresh-and-retry as the JSON methods. Raw `fetch()` calls for PDFs
   * bypass token refresh, so once the short-lived access token expires they
   * 401 and the caller shows "Failed to generate PDF" — use this instead.
   */
  async getBlob(endpoint: string): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    let response = await fetch(url, { headers: getHeaders() });
    if (response.status === 401) {
      const newToken = await refreshToken();
      if (!newToken) {
        handleUnauthorized();
        throw new Error("Session expired. Please login again.");
      }
      response = await fetch(url, { headers: getHeaders() });
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.blob();
  }

  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: HeadersInit = {};
    const token = getEhrAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    // Do NOT set Content-Type — browser sets multipart boundary automatically
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });
    return handleResponse<T>(response, () => {
      const retryHeaders: HeadersInit = {};
      const retryToken = getEhrAccessToken();
      if (retryToken) retryHeaders["Authorization"] = `Bearer ${retryToken}`;
      return fetch(url, { method: "POST", headers: retryHeaders, body: formData });
    });
  }
}

export const ehrClient = new EhrApiClient(API_BASE_URL);

// ============================================================================
// Named API functions — Patients
// ============================================================================

import type {
  Patient,
  PatientListResponse,
  PatientFieldSchema,
  CreatePatientPayload,
  UpdatePatientPayload,
  CheckDuplicatesResponse,
  WhatsAppConnectionResponse,
  WhatsAppConnectPayload,
  WhatsAppNumber,
  WhatsAppNumberUpdate,
  WhatsAppTestSendResponse,
  WhatsAppMessageLogEntry,
} from "@/types/ehr";
import type {
  AbdmHiuConsentRequestListResponse,
  AbdmHiuConsentRequestDetail,
  AbdmHiuCreatePayload,
  AbdmHiuCreateResponse,
  AbdmHiuPullDataResponse,
  AbdmHiuActionResponse,
  AbdmHiuPatientSearchResponse,
} from "@/types/ehr";
import type {
  BmiBand,
  ChartPreviewResponse,
  DietChangeReason,
  DietChartTemplate,
  DietChartTemplateListItem,
  DietConsistency,
  DietOrder,
  DietPlan,
  DietPlanListItem,
  DietPreferenceTag,
  DietShift,
  DietType,
  DietaryMaster,
  DieteticAssessment,
  DerivedAnthropometry,
  DieteticLabReference,
  DieteticExternalReport,
  DietaryPrintConfig,
  EffectivePrintConfig,
  DieteticLabPanel,
  DieteticAssessmentListItem,
  DieteticFollowUp,
  DieteticFormConfig,
  DieteticRound,
  DieticianWardAssignment,
  DieticianWorklistResponse,
  ExchangeGroup,
  FeedingRoute,
  FoodItem,
  FunctionalStatus,
  GiSymptom,
  KitchenSheetResponse,
  MealSlot,
  MeasurementUnit,
  MetabolicStressLevel,
  NutritionDiagnosisMaster,
  PatientDietSummary,
  PrintLanguage,
  ResolvedChartBlock,
  ReviewInterval,
  SgaGrade,
  WardRoundResponse,
} from "@/types/ehr";

export const patientsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PatientListResponse>(`/patients/${qs}`);
  },
  get: (id: string) => ehrClient.get<Patient>(`/patients/${id}/`),
  create: (data: CreatePatientPayload) =>
    ehrClient.post<Patient>("/patients/", data),
  update: (id: string, data: UpdatePatientPayload) =>
    ehrClient.patch<Patient>(`/patients/${id}/`, data),
  deactivate: (id: string) => ehrClient.delete<void>(`/patients/${id}/`),
  fieldSchema: (locationId?: string) => {
    const qs = locationId ? `?location=${encodeURIComponent(locationId)}` : "";
    return ehrClient
      .get<{ schema: PatientFieldSchema[] }>(`/patients/field-schema/${qs}`)
      .then((res) => res.schema ?? []);
  },
  checkDuplicates: (body: { contact_number?: string; aadhaar_id?: string }) =>
    ehrClient.post<CheckDuplicatesResponse>(
      "/patients/check-duplicates/",
      body,
    ),
};

// ============================================================================
// Named API functions — Departments
// ============================================================================

import type {
  Department,
  PaginatedResponse,
  CreateDepartmentPayload,
  SubDepartment,
  CreateSubDepartmentPayload,
} from "@/types/ehr";

export const departmentsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<Department>>(`/departments/${qs}`);
  },
  get: (id: string) => ehrClient.get<Department>(`/departments/${id}/`),
  create: (data: CreateDepartmentPayload) =>
    ehrClient.post<Department>("/departments/", data),
  update: (id: string, data: Partial<CreateDepartmentPayload>) =>
    ehrClient.patch<Department>(`/departments/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/departments/${id}/`),
};

// ============================================================================
// Named API functions — Sub-Departments
// ============================================================================

export const subDepartmentsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<SubDepartment>>(
      `/sub-departments/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<SubDepartment>(`/sub-departments/${id}/`),
  create: (data: CreateSubDepartmentPayload) =>
    ehrClient.post<SubDepartment>("/sub-departments/", data),
  update: (id: string, data: Partial<CreateSubDepartmentPayload>) =>
    ehrClient.patch<SubDepartment>(`/sub-departments/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/sub-departments/${id}/`),
};

// ============================================================================
// Named API functions — Hospital Staff
// ============================================================================

import type {
  HospitalStaff,
  HospitalStaffListResponse,
  CreateStaffPayload,
  UpdateStaffPayload,
  ChangePasswordPayload,
  StaffDependent,
  StaffDependentListResponse,
  StaffDependentPayload,
} from "@/types/ehr";

export const staffApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<HospitalStaffListResponse>(`/hospital-users/${qs}`);
  },
  summary: (role?: string) => {
    const qs = role ? `?role=${encodeURIComponent(role)}` : "";
    return ehrClient.get<{
      total: number;
      active: number;
      inactive: number;
      suspended: number;
      new_this_month: number;
    }>(`/hospital-users/summary/${qs}`);
  },
  get: (id: string) => ehrClient.get<HospitalStaff>(`/hospital-users/${id}/`),
  me: () => ehrClient.get<HospitalStaff>("/hospital-users/me/"),
  // Laboratory dept doctors authorised to verify results.
  // Convenience over staffApi.list({ lab_verifiers: 'true' }) — used by
  // the Verification dialog to populate the verifying-doctor dropdown.
  labVerifiers: () =>
    ehrClient.get<HospitalStaffListResponse>(`/hospital-users/?lab_verifiers=true&limit=200`),
  create: (data: CreateStaffPayload) => {
    const { location_id, department_ids, primary_department_id, location_ids, primary_location_id, ...rest } = data;
    const payload: Record<string, unknown> = {
      ...rest,
      password2: data.password,
    };
    if (location_id) payload.location = location_id;
    if (department_ids?.length) payload.department_ids = department_ids;
    if (primary_department_id) payload.primary_department_id = primary_department_id;
    if (location_ids?.length) payload.location_ids = location_ids;
    if (primary_location_id) payload.primary_location_id = primary_location_id;
    return ehrClient.post<HospitalStaff>("/hospital-users/", payload);
  },
  update: (id: string, data: UpdateStaffPayload) => {
    const { location_id, department_ids, primary_department_id, location_ids, primary_location_id, ...rest } = data as any;
    const payload: Record<string, unknown> = { ...rest };
    if (location_id !== undefined) payload.location = location_id;
    if (department_ids !== undefined) payload.department_ids = department_ids;
    if (primary_department_id !== undefined) payload.primary_department_id = primary_department_id;
    if (location_ids !== undefined) payload.location_ids = location_ids;
    if (primary_location_id !== undefined) payload.primary_location_id = primary_location_id;
    return ehrClient.patch<HospitalStaff>(`/hospital-users/${id}/`, payload);
  },
  deactivate: (id: string) =>
    ehrClient.delete<void>(`/hospital-users/${id}/`),
  changePassword: (data: ChangePasswordPayload) =>
    ehrClient.post<void>("/hospital-users/change-password/", {
      ...data,
      new_password2: data.new_password,
    }),
  checkEmail: (email: string) =>
    ehrClient.get<{ available: boolean; message: string }>(
      `/hospital-users/check-email/?email=${encodeURIComponent(email)}`
    ),
  resetPassword: (userId: string, newPassword: string) =>
    ehrClient.post<{ detail: string }>(
      `/hospital-users/${userId}/reset-password/`,
      { new_password: newPassword }
    ),
  uploadSignature: (userId: string, file: File) => {
    const formData = new FormData();
    formData.append("signature_image", file);
    return ehrClient.postFormData<HospitalStaff>(
      `/hospital-users/${userId}/signature/`,
      formData
    );
  },
  deleteSignature: (userId: string) =>
    ehrClient.delete<HospitalStaff>(`/hospital-users/${userId}/signature/`),
};

/**
 * Staff dependents — the Dependents tab on the Staff master, and the source of
 * the Dependent picker on patient registration.
 *
 * Reads are open to any hospital user; writes are admin-only. The registration
 * counter adds a dependent it cannot find through the patient-create payload
 * (`staff_dependent_new`), not through `create` here.
 */
export const staffDependentsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<StaffDependentListResponse>(`/staff-dependents/${qs}`);
  },
  get: (id: string) => ehrClient.get<StaffDependent>(`/staff-dependents/${id}/`),
  create: (data: StaffDependentPayload) =>
    ehrClient.post<StaffDependent>("/staff-dependents/", data),
  update: (id: string, data: Partial<StaffDependentPayload>) =>
    ehrClient.patch<StaffDependent>(`/staff-dependents/${id}/`, data),
  /** Soft delete — frees the (name, relation) slot for reuse. */
  deactivate: (id: string) => ehrClient.delete<void>(`/staff-dependents/${id}/`),
};

// ============================================================================
// Named API functions — Payors
// ============================================================================

import type {
  Payor,
  PayorListItem,
  CreatePayorPayload,
  UpdatePayorPayload,
  PaginatedResponse as PayorPaginatedResponse,
} from "@/types/ehr";

export const payorsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PayorPaginatedResponse<PayorListItem>>(`/payors/${qs}`);
  },
  get: (id: string) => ehrClient.get<Payor>(`/payors/${id}/`),
  create: (data: CreatePayorPayload) =>
    ehrClient.post<Payor>("/payors/", data),
  update: (id: string, data: UpdatePayorPayload) =>
    ehrClient.patch<Payor>(`/payors/${id}/`, data),
  deactivate: (id: string) => ehrClient.delete<void>(`/payors/${id}/`),
};

// ============================================================================
// Named API functions — Services (Service Master)
// ============================================================================

import type {
  Service,
  ServiceListItem,
  CreateServicePayload,
  UpdateServicePayload,
  PaginatedResponse as ServicePaginatedResponse,
} from "@/types/ehr";

export const SERVICE_CATEGORIES = [
  { value: "anesthesia", label: "Anesthesia" },
  { value: "clinical_procedures", label: "Clinical Procedures" },
  { value: "consultation", label: "Consultation" },
  { value: "equipment", label: "EQUIPMENT" },
  { value: "investigation", label: "Investigation" },
  { value: "ipd_packages", label: "IPD Packages" },
  { value: "lab_investigation", label: "Lab Investigation" },
  { value: "medicine_and_consumable", label: "Medicine & Consumable" },
  { value: "misc", label: "Misc" },
  { value: "opd_packages", label: "OPD Packages" },
  { value: "operation_theatre", label: "Operation Theatre" },
  { value: "others", label: "Others" },
  { value: "procedures", label: "Procedures" },
  { value: "radiology_investigation", label: "Radiology Investigation" },
  { value: "registration", label: "Registration" },
  { value: "reusable_item", label: "Reusable Item" },
  { value: "room_charge", label: "Room Charge" },
  { value: "surgery", label: "Surgery" },
  { value: "surgery_grade", label: "Surgery Grade" },
  { value: "visit_fees", label: "Visit Fees" },
] as const;

export const servicesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<ServicePaginatedResponse<ServiceListItem>>(`/services/${qs}`);
  },
  get: (id: string) => ehrClient.get<Service>(`/services/${id}/`),
  create: (data: CreateServicePayload) =>
    ehrClient.post<Service>("/services/", data),
  update: (id: string, data: UpdateServicePayload) =>
    ehrClient.patch<Service>(`/services/${id}/`, data),
  deactivate: (id: string) => ehrClient.delete<void>(`/services/${id}/`),
  categories: () =>
    ehrClient.get<{ value: string; label: string }[]>("/services/categories/"),
};

// ============================================================================
// Named API functions — Tariffs
// ============================================================================

import type {
  Tariff,
  TariffListItem,
  CreateTariffPayload,
  UpdateTariffPayload,
  TariffItem,
  CreateTariffItemPayload,
  BulkTariffItemPayload,
  PayorTariffMapping,
  CreatePayorTariffMappingPayload,
  PriceLookupResult,
} from "@/types/ehr";

export const tariffsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<TariffListItem>>(`/tariffs/${qs}`);
  },
  get: (id: string) => ehrClient.get<Tariff>(`/tariffs/${id}/`),
  create: (data: CreateTariffPayload) =>
    ehrClient.post<Tariff>("/tariffs/", data),
  update: (id: string, data: UpdateTariffPayload) =>
    ehrClient.patch<Tariff>(`/tariffs/${id}/`, data),
  deactivate: (id: string) => ehrClient.delete<void>(`/tariffs/${id}/`),

  // Items
  listItems: (tariffId: string, params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<TariffItem>>(
      `/tariffs/${tariffId}/items/${qs}`
    );
  },
  addItem: (tariffId: string, data: CreateTariffItemPayload) =>
    ehrClient.post<TariffItem>(`/tariffs/${tariffId}/items/add/`, data),
  updateItem: (tariffId: string, itemId: string, data: Partial<CreateTariffItemPayload>) =>
    ehrClient.patch<TariffItem>(`/tariffs/${tariffId}/items/${itemId}/`, data),
  deleteItem: (tariffId: string, itemId: string) =>
    ehrClient.delete<void>(`/tariffs/${tariffId}/items/${itemId}/delete/`),
  bulkItems: (tariffId: string, data: BulkTariffItemPayload) =>
    ehrClient.post<TariffItem[]>(`/tariffs/${tariffId}/items/bulk/`, data),
  priceMatrix: (tariffId: string) =>
    ehrClient.get<{ tariff_id: string; tariff_name: string; tariff_code: string; items: TariffItem[] }>(
      `/tariffs/${tariffId}/price-matrix/`
    ),
  priceLookup: (params: { service_id: string; payor_id?: string; location_id?: string }) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
    ).toString();
    return ehrClient.get<PriceLookupResult>(`/tariffs/price-lookup/?${qs}`);
  },
};

// ============================================================================
// Named API functions — Health Packages
// ============================================================================

import type {
  HealthPackage,
  HealthPackageListItem,
  HealthPackageComponent,
  HealthPackageComponentPayload,
  CreateHealthPackagePayload,
  UpdateHealthPackagePayload,
  PackagePricePreview,
  HealthPackageSale,
  HealthPackageSaleListItem,
  HealthPackageSaleComponent,
  SellHealthPackagePayload,
  BookPackageAppointmentPayload,
  HealthCheckReportData,
} from "@/types/ehr";

export const healthPackagesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<HealthPackageListItem>>(`/health-packages/${qs}`);
  },
  get: (id: string) => ehrClient.get<HealthPackage>(`/health-packages/${id}/`),
  create: (data: CreateHealthPackagePayload) =>
    ehrClient.post<HealthPackage>("/health-packages/", data),
  update: (id: string, data: UpdateHealthPackagePayload) =>
    ehrClient.patch<HealthPackage>(`/health-packages/${id}/`, data),
  deactivate: (id: string) => ehrClient.delete<void>(`/health-packages/${id}/`),

  // Components
  listComponents: (packageId: string) =>
    ehrClient.get<HealthPackageComponent[]>(`/health-packages/${packageId}/components/`),
  addComponent: (packageId: string, data: HealthPackageComponentPayload) =>
    ehrClient.post<HealthPackageComponent>(`/health-packages/${packageId}/components/add/`, data),
  updateComponent: (
    packageId: string,
    componentId: string,
    data: Partial<HealthPackageComponentPayload>,
  ) =>
    ehrClient.patch<HealthPackageComponent>(
      `/health-packages/${packageId}/components/${componentId}/`,
      data,
    ),
  deleteComponent: (packageId: string, componentId: string) =>
    ehrClient.delete<void>(`/health-packages/${packageId}/components/${componentId}/delete/`),
  bulkComponents: (packageId: string, components: HealthPackageComponentPayload[]) =>
    ehrClient.post<HealthPackageComponent[]>(
      `/health-packages/${packageId}/components/bulk/`,
      { components },
    ),

  /** Package price for a payor, plus the list value of its components. */
  pricePreview: (packageId: string, params?: { payor_id?: string; location_id?: string }) => {
    const clean = Object.fromEntries(
      Object.entries(params ?? {}).filter(([, v]) => v),
    ) as Record<string, string>;
    const qs = Object.keys(clean).length ? "?" + new URLSearchParams(clean).toString() : "";
    return ehrClient.get<PackagePricePreview>(`/health-packages/${packageId}/price-preview/${qs}`);
  },
};

export const healthPackageSalesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<HealthPackageSaleListItem>>(
      `/health-package-sales/${qs}`,
    );
  },
  get: (id: string) => ehrClient.get<HealthPackageSale>(`/health-package-sales/${id}/`),

  /** Sell a package onto a draft bill. Creates ONE bill line plus the entitlement. */
  sell: (data: SellHealthPackagePayload) =>
    ehrClient.post<HealthPackageSale>("/health-package-sales/", data),

  /** Draft bills only — once billed, the money goes back through the refund flow. */
  cancel: (id: string, reason?: string) =>
    ehrClient.post<HealthPackageSale>(`/health-package-sales/${id}/cancel/`, {
      reason: reason ?? "",
    }),

  waiveComponent: (saleId: string, componentId: string, reason: string) =>
    ehrClient.post<HealthPackageSaleComponent>(
      `/health-package-sales/${saleId}/components/${componentId}/waive/`,
      { reason },
    ),

  bookAppointment: (
    saleId: string,
    componentId: string,
    data: BookPackageAppointmentPayload,
  ) =>
    ehrClient.post<HealthPackageSaleComponent>(
      `/health-package-sales/${saleId}/components/${componentId}/book-appointment/`,
      data,
    ),

  saveSummary: (id: string, data: { summary_findings: string; doctor_advice: string }) =>
    ehrClient.post<HealthPackageSale>(`/health-package-sales/${id}/summary/`, data),

  reportData: (id: string) =>
    ehrClient.get<HealthCheckReportData>(`/health-package-sales/${id}/report-data/`),
};

// ============================================================================
// Named API functions — Payor-Tariff Mappings
// ============================================================================

export const payorTariffMappingsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PayorTariffMapping>>(
      `/payor-tariff-mappings/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<PayorTariffMapping>(`/payor-tariff-mappings/${id}/`),
  create: (data: CreatePayorTariffMappingPayload) =>
    ehrClient.post<PayorTariffMapping>("/payor-tariff-mappings/", data),
  update: (id: string, data: Partial<CreatePayorTariffMappingPayload>) =>
    ehrClient.patch<PayorTariffMapping>(`/payor-tariff-mappings/${id}/`, data),
  delete: (id: string) =>
    ehrClient.delete<void>(`/payor-tariff-mappings/${id}/`),
  /**
   * Renumber one payor + scope's mappings 1..N atomically.
   *
   * Do NOT reorder by PATCHing each row's priority individually: every
   * permutation passes through a state where two rows share a number, which the
   * duplicate-priority guard rejects — so each PATCH 400s and the reorder is
   * left half-applied. `mapping_ids` is the full desired order.
   */
  reorder: (data: { payor: string; location?: string | null; mapping_ids: string[] }) =>
    ehrClient.post<PayorTariffMapping[]>("/payor-tariff-mappings/reorder/", data),
};

// ============================================================================
// Named API functions — Encounters
// ============================================================================

import type {
  Encounter,
  EncounterListItem,
  CreateEncounterPayload,
} from "@/types/ehr";

export const encountersApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<EncounterListItem>>(
      `/encounters/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<Encounter>(`/encounters/${id}/`),
  create: (data: CreateEncounterPayload) =>
    ehrClient.post<Encounter>("/encounters/", data),
  update: (id: string, data: Partial<CreateEncounterPayload>) =>
    ehrClient.patch<Encounter>(`/encounters/${id}/`, data),
  cancel: (id: string) => ehrClient.delete<void>(`/encounters/${id}/`),
  unbilledServices: (id: string) =>
    ehrClient.get<import("@/types/ehr").UnbilledServicesResponse>(
      `/encounters/${id}/unbilled-services/`
    ),
};

// ============================================================================
// Named API functions — Bills
// ============================================================================

import type {
  Bill,
  BillListItem,
  BillItem as BillItemType,
  BillPrintCopyResponse,
  AddBillItemPayload,
  UpdateBillItemPayload,
} from "@/types/ehr";

export const billsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<BillListItem>>(`/bills/${qs}`);
  },
  get: (id: string) => ehrClient.get<Bill>(`/bills/${id}/`),
  updateDraft: (id: string, data: Record<string, unknown>) =>
    ehrClient.patch<Bill>(`/bills/${id}/update-draft/`, data),

  // Items
  addItem: (billId: string, data: AddBillItemPayload) =>
    ehrClient.post<BillItemType>(`/bills/${billId}/items/add/`, data),
  updateItem: (billId: string, itemId: string, data: UpdateBillItemPayload) =>
    ehrClient.patch<BillItemType>(`/bills/${billId}/items/${itemId}/`, data),
  removeItem: (billId: string, itemId: string) =>
    ehrClient.delete<void>(`/bills/${billId}/items/${itemId}/remove/`),

  // Lifecycle
  finalize: (billId: string) =>
    ehrClient.post<Bill>(`/bills/${billId}/finalize/`, {}),
  cancel: (billId: string) =>
    ehrClient.post<Bill>(`/bills/${billId}/cancel/`, {}),
  // Hard-delete a pristine OPD draft (backend rejects non-draft / non-OPD /
  // bills carrying any payment, advance adjustment, refund or settlement).
  deleteDraft: (billId: string) =>
    ehrClient.post<void>(`/bills/${billId}/delete-draft/`, {}),
  recalculate: (billId: string) =>
    ehrClient.post<Bill>(`/bills/${billId}/recalculate/`, {}),

  // Campaign
  applyCampaign: (billId: string, data: { campaign_id: string | null }) =>
    ehrClient.post<Bill>(`/bills/${billId}/apply-campaign/`, data),

  // Settle from advances (FIFO)
  settleFromAdvances: (billId: string, data: { amount: number | string }) =>
    ehrClient.post<{
      bill: Bill;
      adjustments: Array<{ advance_id: string; advance_number: string; amount: string }>;
      total_adjusted: string;
      requested_amount: string;
      capped_reason: "requested" | "available_balance" | "bill_balance";
    }>(`/bills/${billId}/settle-from-advances/`, data),

  /**
   * Claim this print as the ORIGINAL or a DUPLICATE — call BEFORE rendering.
   *
   * The server decides and stamps under a row lock: the client must never work
   * it out from `bill.first_printed_at`, or two cashiers pressing Print at the
   * same instant would each be handed an original, which is exactly the false
   * refund this prevents. A draft returns "interim" and stamps nothing.
   */
  markPrinted: (billId: string) =>
    ehrClient.post<BillPrintCopyResponse>(`/bills/${billId}/mark-printed/`, {}),
};

// ============================================================================
// Named API functions — OPD Billing Workflow
// ============================================================================

import type {
  OpdBillingCreatePayload,
  OpdBillingCreateResponse,
  ProceedToPayPayload,
} from "@/types/ehr";

export const opdBillingApi = {
  create: (data: OpdBillingCreatePayload) =>
    ehrClient.post<OpdBillingCreateResponse>("/opd-billing/", data),
  /**
   * Settle a bill.
   *
   * ⚠️ **A 200 is checked, not trusted.** `proceed_to_pay` finalises the bill
   * before it books the payment, so a successful call can never come back
   * still `draft`. On 2026-09-10 it did: a swallowed IntegrityError in the
   * post-payment fan-out rolled the whole transaction back, and because
   * nothing was re-raised the endpoint answered 200 with a bill serialized
   * AFTER that rollback. Every caller then toasted "Payment completed",
   * navigated to the bill, and the router bounced them back to the draft they
   * had just "paid" — with no receipt anywhere.
   *
   * The server-side defect is fixed (billing/views.py savepoints each fan-out),
   * but this assertion is the cheap independent one: it belongs here rather
   * than in the four call sites, where it would drift. A silent 200 that saved
   * nothing must read as a failure, not a success.
   */
  proceedToPay: async (data: ProceedToPayPayload) => {
    const bill = await ehrClient.post<Bill>("/opd-billing/proceed-to-pay/", data);
    if (bill?.status === "draft") {
      throw new Error(
        "The payment was not saved — the bill is still a draft. " +
          "Nothing has been collected. Please try again, and report this if it repeats.",
      );
    }
    return bill;
  },
  supplementary: (data: import("@/types/ehr").SupplementaryBillPayload) =>
    ehrClient.post<OpdBillingCreateResponse>(
      "/opd-billing/supplementary/",
      data
    ),
  calculateRoomCharges: (billId: string) =>
    ehrClient.post<import("@/types/ehr").CalculateRoomChargesResponse>(
      "/opd-billing/calculate-room-charges/",
      { bill_id: billId }
    ),
  reverseRoomCharge: (billId: string, itemId: string, reason?: string) =>
    ehrClient.post<void>(
      `/bills/${billId}/items/${itemId}/reverse-room-charge/`,
      { reason: reason ?? "" }
    ),
};

// ============================================================================
// Named API functions — Payments
// ============================================================================

import type {
  Payment,
  PaymentListItem,
  CreatePaymentPayload,
} from "@/types/ehr";

export const paymentsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PaymentListItem>>(`/payments/${qs}`);
  },
  get: (id: string) => ehrClient.get<Payment>(`/payments/${id}/`),
  create: (data: CreatePaymentPayload) =>
    ehrClient.post<Payment>("/payments/", data),
};

// ============================================================================
// Named API functions — Advances
// ============================================================================

import type {
  Advance,
  AdvanceListItem,
  AdvanceStats,
  AdvancePatientSummary,
  AdvanceGroupedRow,
  AdvanceAdjustment,
  CreateAdvancePayload,
  AdjustAdvancePayload,
} from "@/types/ehr";

export const advancesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<AdvanceListItem>>(`/advances/${qs}`);
  },
  get: (id: string) => ehrClient.get<Advance>(`/advances/${id}/`),
  create: (data: CreateAdvancePayload) =>
    ehrClient.post<Advance>("/advances/", data),
  adjust: (advanceId: string, data: AdjustAdvancePayload) =>
    ehrClient.post<Advance>(`/advances/${advanceId}/adjust/`, data),
  patientAdvances: (params: { patient_id: string; status?: string }) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
    ).toString();
    return ehrClient.get<PaginatedResponse<Advance>>(
      `/advances/patient-advances/?${qs}`
    );
  },
  stats: () => ehrClient.get<AdvanceStats>(`/advances/stats/`),
  patientSummary: (patientId: string) =>
    ehrClient.get<AdvancePatientSummary>(
      `/advances/patient-summary/?patient_id=${patientId}`
    ),
  grouped: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<AdvanceGroupedRow>>(
      `/advances/grouped/${qs}`
    );
  },
  patientAdjustments: (patientId: string) =>
    ehrClient.get<PaginatedResponse<AdvanceAdjustment>>(
      `/advances/patient-adjustments/?patient_id=${patientId}&limit=200`
    ),
};

// ============================================================================
// Named API functions — Campaigns (Marketing)
// ============================================================================

import type {
  Campaign,
  CampaignListItem,
  CreateCampaignPayload,
  UpdateCampaignPayload,
} from "@/types/ehr";

export const campaignsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<CampaignListItem>>(
      `/campaigns/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<Campaign>(`/campaigns/${id}/`),
  create: (data: CreateCampaignPayload) =>
    ehrClient.post<Campaign>("/campaigns/", data),
  update: (id: string, data: UpdateCampaignPayload) =>
    ehrClient.patch<Campaign>(`/campaigns/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/campaigns/${id}/`),
  active: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<CampaignListItem>>(
      `/campaigns/active/${qs}`
    );
  },
};

// ============================================================================
// Named API functions — Locations (for hospital admin)
// ============================================================================

import type { Location } from "@/types/ehr";

export const locationsEhrApi = {
  list: (hospitalId: string) =>
    ehrClient.get<Location[]>(`/hospitals/${hospitalId}/locations/`),
};

import type { LocationVisitTypeConfig } from "@/types/ehr";

export const visitTypesApi = {
  get: (locationId: string) =>
    ehrClient.get<LocationVisitTypeConfig>(
      `/location-visit-types/?location_id=${locationId}`
    ),
  patch: (locationId: string, data: Partial<LocationVisitTypeConfig>) =>
    ehrClient.patch<LocationVisitTypeConfig>(
      `/location-visit-types/?location_id=${locationId}`,
      data
    ),
};

// ============================================================================
// Named API functions — Hospital Auth
// ============================================================================

import type { HospitalLoginResponse } from "@/types/ehr";

// ============================================================================
// Appointments API
// ============================================================================

import type {
  AppointmentSettings,
  UpdateAppointmentSettingsPayload,
  DoctorSchedule,
  OpScheduleResponse,
  CreateDoctorSchedulePayload,
  UpdateDoctorSchedulePayload,
  DoctorLeave,
  CreateDoctorLeavePayload,
  UpdateDoctorLeavePayload,
  ScheduleOverride,
  CreateScheduleOverridePayload,
  AvailableSlotsResponse,
  Appointment,
  AppointmentListItem,
  CreateAppointmentPayload,
  CreateWalkInPayload,
  WalkInPlacement,
  WalkInPreviewResponse,
  ReschedulePayload,
  CancelAppointmentPayload,
  AppointmentCalendarResponse,
  PatientAppointmentHistory,
  LeaveConflictCheck,
  AppointmentReportsSummary,
} from "@/types/ehr";

export const appointmentSettingsApi = {
  get: () => ehrClient.get<AppointmentSettings>("/appointment-settings/"),
  update: (data: UpdateAppointmentSettingsPayload) =>
    ehrClient.patch<AppointmentSettings>("/appointment-settings/", data),
};

export const doctorSchedulesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<DoctorSchedule>>(
      `/doctor-schedules/${qs}`
    );
  },
  byDoctor: (doctorId: string) =>
    ehrClient.get<DoctorSchedule[]>(
      `/doctor-schedules/by-doctor/?doctor_id=${doctorId}`
    ),
  /** Read-only OP schedule board (departments x weekdays), pivoted server-side.
   *  `date` is any day in the week of interest; the server snaps to its Monday. */
  opSchedule: (params: {
    location: string;
    date?: string;
    department?: string;
  }) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v) as [string, string][]
    ).toString();
    return ehrClient.get<OpScheduleResponse>(`/doctor-schedules/op-schedule/?${qs}`);
  },
  get: (id: string) =>
    ehrClient.get<DoctorSchedule>(`/doctor-schedules/${id}/`),
  create: (data: CreateDoctorSchedulePayload) =>
    ehrClient.post<DoctorSchedule>("/doctor-schedules/", data),
  bulkCreate: (schedules: CreateDoctorSchedulePayload[]) =>
    ehrClient.post<DoctorSchedule[]>("/doctor-schedules/bulk-create/", schedules),
  update: (id: string, data: UpdateDoctorSchedulePayload) =>
    ehrClient.patch<DoctorSchedule>(`/doctor-schedules/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/doctor-schedules/${id}/`),
};

export const doctorLeavesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<DoctorLeave>>(
      `/doctor-leaves/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<DoctorLeave>(`/doctor-leaves/${id}/`),
  create: (data: CreateDoctorLeavePayload) =>
    ehrClient.post<DoctorLeave>("/doctor-leaves/", data),
  update: (id: string, data: UpdateDoctorLeavePayload) =>
    ehrClient.patch<DoctorLeave>(`/doctor-leaves/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/doctor-leaves/${id}/`),
};

export const scheduleOverridesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ScheduleOverride>>(
      `/schedule-overrides/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<ScheduleOverride>(`/schedule-overrides/${id}/`),
  create: (data: CreateScheduleOverridePayload) =>
    ehrClient.post<ScheduleOverride>("/schedule-overrides/", data),
  update: (id: string, data: Partial<CreateScheduleOverridePayload>) =>
    ehrClient.patch<ScheduleOverride>(`/schedule-overrides/${id}/`, data),
  delete: (id: string) =>
    ehrClient.delete<void>(`/schedule-overrides/${id}/`),
};

export const availableSlotsApi = {
  get: (doctorId: string, date: string, locationId?: string) => {
    const params: Record<string, string> = {
      doctor_id: doctorId,
      date,
    };
    if (locationId) params.location_id = locationId;
    return ehrClient.get<AvailableSlotsResponse>(
      `/available-slots/?${new URLSearchParams(params).toString()}`
    );
  },
};

export const appointmentsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<AppointmentListItem>>(
      `/appointments/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<Appointment>(`/appointments/${id}/`),
  create: (data: CreateAppointmentPayload) =>
    ehrClient.post<Appointment>("/appointments/", data),
  update: (id: string, data: Partial<CreateAppointmentPayload>) =>
    ehrClient.patch<Appointment>(`/appointments/${id}/`, data),
  checkIn: (id: string) =>
    ehrClient.post<Appointment>(`/appointments/${id}/check-in/`, {}),
  startConsultation: (id: string) =>
    ehrClient.post<Appointment>(`/appointments/${id}/start-consultation/`, {}),
  complete: (id: string) =>
    ehrClient.post<Appointment>(`/appointments/${id}/complete/`, {}),
  cancel: (id: string, data: CancelAppointmentPayload) =>
    ehrClient.post<Appointment>(`/appointments/${id}/cancel/`, data),
  noShow: (id: string) =>
    ehrClient.post<Appointment>(`/appointments/${id}/no-show/`, {}),
  reschedule: (id: string, data: ReschedulePayload) =>
    ehrClient.post<Appointment>(`/appointments/${id}/reschedule/`, data),
  /** Server decides date/time/slot — see CreateWalkInPayload. Refuses with
   *  `code: 'no_walkin_slot'` + the `overflow` placement when the roster is
   *  exhausted; re-send with `allow_overflow: true` once the user confirms. */
  walkIn: (data: CreateWalkInPayload) =>
    ehrClient.post<Appointment & { placement?: WalkInPlacement }>(
      "/appointments/walk-in/", data),
  /** What this doctor's next walk-in would get, without writing anything.
   *  Backed by the same function the booking uses — never re-derive a token in
   *  the UI (BUG_LOG appointments 2026-07-28). */
  walkInPreview: (params: { doctor: string; location?: string; session_code?: string }) =>
    ehrClient.get<WalkInPreviewResponse>(
      `/appointments/walk-in-preview/?${new URLSearchParams(
        Object.entries(params).filter(([, v]) => !!v) as [string, string][],
      ).toString()}`,
    ),
  linkPatient: (id: string, patientId: string) =>
    ehrClient.post<Appointment>(`/appointments/${id}/link-patient/`, { patient_id: patientId }),
  calendar: (params: Record<string, string>) =>
    ehrClient.get<AppointmentCalendarResponse>(
      `/appointments/calendar/?${new URLSearchParams(params).toString()}`
    ),
  queue: (doctorId: string, date: string) =>
    ehrClient.get<{ queue: AppointmentListItem[]; date: string }>(
      `/appointments/queue/?doctor_id=${doctorId}&date=${date}`
    ),
  departmentView: (departmentId: string, date: string) =>
    ehrClient.get<Record<string, AppointmentListItem[]>>(
      `/appointments/department-view/?department_id=${departmentId}&date=${date}`
    ),
  patientHistory: (patientId: string) =>
    ehrClient.get<PatientAppointmentHistory>(
      `/appointments/patient-history/?patient_id=${patientId}`
    ),
  reportsSummary: (params: Record<string, string>) =>
    ehrClient.get<AppointmentReportsSummary>(
      `/appointments/reports/summary/?${new URLSearchParams(params).toString()}`
    ),
};

export const doctorLeaveConflictApi = {
  check: (doctorId: string, startDate: string, endDate: string) =>
    ehrClient.get<LeaveConflictCheck>(
      `/doctor-leaves/conflict-check/?doctor_id=${doctorId}&start_date=${startDate}&end_date=${endDate}`
    ),
};

// ============================================================================
// Named API functions — Stores (Inventory)
// ============================================================================

import type {
  Store,
  StoreListItem,
  CreateStorePayload,
  UpdateStorePayload,
  UnitOfMeasure,
  CreateUnitPayload,
  UpdateUnitPayload,
  UnitConversion,
  CreateUnitConversionPayload,
  ItemCategory,
  CreateItemCategoryPayload,
  ItemSubcategory,
  CreateItemSubcategoryPayload,
  ItemClass,
  CreateItemClassPayload,
  UpdateItemClassPayload,
  CIMCategory,
  CreateCIMCategoryPayload,
  CIMSubcategory,
  CreateCIMSubcategoryPayload,
  Generic,
  CreateGenericPayload,
  ChargeSetup,
  ChargeSetupListItem,
  CreateChargeSetupPayload,
  UpdateChargeSetupPayload,
  HSNGroup,
  CreateHSNGroupPayload,
  UpdateHSNGroupPayload,
  HSNCode,
  CreateHSNCodePayload,
  UpdateHSNCodePayload,
  Item,
  ItemListItem,
  CreateItemPayload,
  UpdateItemPayload,
  ItemImportRequest,
  ItemImportResponse,
  Supplier,
  SupplierListItem,
  CreateSupplierPayload,
  UpdateSupplierPayload,
  GstState,
  StoreInventoryItem,
  StoreInventoryListItem,
  CreateStoreInventoryPayload,
  StockSummaryItem,
  InventorySetup,
  CreateInventorySetupPayload,
  Indent,
  IndentListItem,
  CreateIndentPayload,
  ApproveIndentPayload,
  RejectIndentPayload,
  IssueStockPayload,
  StockIssue,
  StockIssueListItem,
  PendingIssueSummaryItem,
  StockReturn,
  StockReturnListItem,
  CreateStockReturnPayload,
  DirectIssue,
  DirectIssueListItem,
  CreateDirectIssuePayload,
  IndentToPRPayload,
  ReorderRule,
  ReorderRuleBulkUpsertPayload,
  ReorderStoreItemsResponse,
  ReorderRun,
  ReorderRunDetail,
  RunAutoReorderPayload,
  PurchaseRequisition,
  PRListItem,
  CreatePRPayload,
  ApprovePRPayload,
  RejectPRPayload,
  PurchaseOrder,
  POListItem,
  POTerm,
  CreatePOPayload,
  UpdatePOPayload,
  ApprovePOPayload,
  RejectPOPayload,
  CancelPOPayload,
  GRN,
  GRNListItem,
  CreateGRNPayload,
  VerifyGRNPayload,
} from "@/types/ehr";

export const storesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<StoreListItem>>(`/stores/${qs}`);
  },
  get: (id: string) => ehrClient.get<Store>(`/stores/${id}/`),
  create: (data: CreateStorePayload) =>
    ehrClient.post<Store>("/stores/", data),
  update: (id: string, data: UpdateStorePayload) =>
    ehrClient.patch<Store>(`/stores/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/stores/${id}/`),
  toggleActive: (id: string) =>
    ehrClient.post<Store>(`/stores/${id}/toggle-active/`, {}),
};

// ============================================================================
// Named API functions — Units of Measure (Inventory)
// ============================================================================

export const unitsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<UnitOfMeasure>>(
      `/units-of-measure/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<UnitOfMeasure>(`/units-of-measure/${id}/`),
  create: (data: CreateUnitPayload) =>
    ehrClient.post<UnitOfMeasure>("/units-of-measure/", data),
  update: (id: string, data: UpdateUnitPayload) =>
    ehrClient.patch<UnitOfMeasure>(`/units-of-measure/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/units-of-measure/${id}/`),
};

// ============================================================================
// Named API functions — Unit Conversions (Inventory)
// ============================================================================

export const unitConversionsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<UnitConversion>>(
      `/unit-conversions/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<UnitConversion>(`/unit-conversions/${id}/`),
  create: (data: CreateUnitConversionPayload) =>
    ehrClient.post<UnitConversion>("/unit-conversions/", data),
  update: (id: string, data: Partial<CreateUnitConversionPayload>) =>
    ehrClient.patch<UnitConversion>(`/unit-conversions/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/unit-conversions/${id}/`),
};

// ============================================================================
// Named API functions — Item Categories (Inventory)
// ============================================================================

export const itemCategoriesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ItemCategory>>(
      `/item-categories/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<ItemCategory>(`/item-categories/${id}/`),
  create: (data: CreateItemCategoryPayload) =>
    ehrClient.post<ItemCategory>("/item-categories/", data),
  update: (id: string, data: Partial<CreateItemCategoryPayload>) =>
    ehrClient.patch<ItemCategory>(`/item-categories/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/item-categories/${id}/`),
};

// ============================================================================
// Named API functions — Item Subcategories (Inventory, level-2)
// ============================================================================

export const itemSubcategoriesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ItemSubcategory>>(
      `/item-subcategories/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<ItemSubcategory>(`/item-subcategories/${id}/`),
  create: (data: CreateItemSubcategoryPayload) =>
    ehrClient.post<ItemSubcategory>("/item-subcategories/", data),
  update: (id: string, data: Partial<CreateItemSubcategoryPayload>) =>
    ehrClient.patch<ItemSubcategory>(`/item-subcategories/${id}/`, data),
  delete: (id: string) =>
    ehrClient.delete<void>(`/item-subcategories/${id}/`),
};

// ============================================================================
// Named API functions — Item Classes (Inventory). 14 system rows + customs.
// ============================================================================

export const itemClassesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ItemClass>>(`/item-classes/${qs}`);
  },
  get: (id: string) => ehrClient.get<ItemClass>(`/item-classes/${id}/`),
  create: (data: CreateItemClassPayload) =>
    ehrClient.post<ItemClass>("/item-classes/", data),
  update: (id: string, data: UpdateItemClassPayload) =>
    ehrClient.patch<ItemClass>(`/item-classes/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/item-classes/${id}/`),
};

// ============================================================================
// Named API functions — CIM Categories (Inventory)
// ============================================================================

export const cimCategoriesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<CIMCategory>>(
      `/cim-categories/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<CIMCategory>(`/cim-categories/${id}/`),
  create: (data: CreateCIMCategoryPayload) =>
    ehrClient.post<CIMCategory>("/cim-categories/", data),
  update: (id: string, data: Partial<CreateCIMCategoryPayload>) =>
    ehrClient.patch<CIMCategory>(`/cim-categories/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/cim-categories/${id}/`),
};

// ============================================================================
// Named API functions — CIM Subcategories (Inventory)
// ============================================================================

export const cimSubcategoriesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<CIMSubcategory>>(
      `/cim-subcategories/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<CIMSubcategory>(`/cim-subcategories/${id}/`),
  create: (data: CreateCIMSubcategoryPayload) =>
    ehrClient.post<CIMSubcategory>("/cim-subcategories/", data),
  update: (id: string, data: Partial<CreateCIMSubcategoryPayload>) =>
    ehrClient.patch<CIMSubcategory>(`/cim-subcategories/${id}/`, data),
  delete: (id: string) =>
    ehrClient.delete<void>(`/cim-subcategories/${id}/`),
};

// ============================================================================
// Named API functions — Generics (Inventory)
// ============================================================================

export const genericsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<Generic>>(`/generics/${qs}`);
  },
  get: (id: string) => ehrClient.get<Generic>(`/generics/${id}/`),
  create: (data: CreateGenericPayload) =>
    ehrClient.post<Generic>("/generics/", data),
  update: (id: string, data: Partial<CreateGenericPayload>) =>
    ehrClient.patch<Generic>(`/generics/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/generics/${id}/`),
};

// ============================================================================
// Named API functions — Charge Setup (Inventory)
// ============================================================================

export const chargeSetupApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ChargeSetupListItem>>(
      `/charge-setup/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<ChargeSetup>(`/charge-setup/${id}/`),
  create: (data: CreateChargeSetupPayload) =>
    ehrClient.post<ChargeSetup>("/charge-setup/", data),
  update: (id: string, data: UpdateChargeSetupPayload) =>
    ehrClient.patch<ChargeSetup>(`/charge-setup/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/charge-setup/${id}/`),
  reorder: (items: { id: string; sort_order: number }[]) =>
    ehrClient.post<{ detail: string }>("/charge-setup/reorder/", { items }),
};

// ============================================================================
// Named API functions — HSN Groups (Inventory)
// ============================================================================

export const hsnGroupsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<HSNGroup>>(`/hsn-groups/${qs}`);
  },
  get: (id: string) =>
    ehrClient.get<HSNGroup>(`/hsn-groups/${id}/`),
  create: (data: CreateHSNGroupPayload) =>
    ehrClient.post<HSNGroup>("/hsn-groups/", data),
  update: (id: string, data: UpdateHSNGroupPayload) =>
    ehrClient.patch<HSNGroup>(`/hsn-groups/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/hsn-groups/${id}/`),
};

// ============================================================================
// Named API functions — HSN Codes (Inventory)
// ============================================================================

export const hsnCodesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<HSNCode>>(`/hsn-codes/${qs}`);
  },
  get: (id: string) =>
    ehrClient.get<HSNCode>(`/hsn-codes/${id}/`),
  create: (data: CreateHSNCodePayload) =>
    ehrClient.post<HSNCode>("/hsn-codes/", data),
  update: (id: string, data: UpdateHSNCodePayload) =>
    ehrClient.patch<HSNCode>(`/hsn-codes/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/hsn-codes/${id}/`),
};

// ============================================================================
// Named API functions — Items (Inventory)
// ============================================================================

export const itemsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ItemListItem>>(`/items/${qs}`);
  },
  get: (id: string) => ehrClient.get<Item>(`/items/${id}/`),
  create: (data: CreateItemPayload) =>
    ehrClient.post<Item>("/items/", data),
  update: (id: string, data: UpdateItemPayload) =>
    ehrClient.patch<Item>(`/items/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/items/${id}/`),
  /** Validate a sheet of items (dry_run) or create them. Same code path both ways. */
  bulkImport: (data: ItemImportRequest) =>
    ehrClient.post<ItemImportResponse>("/items/bulk-import/", data),
};

// ============================================================================
// Named API functions — Suppliers
// ============================================================================

export const suppliersApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<SupplierListItem>>(
      `/suppliers/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<Supplier>(`/suppliers/${id}/`),
  create: (data: CreateSupplierPayload) =>
    ehrClient.post<Supplier>("/suppliers/", data),
  update: (id: string, data: UpdateSupplierPayload) =>
    ehrClient.patch<Supplier>(`/suppliers/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/suppliers/${id}/`),
  toggleActive: (id: string) =>
    ehrClient.post<Supplier>(`/suppliers/${id}/toggle-active/`, {}),
  /** GSTN 01-38 state registry, for the supplier address picker. */
  gstStates: () => ehrClient.get<GstState[]>("/suppliers/gst-states/"),
};

// ============================================================================
// Named API functions — Store Inventory (Inventory)
// ============================================================================

export const storeInventoryApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<StoreInventoryListItem>>(
      `/store-inventory/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<StoreInventoryItem>(`/store-inventory/${id}/`),
  create: (data: CreateStoreInventoryPayload) =>
    ehrClient.post<StoreInventoryItem>("/store-inventory/", data),
  expiryAlerts: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<StoreInventoryListItem>>(
      `/store-inventory/expiry-alerts/${qs}`
    );
  },
  stockSummary: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<StockSummaryItem>>(
      `/store-inventory/stock-summary/${qs}`
    );
  },
  consume: (data: {
    items: { store_inventory_id: string; quantity: number }[];
    patient_id?: string;
    encounter_id?: string;
    patient_name?: string;
    patient_display_id?: string;
    notes?: string;
  }) =>
    ehrClient.post<{ consumption_number: string; consumed: { store_inventory_id: string; item_name: string; batch_no: string; quantity_deducted: number; remaining: number }[] }>(
      "/store-inventory/consume/", data
    ),
  returnConsumption: (data: {
    consumption_id: string;
    items: { consumption_item_id: string; qty_returned: number }[];
    notes?: string;
  }) =>
    ehrClient.post<{ consumption_number: string; returned: { item_name: string; batch_no: string; qty_returned: number; new_stock: number }[] }>(
      "/store-inventory/return-consumption/", data
    ),
};

// ============================================================================
// Named API functions — Inventory Setup
// ============================================================================

export const inventorySetupApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<InventorySetup>>(
      `/inventory-setup/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<InventorySetup>(`/inventory-setup/${id}/`),
  create: (data: CreateInventorySetupPayload) =>
    ehrClient.post<InventorySetup>("/inventory-setup/", data),
  update: (id: string, data: CreateInventorySetupPayload) =>
    ehrClient.patch<InventorySetup>(`/inventory-setup/${id}/`, data),
};

// ============================================================================
// Named API functions — Sale Price Setup (per-payer pharmacy pricing)
// ============================================================================

import type {
  SalePriceRule,
  CreateSalePriceRulePayload,
  UpdateSalePriceRulePayload,
} from "@/types/ehr";

export const salePriceRulesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<SalePriceRule>>(
      `/sale-price-rules/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<SalePriceRule>(`/sale-price-rules/${id}/`),
  create: (data: CreateSalePriceRulePayload) =>
    ehrClient.post<SalePriceRule>("/sale-price-rules/", data),
  update: (id: string, data: UpdateSalePriceRulePayload) =>
    ehrClient.patch<SalePriceRule>(`/sale-price-rules/${id}/`, data),
  /** Soft-delete — the rule stays readable next to the sales it priced. */
  delete: (id: string) => ehrClient.delete<void>(`/sale-price-rules/${id}/`),
};

// ============================================================================
// Named API functions — Reorder Rules / Auto-Reorder (Inventory)
// ============================================================================

export const reorderRulesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ReorderRule>>(`/reorder-rules/${qs}`);
  },
  get: (id: string) => ehrClient.get<ReorderRule>(`/reorder-rules/${id}/`),
  create: (data: Partial<ReorderRule>) =>
    ehrClient.post<ReorderRule>("/reorder-rules/", data),
  update: (id: string, data: Partial<ReorderRule>) =>
    ehrClient.patch<ReorderRule>(`/reorder-rules/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/reorder-rules/${id}/`),
  /** Item + current stock + existing rule for one store, in one call. */
  storeItems: (params: Record<string, string>) =>
    ehrClient.get<ReorderStoreItemsResponse>(
      `/reorder-rules/store-items/?${new URLSearchParams(params).toString()}`
    ),
  bulkUpsert: (data: ReorderRuleBulkUpsertPayload) =>
    ehrClient.post<{ saved: number; results: ReorderRule[] }>(
      "/reorder-rules/bulk-upsert/",
      data
    ),
  run: (data: RunAutoReorderPayload) =>
    ehrClient.post<ReorderRunDetail>("/reorder-rules/run/", data),
};

export const reorderRunsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ReorderRun>>(`/reorder-runs/${qs}`);
  },
  /** `params` carries the store scope through, so a run opened from a
   *  store-filtered list shows that store's lines and not every store's. */
  get: (id: string, params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<ReorderRunDetail>(`/reorder-runs/${id}/${qs}`);
  },
};

// ============================================================================
// Named API functions — Indents (Inventory)
// ============================================================================

export const indentsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<IndentListItem>>(`/indents/${qs}`);
  },
  get: (id: string) => ehrClient.get<Indent>(`/indents/${id}/`),
  create: (data: CreateIndentPayload) =>
    ehrClient.post<Indent>("/indents/", data),
  update: (id: string, data: { notes?: string }) =>
    ehrClient.patch<Indent>(`/indents/${id}/`, data),
  submit: (id: string) =>
    ehrClient.post<Indent>(`/indents/${id}/submit/`, {}),
  approve: (id: string, data: ApproveIndentPayload) =>
    ehrClient.post<Indent>(`/indents/${id}/approve/`, data),
  reject: (id: string, data: RejectIndentPayload) =>
    ehrClient.post<Indent>(`/indents/${id}/reject/`, data),
  cancel: (id: string) =>
    ehrClient.post<Indent>(`/indents/${id}/cancel/`, {}),
  issue: (id: string, data: IssueStockPayload) =>
    ehrClient.post<StockIssue>(`/indents/${id}/issue/`, data),
  acknowledge: (id: string, data: { stock_issue_id: string }) =>
    ehrClient.post<StockIssue>(`/indents/${id}/acknowledge/`, data),
  convertToPR: (data: IndentToPRPayload) =>
    ehrClient.post<PurchaseRequisition>("/indents/convert-to-pr/", data),
};

// ============================================================================
// Named API functions — Stock Issues (Inventory)
// ============================================================================

export const stockIssuesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<StockIssueListItem>>(
      `/stock-issues/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<StockIssue>(`/stock-issues/${id}/`),
  dispute: (id: string, data: { reason: string }) =>
    ehrClient.post<StockIssue>(`/stock-issues/${id}/dispute/`, data),
  resolve: (id: string, data: { resolution_notes: string }) =>
    ehrClient.post<StockIssue>(`/stock-issues/${id}/resolve/`, data),
  pendingSummary: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PendingIssueSummaryItem[]>(
      `/stock-issues/pending-summary/${qs}`
    );
  },
};

// ============================================================================
// Named API functions — Stock Returns (Inventory)
// ============================================================================

export const stockConsumptionsApi = {
  list: (params?: { encounter?: string; patient?: string; store?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<StockConsumption>>(`/stock-consumptions/${toQs(params)}`),
  get: (id: string) => ehrClient.get<StockConsumption>(`/stock-consumptions/${id}/`),
};

export const stockReturnsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<StockReturnListItem>>(
      `/stock-returns/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<StockReturn>(`/stock-returns/${id}/`),
  create: (data: CreateStockReturnPayload) =>
    ehrClient.post<StockReturn>("/stock-returns/", data),
  submit: (id: string) =>
    ehrClient.post<StockReturn>(`/stock-returns/${id}/submit/`, {}),
  acknowledge: (id: string) =>
    ehrClient.post<StockReturn>(`/stock-returns/${id}/acknowledge/`, {}),
  cancel: (id: string) =>
    ehrClient.post<StockReturn>(`/stock-returns/${id}/cancel/`, {}),
  reject: (id: string, data?: { reason?: string }) =>
    ehrClient.post<StockReturn>(`/stock-returns/${id}/reject/`, data ?? {}),
  dispute: (id: string, data: { reason: string }) =>
    ehrClient.post<StockReturn>(`/stock-returns/${id}/dispute/`, data),
  resolve: (id: string, data: { resolution_notes: string }) =>
    ehrClient.post<StockReturn>(`/stock-returns/${id}/resolve/`, data),
};

// ============================================================================
// Named API functions — Direct Issues (Inventory)
// ============================================================================

export const directIssuesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<DirectIssueListItem>>(
      `/direct-issues/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<DirectIssue>(`/direct-issues/${id}/`),
  create: (data: CreateDirectIssuePayload) =>
    ehrClient.post<DirectIssue>("/direct-issues/", data),
  acknowledge: (id: string) =>
    ehrClient.post<DirectIssue>(`/direct-issues/${id}/acknowledge/`, {}),
};

// ============================================================================
// Named API functions — Purchase Requisitions (Inventory)
// ============================================================================

export const purchaseRequisitionsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PRListItem>>(
      `/purchase-requisitions/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<PurchaseRequisition>(`/purchase-requisitions/${id}/`),
  create: (data: CreatePRPayload) =>
    ehrClient.post<PurchaseRequisition>("/purchase-requisitions/", data),
  update: (id: string, data: { notes?: string }) =>
    ehrClient.patch<PurchaseRequisition>(`/purchase-requisitions/${id}/`, data),
  submit: (id: string) =>
    ehrClient.post<PurchaseRequisition>(
      `/purchase-requisitions/${id}/submit/`,
      {}
    ),
  approve: (id: string, data: ApprovePRPayload) =>
    ehrClient.post<PurchaseRequisition>(
      `/purchase-requisitions/${id}/approve/`,
      data
    ),
  reject: (id: string, data: RejectPRPayload) =>
    ehrClient.post<PurchaseRequisition>(
      `/purchase-requisitions/${id}/reject/`,
      data
    ),
  updateItems: (
    id: string,
    data: { items: { pr_item_id: string; requested_qty: number }[] }
  ) =>
    ehrClient.post<PurchaseRequisition>(
      `/purchase-requisitions/${id}/update-items/`,
      data
    ),
  cancel: (id: string) =>
    ehrClient.post<PurchaseRequisition>(
      `/purchase-requisitions/${id}/cancel/`,
      {}
    ),
};

// ============================================================================
// Named API functions — Purchase Orders (Inventory)
// ============================================================================

export const purchaseOrdersApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<POListItem>>(
      `/purchase-orders/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<PurchaseOrder>(`/purchase-orders/${id}/`),
  create: (data: CreatePOPayload) =>
    ehrClient.post<PurchaseOrder>("/purchase-orders/", data),
  update: (id: string, data: UpdatePOPayload) =>
    ehrClient.patch<PurchaseOrder>(`/purchase-orders/${id}/`, data),
  submit: (id: string) =>
    ehrClient.post<PurchaseOrder>(`/purchase-orders/${id}/submit/`, {}),
  approve: (id: string, data: ApprovePOPayload) =>
    ehrClient.post<PurchaseOrder>(`/purchase-orders/${id}/approve/`, data),
  reject: (id: string, data: RejectPOPayload) =>
    ehrClient.post<PurchaseOrder>(`/purchase-orders/${id}/reject/`, data),
  revertToDraft: (id: string) =>
    ehrClient.post<PurchaseOrder>(
      `/purchase-orders/${id}/revert-to-draft/`,
      {}
    ),
  send: (id: string) =>
    ehrClient.post<PurchaseOrder>(`/purchase-orders/${id}/send/`, {}),
  cancel: (id: string, data: CancelPOPayload) =>
    ehrClient.post<PurchaseOrder>(`/purchase-orders/${id}/cancel/`, data),
  close: (id: string) =>
    ehrClient.post<PurchaseOrder>(`/purchase-orders/${id}/close/`, {}),
};

// ============================================================================
// Named API functions — PO Terms (Inventory)
// ============================================================================

export const poTermsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<POTerm>>(`/po-terms/${qs}`);
  },
  create: (data: { text: string; is_active?: boolean; sort_order?: number }) =>
    ehrClient.post<POTerm>("/po-terms/", data),
  update: (
    id: string,
    data: Partial<{ text: string; is_active: boolean; sort_order: number }>
  ) => ehrClient.patch<POTerm>(`/po-terms/${id}/`, data),
  delete: (id: string) => ehrClient.delete(`/po-terms/${id}/`),
};

// ============================================================================
// Named API functions — GRNs (Inventory)
// ============================================================================

export const grnsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<GRNListItem>>(`/grns/${qs}`);
  },
  get: (id: string) => ehrClient.get<GRN>(`/grns/${id}/`),
  create: (data: CreateGRNPayload) => ehrClient.post<GRN>("/grns/", data),
  update: (id: string, data: { notes?: string; invoice_number?: string }) =>
    ehrClient.patch<GRN>(`/grns/${id}/`, data),
  verify: (id: string, data: VerifyGRNPayload) =>
    ehrClient.post<GRN>(`/grns/${id}/verify/`, data),
  accept: (id: string) =>
    ehrClient.post<GRN>(`/grns/${id}/accept/`, {}),
  reject: (id: string, data: { rejection_reason: string }) =>
    ehrClient.post<GRN>(`/grns/${id}/reject/`, data),
};

// ============================================================================
// Named API functions — EHR Activity Logs
// ============================================================================

import type { ActivityLog } from "@/types/ehr";

export const activityLogsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ActivityLog>>(
      `/ehr-activity-logs/${qs}`
    );
  },
};

// ============================================================================
// Named API functions — Hospital Auth
// ============================================================================

export const hospitalAuthApi = {
  login: (email: string, password: string) =>
    fetch(`${API_BASE_URL}/auth/hospital/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    }).then(async (res) => {
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.error || "Login failed");
      }
      return res.json() as Promise<HospitalLoginResponse>;
    }),

  refresh: (refresh: string) =>
    fetch(`${API_BASE_URL}/auth/hospital/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    }).then((res) => res.json()),

  /** Get locations assigned to the current user */
  getMyLocations: () =>
    ehrClient.get<{ id: string; name: string; slug: string; short_code: string; city: string; is_primary: boolean }[]>(
      "/auth/my-locations/"
    ),
};

// ============================================================================
// IPD — Bed & Ward Management API
// ============================================================================

import type {
  NursingUnit,
  NursingUnitListItem,
  Room,
  BedCategory,
  Bed,
  BedListItem,
  BedCensus,
  IPDMetrics,
  AdmissionRequest,
  AdmissionTypeMaster,
  AdmissionOption,
  AdmissionEstimate,
  PreAuthorization,
  Admission,
  PatientKin,
  CreatePatientKinPayload,
  TransferRequest,
  BedTransfer,
  DischargeTemplate,
  DischargeSummary,
  Discharge,
  DischargeSettings,
  DischargeWorklistSummary,
  CreateNursingUnitPayload,
  CreateBedCategoryPayload,
  CreateBedPayload,
  CreateAdmissionRequestPayload,
  CreateDischargePayload,
  UnitType,
} from "@/types/ehr";

export const unitTypesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<UnitType>>(`/unit-types/${qs}`);
  },
  create: (data: { name: string }) => ehrClient.post<UnitType>("/unit-types/", data),
  update: (id: string, data: { name?: string; is_active?: boolean }) =>
    ehrClient.patch<UnitType>(`/unit-types/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/unit-types/${id}/`),
};

export const nursingUnitsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<NursingUnitListItem>>(`/nursing-units/${qs}`);
  },
  get: (id: string) => ehrClient.get<NursingUnit>(`/nursing-units/${id}/`),
  create: (data: CreateNursingUnitPayload) => ehrClient.post<NursingUnit>("/nursing-units/", data),
  update: (id: string, data: Partial<CreateNursingUnitPayload>) =>
    ehrClient.patch<NursingUnit>(`/nursing-units/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/nursing-units/${id}/`),
};

export const roomsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<Room>>(`/rooms/${qs}`);
  },
  get: (id: string) => ehrClient.get<Room>(`/rooms/${id}/`),
  create: (data: { nursing_unit: string; room_number: string; room_name?: string; room_type?: string; floor_number?: number | null }) =>
    ehrClient.post<Room>("/rooms/", data),
  update: (id: string, data: Partial<Room>) => ehrClient.patch<Room>(`/rooms/${id}/`, data),
};

export const bedCategoriesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<BedCategory>>(`/bed-categories/${qs}`);
  },
  get: (id: string) => ehrClient.get<BedCategory>(`/bed-categories/${id}/`),
  create: (data: CreateBedCategoryPayload) => ehrClient.post<BedCategory>("/bed-categories/", data),
  update: (id: string, data: Partial<CreateBedCategoryPayload>) =>
    ehrClient.patch<BedCategory>(`/bed-categories/${id}/`, data),
};

export const roomRentRulesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<import("@/types/ehr").RoomRentRuleSet>>(
      `/room-rent-rules/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<import("@/types/ehr").RoomRentRuleSet>(`/room-rent-rules/${id}/`),
  create: (data: import("@/types/ehr").RoomRentRuleSetWritePayload) =>
    ehrClient.post<import("@/types/ehr").RoomRentRuleSet>(
      "/room-rent-rules/",
      data
    ),
  update: (id: string, data: Partial<import("@/types/ehr").RoomRentRuleSetWritePayload>) =>
    ehrClient.patch<import("@/types/ehr").RoomRentRuleSet>(
      `/room-rent-rules/${id}/`,
      data
    ),
  deactivate: (id: string) =>
    ehrClient.delete<void>(`/room-rent-rules/${id}/`),
};

export const bedsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<BedListItem>>(`/beds/${qs}`);
  },
  get: (id: string) => ehrClient.get<Bed>(`/beds/${id}/`),
  create: (data: CreateBedPayload) => ehrClient.post<Bed>("/beds/", data),
  update: (id: string, data: Partial<CreateBedPayload>) => ehrClient.patch<Bed>(`/beds/${id}/`, data),
  availability: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<Bed[]>(`/beds/availability/${qs}`);
  },
  setMaintenance: (id: string, notes?: string) =>
    ehrClient.post<Bed>(`/beds/${id}/set-maintenance/`, { notes: notes ?? "" }),
  clearMaintenance: (id: string) => ehrClient.post<Bed>(`/beds/${id}/clear-maintenance/`, {}),
  housekeepingDone: (id: string) => ehrClient.post<Bed>(`/beds/${id}/housekeeping-done/`, {}),
  reassign: (id: string, data: { nursing_unit?: string; room?: string | null; bed_category?: string; notes?: string }) =>
    ehrClient.post<Bed>(`/beds/${id}/reassign/`, data),
};

// ── Emergency Department ─────────────────────────────────────────────────

import type {
  PatientAllergy,
  CreatePatientAllergyPayload,
  PatientDocument,
  PatientPayorLink,
  PatientPayorPayload,
  EmergencyVisit,
  EmergencyVisitListItem,
  EmergencySummary,
  CreateEmergencyVisitPayload,
  AdmitEmergencyVisitPayload,
  RegisterAndAdmitEmergencyVisitPayload,
  RegisterAndAdmitEmergencyVisitResponse,
  DischargeEmergencyVisitPayload,
  TriageEmergencyVisitPayload,
} from "@/types/ehr";

export const patientAllergiesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PatientAllergy>>(`/patient-allergies/${qs}`);
  },
  get: (id: string) => ehrClient.get<PatientAllergy>(`/patient-allergies/${id}/`),
  create: (data: CreatePatientAllergyPayload) =>
    ehrClient.post<PatientAllergy>("/patient-allergies/", data),
  update: (id: string, data: Partial<PatientAllergy>) =>
    ehrClient.patch<PatientAllergy>(`/patient-allergies/${id}/`, data),
  deactivate: (id: string, deactivation_remarks?: string) =>
    ehrClient.post<PatientAllergy>(`/patient-allergies/${id}/deactivate/`, { deactivation_remarks: deactivation_remarks ?? "" }),
  delete: (id: string) => ehrClient.delete<void>(`/patient-allergies/${id}/`),
};

export const patientDocumentsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PatientDocument>>(`/patient-documents/${qs}`);
  },
  get: (id: string) => ehrClient.get<PatientDocument>(`/patient-documents/${id}/`),
  /**
   * The ONLY supported way to read a document's bytes. `file_url` on the model
   * is a raw `/media/...` path that 404s wherever DEBUG is False and is served
   * with no permission check at all — never fetch it. Requires the bearer
   * token, so use the helpers in `lib/utils/authed-file.ts` rather than putting
   * this in an `<img src>` or `<a href>`.
   */
  fileUrl: (id: string, opts?: { inline?: boolean }) =>
    `/api/patient-documents/${id}/download/${opts?.inline ? "?inline=1" : ""}`,
  upload: (params: {
    patient: string;
    encounter?: string;
    /** The payor arrangement this document backs, for policy cards etc. */
    patient_payor?: string;
    title?: string;
    description?: string;
    /** Code from the `id_document_type`, `referral_document_type` or
     *  `payor_document_type` PatientLookup category. */
    document_type?: string;
    file: File;
  }) => {
    const fd = new FormData();
    fd.append("patient", params.patient);
    if (params.encounter) fd.append("encounter", params.encounter);
    if (params.patient_payor) fd.append("patient_payor", params.patient_payor);
    if (params.title) fd.append("title", params.title);
    if (params.description) fd.append("description", params.description);
    if (params.document_type) fd.append("document_type", params.document_type);
    fd.append("file", params.file);
    return ehrClient.postFormData<PatientDocument>("/patient-documents/", fd);
  },
  delete: (id: string) => ehrClient.delete<void>(`/patient-documents/${id}/`),
  downloadUrl: (id: string) => `${API_BASE_URL}/patient-documents/${id}/download/`,
};

/**
 * A patient's standing payor arrangements, in preference order.
 *
 * The register and edit pages write these nested inside the patient payload
 * (one transaction, no half-saved registration). This client is for the places
 * that read or adjust them on their own: the billing payor switcher, Patient
 * 360, and the reorder / activate controls on the edit page.
 */
export const patientPayorsApi = {
  /** `?patient=<uuid>`; `?eligible_on=YYYY-MM-DD` narrows to links usable that
   *  day — active, naming a payor, and inside their validity window. */
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PatientPayorLink>>(`/patient-payors/${qs}`);
  },
  get: (id: string) => ehrClient.get<PatientPayorLink>(`/patient-payors/${id}/`),
  create: (data: PatientPayorPayload & { patient: string }) =>
    ehrClient.post<PatientPayorLink>("/patient-payors/", data),
  update: (id: string, data: Partial<PatientPayorPayload>) =>
    ehrClient.patch<PatientPayorLink>(`/patient-payors/${id}/`, data),
  remove: (id: string) => ehrClient.delete<void>(`/patient-payors/${id}/`),
  /** `order` must list every one of the patient's links exactly once — a
   *  partial list is rejected, since reordering is a statement about all of
   *  them. */
  reorder: (patient: string, order: string[]) =>
    ehrClient.post<PatientPayorLink[]>("/patient-payors/reorder/", { patient, order }),
  setActive: (id: string, is_active: boolean) =>
    ehrClient.post<PatientPayorLink>(`/patient-payors/${id}/set-active/`, { is_active }),
};

export const emergencyVisitsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<EmergencyVisitListItem>>(`/emergency-visits/${qs}`);
  },
  /**
   * ER dashboard roll-up behind `ERDashboardTiles` on both ER screens.
   *
   * Server-side aggregate over OPEN visits + ER beds, scoped exactly as `list`
   * is. Every patient bucket carries its matching visit ids so the tiles can
   * filter the table without the client re-deriving any predicate — see
   * `ErSummaryBucket`.
   */
  summary: () => ehrClient.get<EmergencySummary>("/emergency-visits/summary/"),
  get: (id: string) => ehrClient.get<EmergencyVisit>(`/emergency-visits/${id}/`),
  create: (data: CreateEmergencyVisitPayload) =>
    ehrClient.post<EmergencyVisit>("/emergency-visits/", data),
  update: (id: string, data: Partial<EmergencyVisit>) =>
    ehrClient.patch<EmergencyVisit>(`/emergency-visits/${id}/`, data),
  admit: (id: string, data: AdmitEmergencyVisitPayload) =>
    ehrClient.post<EmergencyVisit>(`/emergency-visits/${id}/admit/`, data),
  registerAndAdmit: (data: RegisterAndAdmitEmergencyVisitPayload) =>
    ehrClient.post<RegisterAndAdmitEmergencyVisitResponse>(
      `/emergency-visits/register-and-admit/`,
      data,
    ),
  discharge: (id: string, data?: DischargeEmergencyVisitPayload) =>
    ehrClient.post<EmergencyVisit>(`/emergency-visits/${id}/discharge/`, data ?? {}),
  triage: (id: string, data: TriageEmergencyVisitPayload) =>
    ehrClient.post<EmergencyVisit>(`/emergency-visits/${id}/triage/`, data),
  transferBed: (id: string, data: { new_bed_id: string; reason?: string }) =>
    ehrClient.post<EmergencyVisit>(`/emergency-visits/${id}/transfer-bed/`, data),
  /**
   * Give a bed to a bedless (`in_treatment`) patient → `admitted`.
   * Distinct from transferBed, which moves a patient who already has one.
   */
  assignBed: (id: string, data: { bed_id: string; notes?: string }) =>
    ehrClient.post<EmergencyVisit>(`/emergency-visits/${id}/assign-bed/`, data),
  /**
   * Pre-Bill escape hatch — activate a registered visit with no settled bill.
   *
   * The normal path is automatic (`post_save(Bill)` activates on settlement).
   * This covers what the signal structurally cannot: an EMPTY registration draft
   * (no consultation service configured — `finalize` refuses a bill with no
   * items, so it can never leave draft), fee-exempt patients, and recovery from
   * a swallowed signal. Roles: staff / receptionist / admin / location_admin.
   */
  activate: (id: string, data?: { reason?: string }) =>
    ehrClient.post<EmergencyVisit>(`/emergency-visits/${id}/activate/`, data ?? {}),
  /**
   * Cancel a registration. **Soft** — the backend sets `status='discharged'` and
   * releases any held bed; the record is never removed.
   *
   * The endpoint has existed since the module shipped but had no caller, which
   * is how encounter-less `waiting` visits from the legacy two-step intake ended
   * up with no exit at all. See emergency BUG_LOG 2026-08-02.
   */
  cancel: (id: string) => ehrClient.delete<void>(`/emergency-visits/${id}/`),
};

export const admissionOptionsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<AdmissionOption>>(`/admission-options/${qs}`);
  },
  create: (data: Partial<AdmissionOption>) =>
    ehrClient.post<AdmissionOption>("/admission-options/", data),
  update: (id: string, data: Partial<AdmissionOption>) =>
    ehrClient.patch<AdmissionOption>(`/admission-options/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/admission-options/${id}/`),
};

export const admissionTypesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<AdmissionTypeMaster>>(`/admission-types/${qs}`);
  },
  get: (id: string) => ehrClient.get<AdmissionTypeMaster>(`/admission-types/${id}/`),
  create: (data: Partial<AdmissionTypeMaster>) =>
    ehrClient.post<AdmissionTypeMaster>("/admission-types/", data),
  update: (id: string, data: Partial<AdmissionTypeMaster>) =>
    ehrClient.patch<AdmissionTypeMaster>(`/admission-types/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/admission-types/${id}/`),
};

export const admissionRequestsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<AdmissionRequest>>(`/admission-requests/${qs}`);
  },
  get: (id: string) => ehrClient.get<AdmissionRequest>(`/admission-requests/${id}/`),
  create: (data: CreateAdmissionRequestPayload) =>
    ehrClient.post<AdmissionRequest>("/admission-requests/", data),
  update: (id: string, data: Partial<AdmissionRequest>) =>
    ehrClient.patch<AdmissionRequest>(`/admission-requests/${id}/`, data),
  advanceStep: (id: string, targetStep: string) =>
    ehrClient.post<AdmissionRequest>(`/admission-requests/${id}/advance-step/`, { target_step: targetStep }),
  bookBed: (id: string, bedId: string) =>
    ehrClient.post<AdmissionRequest>(`/admission-requests/${id}/book-bed/`, { bed_id: bedId }),
  allocateBed: (id: string) =>
    ehrClient.post<AdmissionRequest>(`/admission-requests/${id}/allocate-bed/`, {}),
  admit: (id: string) =>
    ehrClient.post<Admission>(`/admission-requests/${id}/admit/`, {}),
  cancel: (id: string, reason?: string) =>
    ehrClient.post<AdmissionRequest>(`/admission-requests/${id}/cancel/`, { reason: reason ?? "" }),
};

export const admissionEstimatesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<AdmissionEstimate>>(`/admission-estimates/${qs}`);
  },
  get: (id: string) => ehrClient.get<AdmissionEstimate>(`/admission-estimates/${id}/`),
  create: (data: Partial<AdmissionEstimate>) =>
    ehrClient.post<AdmissionEstimate>("/admission-estimates/", data),
  approve: (id: string) =>
    ehrClient.post<AdmissionEstimate>(`/admission-estimates/${id}/approve/`, {}),
};

export const preAuthorizationsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PreAuthorization>>(`/pre-authorizations/${qs}`);
  },
  create: (data: Partial<PreAuthorization>) =>
    ehrClient.post<PreAuthorization>("/pre-authorizations/", data),
  approve: (id: string, data: { auth_number?: string; approved_amount?: string }) =>
    ehrClient.post<PreAuthorization>(`/pre-authorizations/${id}/approve/`, data),
  reject: (id: string, remarks: string) =>
    ehrClient.post<PreAuthorization>(`/pre-authorizations/${id}/reject/`, { remarks }),
};

export const ipdAdmissionsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<Admission>>(`/admissions/${qs}`);
  },
  get: (id: string) => ehrClient.get<Admission>(`/admissions/${id}/`),
  update: (id: string, data: Partial<Admission>) =>
    ehrClient.patch<Admission>(`/admissions/${id}/`, data),
  getOrCreateEncounter: (id: string) =>
    ehrClient.post<{ encounter_id: string; encounter_number: string; created: boolean }>(
      `/admissions/${id}/encounter/`, {}
    ),
};

export const patientKinApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PatientKin>>(`/patient-kin/${qs}`);
  },
  create: (data: CreatePatientKinPayload) =>
    ehrClient.post<PatientKin>("/patient-kin/", data),
  remove: (id: string) => ehrClient.delete(`/patient-kin/${id}/`),
};

export const transferRequestsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<TransferRequest>>(`/transfer-requests/${qs}`);
  },
  get: (id: string) => ehrClient.get<TransferRequest>(`/transfer-requests/${id}/`),
  create: (data: Partial<TransferRequest>) =>
    ehrClient.post<TransferRequest>("/transfer-requests/", data),
  approve: (id: string) =>
    ehrClient.post<TransferRequest>(`/transfer-requests/${id}/approve/`, {}),
  reject: (id: string, reason: string) =>
    ehrClient.post<TransferRequest>(`/transfer-requests/${id}/reject/`, { rejection_reason: reason }),
};

export const bedTransfersApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<BedTransfer>>(`/bed-transfers/${qs}`);
  },
  create: (data: { admission: string; to_bed: string; transfer_type?: string; transfer_reason?: string; transfer_request?: string }) =>
    ehrClient.post<BedTransfer>("/bed-transfers/", data),
};

export const caseTransfersApi = {
  create: (transferRequestId: string) =>
    ehrClient.post("/case-transfers/", { transfer_request_id: transferRequestId }),
};

export const dischargeTemplatesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<DischargeTemplate>>(`/discharge-templates/${qs}`);
  },
  get: (id: string) => ehrClient.get<DischargeTemplate>(`/discharge-templates/${id}/`),
  create: (data: Partial<DischargeTemplate>) =>
    ehrClient.post<DischargeTemplate>("/discharge-templates/", data),
  update: (id: string, data: Partial<DischargeTemplate>) =>
    ehrClient.patch<DischargeTemplate>(`/discharge-templates/${id}/`, data),
};

export const dischargeSummariesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<DischargeSummary>>(`/discharge-summaries/${qs}`);
  },
  get: (id: string) => ehrClient.get<DischargeSummary>(`/discharge-summaries/${id}/`),
  create: (data: Partial<DischargeSummary>) =>
    ehrClient.post<DischargeSummary>("/discharge-summaries/", data),
  update: (id: string, data: Partial<DischargeSummary>) =>
    ehrClient.patch<DischargeSummary>(`/discharge-summaries/${id}/`, data),
  approve: (id: string) =>
    ehrClient.post<Discharge>(`/discharge-summaries/${id}/approve/`, {}),
  taggedReports: (id: string) =>
    ehrClient.get<TaggedReportsResponse>(`/discharge-summaries/${id}/tagged-reports/`),
};

export interface TaggedLabReport {
  id: string;
  report_number: string;
  test_name: string;
  report_format: string;
  generated_at: string | null;
  parameters: Array<{ name: string; value: string; unit: string; flag: string; reference_range: string }>;
}

export interface TaggedRadReport {
  id: string;
  report_number: string;
  exam_type_name: string;
  status: string;
  findings: string;
  impressions: string;
  recommendations: string;
  is_critical: boolean;
  reported_by_name: string;
  reported_at: string | null;
}

export interface TaggedReportsResponse {
  lab_reports: TaggedLabReport[];
  radiology_reports: TaggedRadReport[];
}

export const dischargesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<Discharge>>(`/discharges/${qs}`);
  },
  summary: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<DischargeWorklistSummary>(`/discharges/summary/${qs}`);
  },
  get: (id: string) => ehrClient.get<Discharge>(`/discharges/${id}/`),
  create: (data: CreateDischargePayload) =>
    ehrClient.post<Discharge>("/discharges/", data),
  nursingClearance: (id: string, notes?: string) =>
    ehrClient.post<Discharge>(`/discharges/${id}/nursing-clearance/`, { notes: notes ?? "" }),
  pharmacyClearance: (id: string, notes?: string) =>
    ehrClient.post<Discharge>(`/discharges/${id}/pharmacy-clearance/`, { notes: notes ?? "" }),
  billingClearance: (id: string) =>
    ehrClient.post<Discharge>(`/discharges/${id}/billing-clearance/`, {}),
  complete: (id: string) =>
    ehrClient.post<Discharge>(`/discharges/${id}/complete/`, {}),
  // Cancel actions (reverse order)
  cancelBillingClearance: (id: string) =>
    ehrClient.post<Discharge>(`/discharges/${id}/cancel-billing-clearance/`, {}),
  cancelPharmacyClearance: (id: string) =>
    ehrClient.post<Discharge>(`/discharges/${id}/cancel-pharmacy-clearance/`, {}),
  cancelNursingClearance: (id: string) =>
    ehrClient.post<Discharge>(`/discharges/${id}/cancel-nursing-clearance/`, {}),
  cancelIntimation: (id: string) =>
    ehrClient.post<{ detail: string }>(`/discharges/${id}/cancel-intimation/`, {}),
};

export const dischargeSettingsApi = {
  get: () => ehrClient.get<DischargeSettings>("/discharge-settings/"),
  update: (data: Partial<Pick<DischargeSettings, "nursing_clearance_window_hours">>) =>
    ehrClient.patch<DischargeSettings>("/discharge-settings/", data),
};

export const ipdCensusApi = {
  get: () => ehrClient.get<BedCensus>("/ipd/census/"),
};

export const ipdMetricsApi = {
  get: () => ehrClient.get<IPDMetrics>("/ipd/metrics/"),
};

export const wardManagementApi = {
  get: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<WardManagementData>(`/ipd/ward-management/${qs}`);
  },
};

// ============================================================================
// Patient Registration Master
// ============================================================================

import type {
  EmergencyMaster,
  EmergencyMasterUpdatePayload,
  OpBillingMaster,
  OpBillingMasterUpdatePayload,
  PatientRegistrationMaster,
  PatientRegistrationMasterUpdatePayload,
  TitleConfig,
  TitleConfigPayload,
  ReferralSourceConfig,
  QueueType,
  QueueTypePayload,
  AppointmentSession,
  AppointmentSessionPayload,
  AppointmentLookup,
  AppointmentLookupPayload,
  AppointmentRequest,
  AppointmentRequestPayload,
  AppointmentRequestSummary,
  ReferralSourceOption,
  ReferralSourceOptionPayload,
  ReferralSourceOptionBulkPayload,
  ReferralSourceOptionBulkResult,
  ReferringDoctor,
  ReferringDoctorPayload,
  ReferringHospital,
  ReferringHospitalPayload,
  ReferralSourceConfigPayload,
  PatientLookupConfig,
  PatientLookupPayload,
  PincodeEntry,
} from "@/types/ehr";

/**
 * Emergency Master — per-location ER configuration singleton.
 *
 * Flat get/update shape (like registrationMasterApi), NOT the nested
 * creditRegisterApi.masters.* shape: this is one row, and the settings page's
 * tabs are sections of that row, not separate sub-resources.
 */
export const emergencyMasterApi = {
  get: (locationId: string) =>
    ehrClient.get<EmergencyMaster>(`/emergency-master/${locationId}/`),
  update: (locationId: string, data: EmergencyMasterUpdatePayload) =>
    ehrClient.patch<EmergencyMaster>(`/emergency-master/${locationId}/`, data),
};

/**
 * OP Billing Master — per-location OP billing configuration singleton.
 *
 * Same flat get/update shape as emergencyMasterApi. The GET ships the title
 * list, the lookup buckets and the external field config inline, so the OP
 * Billing page can draw the whole inline external-patient form from one call.
 */
export const opBillingMasterApi = {
  get: (locationId: string) =>
    ehrClient.get<OpBillingMaster>(`/op-billing-master/${locationId}/`),
  update: (locationId: string, data: OpBillingMasterUpdatePayload) =>
    ehrClient.patch<OpBillingMaster>(`/op-billing-master/${locationId}/`, data),
};

export const registrationMasterApi = {
  get: (locationId: string) =>
    ehrClient.get<PatientRegistrationMaster>(
      `/patient-registration-master/${locationId}/`
    ),
  update: (locationId: string, data: PatientRegistrationMasterUpdatePayload) =>
    ehrClient.patch<PatientRegistrationMaster>(
      `/patient-registration-master/${locationId}/`,
      data
    ),
};

export const patientTitlesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<TitleConfig>>(
      `/patient-titles/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<TitleConfig>(`/patient-titles/${id}/`),
  create: (data: TitleConfigPayload) =>
    ehrClient.post<TitleConfig>("/patient-titles/", data),
  update: (id: string, data: Partial<TitleConfigPayload>) =>
    ehrClient.patch<TitleConfig>(`/patient-titles/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/patient-titles/${id}/`),
};

// ── Referring Doctor / Hospital masters (hospital-scoped) ──────────────────
export const referringDoctorsApi = {
  list: (params?: {
    location?: string; referring_hospital?: string; is_active?: string;
    search?: string; limit?: string; offset?: string;
  }) =>
    ehrClient.get<PaginatedResponse<ReferringDoctor>>(
      `/referring-doctors/${toQs(params)}`
    ),
  get: (id: string) => ehrClient.get<ReferringDoctor>(`/referring-doctors/${id}/`),
  create: (data: ReferringDoctorPayload) =>
    ehrClient.post<ReferringDoctor>("/referring-doctors/", data),
  update: (id: string, data: Partial<ReferringDoctorPayload>) =>
    ehrClient.patch<ReferringDoctor>(`/referring-doctors/${id}/`, data),
  /** Soft delete — deactivates, never removes. Existing referrals keep resolving. */
  delete: (id: string) => ehrClient.delete<void>(`/referring-doctors/${id}/`),
};

export const referringHospitalsApi = {
  list: (params?: {
    location?: string; is_active?: string; search?: string;
    limit?: string; offset?: string;
  }) =>
    ehrClient.get<PaginatedResponse<ReferringHospital>>(
      `/referring-hospitals/${toQs(params)}`
    ),
  get: (id: string) => ehrClient.get<ReferringHospital>(`/referring-hospitals/${id}/`),
  create: (data: ReferringHospitalPayload) =>
    ehrClient.post<ReferringHospital>("/referring-hospitals/", data),
  update: (id: string, data: Partial<ReferringHospitalPayload>) =>
    ehrClient.patch<ReferringHospital>(`/referring-hospitals/${id}/`, data),
  /** Soft delete — deactivates, never removes. */
  delete: (id: string) => ehrClient.delete<void>(`/referring-hospitals/${id}/`),
};

export const referralSourceOptionsApi = {
  list: (params?: { referral_source?: string; is_active?: string; search?: string; limit?: string }) =>
    ehrClient.get<PaginatedResponse<ReferralSourceOption>>(
      `/referral-source-options/${toQs(params)}`
    ),
  create: (data: ReferralSourceOptionPayload) =>
    ehrClient.post<ReferralSourceOption>("/referral-source-options/", data),
  update: (id: string, data: Partial<ReferralSourceOptionPayload>) =>
    ehrClient.patch<ReferralSourceOption>(`/referral-source-options/${id}/`, data),
  /** Soft delete — patients store the option NAME as text, so it must keep displaying. */
  delete: (id: string) => ehrClient.delete<void>(`/referral-source-options/${id}/`),
  /** Paste-a-list or upload-a-CSV. Existing names are skipped, so re-uploading
   *  a corrected sheet is safe. Auto-enables has_predefined_list. */
  bulk: (data: ReferralSourceOptionBulkPayload) =>
    ehrClient.post<ReferralSourceOptionBulkResult>("/referral-source-options/bulk/", data),
};

export const referralSourcesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<ReferralSourceConfig>>(
      `/referral-sources/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<ReferralSourceConfig>(`/referral-sources/${id}/`),
  create: (data: ReferralSourceConfigPayload) =>
    ehrClient.post<ReferralSourceConfig>("/referral-sources/", data),
  update: (id: string, data: Partial<ReferralSourceConfigPayload>) =>
    ehrClient.patch<ReferralSourceConfig>(`/referral-sources/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/referral-sources/${id}/`),
};

/**
 * Generic per-location registration lookups — one endpoint serves every
 * category (occupation, marital status, language, nationality, country,
 * ID document type). Pass `category` to narrow.
 */
export const patientLookupsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PatientLookupConfig>>(
      `/patient-lookups/${qs}`
    );
  },
  get: (id: string) =>
    ehrClient.get<PatientLookupConfig>(`/patient-lookups/${id}/`),
  create: (data: PatientLookupPayload) =>
    ehrClient.post<PatientLookupConfig>("/patient-lookups/", data),
  update: (id: string, data: Partial<PatientLookupPayload>) =>
    ehrClient.patch<PatientLookupConfig>(`/patient-lookups/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/patient-lookups/${id}/`),
};

/**
 * National PIN code reference data. Not hospital-scoped and not paginated —
 * a PIN code returns every locality registered under it, and the caller lets
 * the operator choose rather than assuming the first row.
 *
 * Returns an empty array when the master has not been seeded; callers should
 * fall back to the live India Post API in that case.
 */
export const pincodesApi = {
  lookup: (pincode: string) =>
    ehrClient.get<PincodeEntry[]>(
      `/pincodes/?pincode=${encodeURIComponent(pincode)}`
    ),
};

// ============================================================================
// RBAC — Roles & Permissions
// ============================================================================

import type {
  UserPermissions,
  RbacModule,
  SystemRole,
  RoleModulePermissionEntry,
} from "@/types/ehr";

export const rbacApi = {
  // ── Current user permissions ──────────────────────────────────────────
  getMyPermissions: () =>
    ehrClient.get<UserPermissions>("/rbac/my-permissions/"),

  // ── Modules (read-only, for admin permission matrix UI) ───────────────
  listModules: (locationSlug?: string) =>
    ehrClient.get<RbacModule[]>(
      `/rbac/modules/${locationSlug ? `?location_slug=${encodeURIComponent(locationSlug)}` : ""}`,
    ),

  // ── System Roles CRUD ─────────────────────────────────────────────────
  listRoles: () => ehrClient.get<SystemRole[]>("/rbac/roles/"),
  getRole: (id: string) => ehrClient.get<SystemRole>(`/rbac/roles/${id}/`),
  createRole: (data: { name: string; code: string; description?: string }) =>
    ehrClient.post<SystemRole>("/rbac/roles/", data),
  updateRole: (
    id: string,
    data: { name?: string; code?: string; description?: string }
  ) => ehrClient.patch<SystemRole>(`/rbac/roles/${id}/`, data),
  deleteRole: (id: string) => ehrClient.delete<void>(`/rbac/roles/${id}/`),

  // ── Role Permission Matrix ────────────────────────────────────────────
  getRolePermissions: (roleId: string) =>
    ehrClient.get<RoleModulePermissionEntry[]>(
      `/rbac/roles/${roleId}/permissions/`
    ),
  setRolePermissions: (
    roleId: string,
    permissions: RoleModulePermissionEntry[]
  ) =>
    ehrClient.put<{ detail: string }>(`/rbac/roles/${roleId}/permissions/`, {
      permissions,
    }),
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Consultations — Doctor Worklist & OPD Consultation
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import type {
  WorklistResponse,
  NursingWorklistResponse,
  NursingDeskConfig,
  OpdNursingDeskMaster,
  OptometryWorklistResponse,
  OphthalmicWorkup,
  CreateOphthalmicWorkupPayload,
  RecordDilationPayload,
  OphthalmicExamination,
  CreateOphthalmicExaminationPayload,
  Vitals,
  CreateVitalsPayload,
  ClinicalNote,
  CreateClinicalNotePayload,
  Diagnosis,
  CreateDiagnosisPayload,
  ICD10Code,
  DoctorFavoriteDiagnosis,
  CreateFavoriteDiagnosisPayload,
  DoctorFavorite,
  DoctorFavoriteItemType,
  CreateDoctorFavoritePayload,
  Prescription,
  CreatePrescriptionPayload,
  PrescriptionItem,
  CreatePrescriptionItemPayload,
  InvestigationOrder,
  CreateInvestigationOrderPayload,
  InvestigationOrderItem,
  OpdProcedure,
  CreateOpdProcedurePayload,
  ClinicalProcedure,
  ClinicalProcedureDetail,
  ClinicalProcedureDepartment,
  ClinicalProcedureStatus,
  SaveClinicalProcedureReportPayload,
  DoctorServiceRequest,
  CreateDoctorServiceRequestPayload,
  Referral,
  CreateReferralPayload,
  ConsultationTemplate,
  ConsultationTemplateApplyPreview,
  SaveFromConsultationPayload,
  PrescriptionTemplate,
  InvestigationTemplate,
  ConsultationSummary,
  PatientClinicalHistory,
} from "@/types/ehr";

function toQs(params?: Record<string, unknown>): string {
  if (!params) return "";
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null);
  if (!entries.length) return "";
  return "?" + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

export const doctorWorklistApi = {
  get: (params?: { doctor_id?: string; date?: string; status?: string }) =>
    ehrClient.get<WorklistResponse>(`/doctor-worklist/${toQs(params)}`),
};

// Doctor-accessible read API for lab + radiology reports on a consultation
// encounter. Sidesteps laboratory/radiology RBAC — the doctor owns the
// encounter so they obviously need to read the results inline.
export const consultationDiagnosticReportsApi = {
  get: (encounterId: string) =>
    ehrClient.get<import("@/types/ehr").EncounterDiagnosticReports>(
      `/consultations/encounters/${encounterId}/diagnostic-reports/`,
    ),
  // Proxy URL for lab report PDF — same payload as /laboratory/reports/{id}/pdf
  // but gated by the consultation-side ACL.
  labReportPdfUrl: (reportId: string) =>
    `/api/consultations/lab-reports/${reportId}/pdf/`,
};

export const nursingWorklistApi = {
  get: (params?: { date?: string; department?: string; doctor_id?: string; search?: string; status?: string; limit?: number; offset?: number }) =>
    ehrClient.get<NursingWorklistResponse>(`/nursing-worklist/${toQs(params)}`),
};

/**
 * OPD Nursing Desk Master — per-location config for what the nurse's
 * pre-consultation station captures. Read by the nurse (to render the dialog),
 * written by hospital admins only.
 */
export const opdNursingDeskMasterApi = {
  get: (locationId: string) =>
    ehrClient.get<OpdNursingDeskMaster>(`/opd-nursing-desk-master/${locationId}/`),
  update: (locationId: string, data: { field_config: NursingDeskConfig }) =>
    ehrClient.patch<OpdNursingDeskMaster>(`/opd-nursing-desk-master/${locationId}/`, data),
};

/**
 * Ophthalmology — the eye department's optometry workup and the doctor's
 * structured eye examination. The workup replaces the nursing vitals step for
 * encounters whose department has `specialty === "ophthalmology"`.
 */
export const ophthalmologyApi = {
  worklist: (params?: {
    date?: string;
    department?: string;
    doctor_id?: string;
    search?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => ehrClient.get<OptometryWorklistResponse>(`/ophthalmology/worklist/${toQs(params)}`),

  workups: {
    list: (params?: { encounter?: string; patient?: string; limit?: number; offset?: number }) =>
      ehrClient.get<PaginatedResponse<OphthalmicWorkup>>(`/ophthalmology/workups/${toQs(params)}`),
    get: (id: string) => ehrClient.get<OphthalmicWorkup>(`/ophthalmology/workups/${id}/`),
    create: (data: CreateOphthalmicWorkupPayload) =>
      ehrClient.post<OphthalmicWorkup>("/ophthalmology/workups/", data),
    update: (id: string, data: Partial<CreateOphthalmicWorkupPayload>) =>
      ehrClient.patch<OphthalmicWorkup>(`/ophthalmology/workups/${id}/`, data),
    /** checked_in -> vitals_taken. Requires >= 1 vision/refraction/IOP reading. */
    markWorkupComplete: (id: string) =>
      ehrClient.post<OphthalmicWorkup>(`/ophthalmology/workups/${id}/mark-workup-complete/`, {}),
    recordDilation: (id: string, data: RecordDilationPayload) =>
      ehrClient.post<OphthalmicWorkup>(`/ophthalmology/workups/${id}/record-dilation/`, data),
  },

  examinations: {
    list: (params?: { encounter?: string; patient?: string; limit?: number; offset?: number }) =>
      ehrClient.get<PaginatedResponse<OphthalmicExamination>>(
        `/ophthalmology/examinations/${toQs(params)}`,
      ),
    get: (id: string) => ehrClient.get<OphthalmicExamination>(`/ophthalmology/examinations/${id}/`),
    create: (data: CreateOphthalmicExaminationPayload) =>
      ehrClient.post<OphthalmicExamination>("/ophthalmology/examinations/", data),
    update: (id: string, data: Partial<CreateOphthalmicExaminationPayload>) =>
      ehrClient.patch<OphthalmicExamination>(`/ophthalmology/examinations/${id}/`, data),
  },
};

export const vitalsApi = {
  list: (params?: { encounter?: string; patient?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<Vitals>>(`/vitals/${toQs(params)}`),
  get: (id: string) => ehrClient.get<Vitals>(`/vitals/${id}/`),
  create: (data: CreateVitalsPayload) => ehrClient.post<Vitals>("/vitals/", data),
  update: (id: string, data: Partial<CreateVitalsPayload>) =>
    ehrClient.patch<Vitals>(`/vitals/${id}/`, data),
  markVitalsTaken: (id: string) =>
    ehrClient.post<Vitals>(`/vitals/${id}/mark-vitals-taken/`, {}),
};

export const clinicalNotesApi = {
  list: (params?: { encounter?: string; patient?: string; note_type?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<ClinicalNote>>(`/clinical-notes/${toQs(params)}`),
  get: (id: string) => ehrClient.get<ClinicalNote>(`/clinical-notes/${id}/`),
  create: (data: CreateClinicalNotePayload) =>
    ehrClient.post<ClinicalNote>("/clinical-notes/", data),
  update: (id: string, data: Partial<CreateClinicalNotePayload>) =>
    ehrClient.patch<ClinicalNote>(`/clinical-notes/${id}/`, data),
  finalize: (id: string) =>
    ehrClient.post<ClinicalNote>(`/clinical-notes/${id}/finalize/`, {}),
  /**
   * Nurse-desk write of the visit's primary complaint. Upserts onto the same
   * note the doctor edits, creating it when the nurse gets there first — so
   * the find-or-create rule lives server-side, not in each calling screen.
   */
  setChiefComplaint: (data: { encounter: string; chief_complaint: string }) =>
    ehrClient.post<ClinicalNote>("/clinical-notes/chief-complaint/", data),
};

export const diagnosesApi = {
  list: (params?: { encounter?: string; patient?: string; diagnosis_type?: string; status?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<Diagnosis>>(`/diagnoses/${toQs(params)}`),
  get: (id: string) => ehrClient.get<Diagnosis>(`/diagnoses/${id}/`),
  create: (data: CreateDiagnosisPayload) =>
    ehrClient.post<Diagnosis>("/diagnoses/", data),
  update: (id: string, data: Partial<CreateDiagnosisPayload>) =>
    ehrClient.patch<Diagnosis>(`/diagnoses/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/diagnoses/${id}/`),
};

export const icd10Api = {
  search: (params: { search: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<ICD10Code>>(`/icd10-codes/${toQs(params)}`),
};

export const favoriteDiagnosesApi = {
  list: (params?: { limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<DoctorFavoriteDiagnosis>>(`/favorite-diagnoses/${toQs(params)}`),
  create: (data: CreateFavoriteDiagnosisPayload) =>
    ehrClient.post<DoctorFavoriteDiagnosis>("/favorite-diagnoses/", data),
  delete: (id: string) =>
    ehrClient.delete<void>(`/favorite-diagnoses/${id}/`),
};

export const doctorFavoritesApi = {
  list: (params?: { item_type?: DoctorFavoriteItemType; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<DoctorFavorite>>(`/doctor-favorites/${toQs(params)}`),
  create: (data: CreateDoctorFavoritePayload) =>
    ehrClient.post<DoctorFavorite>("/doctor-favorites/", data),
  delete: (id: string) =>
    ehrClient.delete<void>(`/doctor-favorites/${id}/`),
};

export const prescriptionsApi = {
  list: (params?: { encounter?: string; patient?: string; status?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<Prescription>>(`/prescriptions/${toQs(params)}`),
  get: (id: string) => ehrClient.get<Prescription>(`/prescriptions/${id}/`),
  create: (data: CreatePrescriptionPayload) =>
    ehrClient.post<Prescription>("/prescriptions/", data),
  update: (id: string, data: Partial<{ general_instructions: string; diet_advice: string; lifestyle_advice: string }>) =>
    ehrClient.patch<Prescription>(`/prescriptions/${id}/`, data),
  finalize: (id: string) =>
    ehrClient.post<Prescription>(`/prescriptions/${id}/finalize/`, {}),
  markPrinted: (id: string) =>
    ehrClient.post<Prescription>(`/prescriptions/${id}/mark-printed/`, {}),
  addItem: (id: string, data: CreatePrescriptionItemPayload) =>
    ehrClient.post<PrescriptionItem>(`/prescriptions/${id}/items/add/`, data),
  bulkAddItems: (id: string, items: CreatePrescriptionItemPayload[]) =>
    ehrClient.post<PrescriptionItem[]>(`/prescriptions/${id}/items/bulk-add/`, { items }),
  removeItem: (id: string, itemId: string) =>
    ehrClient.delete<void>(`/prescriptions/${id}/items/${itemId}/remove/`),
};

export const investigationOrdersApi = {
  list: (params?: { encounter?: string; patient?: string; status?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<InvestigationOrder>>(`/investigation-orders/${toQs(params)}`),
  get: (id: string) => ehrClient.get<InvestigationOrder>(`/investigation-orders/${id}/`),
  create: (data: CreateInvestigationOrderPayload) =>
    ehrClient.post<InvestigationOrder>("/investigation-orders/", data),
  submit: (id: string) =>
    ehrClient.post<{ detail: string; status: string; billed: string[]; billing_failed: string[] }>(
      `/investigation-orders/${id}/submit/`, {}
    ),
  updateItemStatus: (orderId: string, itemId: string, newStatus: string) =>
    ehrClient.patch<InvestigationOrderItem>(
      `/investigation-orders/${orderId}/items/${itemId}/status/`, { status: newStatus }
    ),
  remove: (id: string) => ehrClient.delete(`/investigation-orders/${id}/`),
};

export const opdProceduresApi = {
  list: (params?: { encounter?: string; patient?: string; status?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<OpdProcedure>>(`/opd-procedures/${toQs(params)}`),
  get: (id: string) => ehrClient.get<OpdProcedure>(`/opd-procedures/${id}/`),
  create: (data: CreateOpdProcedurePayload) =>
    ehrClient.post<OpdProcedure>("/opd-procedures/", data),
  complete: (id: string) =>
    ehrClient.post<OpdProcedure>(`/opd-procedures/${id}/complete/`, {}),
  remove: (id: string) => ehrClient.delete(`/opd-procedures/${id}/`),
};

// ============================================================================
// Clinical Procedures — procedure-room worklist (module: clinical.procedures)
// ============================================================================
// Shared by `list` and `counts` so the summary tiles and the table can never be
// filtered differently — the two calls go out together on every load.
type ClinicalProcedureListParams = {
  status?: string;
  visit_type?: string;
  patient?: string;
  encounter?: string;
  location?: string;
  /** Owning department (`service.department`). Omit for every department. */
  department?: string;
  /** Hospital-local calendar day bounds on release date. Omit both for all dates. */
  date_from?: string;
  date_to?: string;
  /** Broad search: procedure no., service name, patient name, UHID. */
  search?: string;
  /** Narrow boxes on the filter bar. */
  patient_display_id?: string;
  procedure_number?: string;
  ordering?: string;
};
// Read-only as a resource: rows are minted by the backend when a bill carrying
// a procedure service is paid. Everything else is a workflow transition.

export const clinicalProceduresApi = {
  list: (params?: ClinicalProcedureListParams & { limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<ClinicalProcedure>>(`/clinical-procedures/${toQs(params)}`),
  /**
   * Per-status counts for the summary tiles — one round trip. Honours every
   * filter EXCEPT `status` (the tiles need all five numbers at once) and adds
   * `total` as the sum, so the "All" tile can never disagree with the five
   * beside it.
   */
  counts: (params?: Omit<ClinicalProcedureListParams, "status">) =>
    ehrClient.get<Record<ClinicalProcedureStatus | "total", number>>(
      `/clinical-procedures/counts/${toQs(params)}`
    ),
  /**
   * Departments that have work on this worklist, with a count each. Honours the
   * same filters as `list` EXCEPT `department` (the picker must keep offering
   * the others) and `status` (an empty status must not make a room disappear).
   */
  departments: (params?: Omit<ClinicalProcedureListParams, "status" | "department">) =>
    ehrClient.get<ClinicalProcedureDepartment[]>(
      `/clinical-procedures/departments/${toQs(params)}`
    ),
  get: (id: string) =>
    ehrClient.get<ClinicalProcedureDetail>(`/clinical-procedures/${id}/`),
  acknowledge: (id: string) =>
    ehrClient.post<ClinicalProcedureDetail>(`/clinical-procedures/${id}/acknowledge/`, {}),
  reject: (id: string, data: { rejection_reason: string }) =>
    ehrClient.post<ClinicalProcedureDetail>(`/clinical-procedures/${id}/reject/`, data),
  cancel: (id: string, data: { cancellation_reason: string }) =>
    ehrClient.post<ClinicalProcedureDetail>(`/clinical-procedures/${id}/cancel/`, data),
  complete: (id: string) =>
    ehrClient.post<ClinicalProcedureDetail>(`/clinical-procedures/${id}/complete/`, {}),
  /** Save is `complete: false`; Save & Complete is `complete: true`. */
  saveReport: (id: string, data: SaveClinicalProcedureReportPayload) =>
    ehrClient.post<ClinicalProcedureDetail>(`/clinical-procedures/${id}/save-report/`, data),
};

export const doctorServiceRequestsApi = {
  list: (params?: { encounter?: string; patient?: string; status?: string; request_type?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<DoctorServiceRequest>>(`/doctor-service-requests/${toQs(params)}`),
  get: (id: string) => ehrClient.get<DoctorServiceRequest>(`/doctor-service-requests/${id}/`),
  create: (data: CreateDoctorServiceRequestPayload) =>
    ehrClient.post<DoctorServiceRequest>("/doctor-service-requests/", data),
  acknowledge: (id: string) =>
    ehrClient.post<DoctorServiceRequest>(`/doctor-service-requests/${id}/acknowledge/`, {}),
  dismiss: (id: string) =>
    ehrClient.post<DoctorServiceRequest>(`/doctor-service-requests/${id}/dismiss/`, {}),
  remove: (id: string) => ehrClient.delete(`/doctor-service-requests/${id}/`),
};

export const referralsApi = {
  list: (params?: { encounter?: string; patient?: string; referral_type?: string; status?: string; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<Referral>>(`/referrals/${toQs(params)}`),
  get: (id: string) => ehrClient.get<Referral>(`/referrals/${id}/`),
  create: (data: CreateReferralPayload) =>
    ehrClient.post<Referral>("/referrals/", data),
  update: (id: string, data: Partial<CreateReferralPayload>) =>
    ehrClient.patch<Referral>(`/referrals/${id}/`, data),
  /** Withdraw a referral raised by mistake. Soft — the row stays as history
   *  and the reason is mandatory, so a later reader can tell "withdrawn" from
   *  "never acted on". Also closes the desk request it created. There is no
   *  `remove`: the endpoint has no DELETE, by design. */
  cancel: (id: string, reason: string) =>
    ehrClient.post<Referral>(`/referrals/${id}/cancel/`, { reason }),
};

export const consultationTemplatesApi = {
  list: (params?: {
    template_type?: string;
    scope?: string;
    department?: string;
    specialty?: string;
    is_active?: boolean | string;
    created_by?: string;
    search?: string;
    ordering?: string;
    limit?: number | string;
    offset?: number | string;
  }) =>
    ehrClient.get<PaginatedResponse<ConsultationTemplate>>(`/consultation-templates/${toQs(params)}`),
  get: (id: string) => ehrClient.get<ConsultationTemplate>(`/consultation-templates/${id}/`),
  create: (data: Partial<ConsultationTemplate>) =>
    ehrClient.post<ConsultationTemplate>("/consultation-templates/", data),
  update: (id: string, data: Partial<ConsultationTemplate>) =>
    ehrClient.patch<ConsultationTemplate>(`/consultation-templates/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/consultation-templates/${id}/`),
  duplicate: (id: string) =>
    ehrClient.post<ConsultationTemplate>(`/consultation-templates/${id}/duplicate/`, {}),
  applyPreview: (id: string, params: { encounter: string; mode?: "merge" | "replace" }) =>
    ehrClient.get<ConsultationTemplateApplyPreview>(`/consultation-templates/${id}/apply-preview/${toQs(params)}`),
  saveFromConsultation: (data: SaveFromConsultationPayload) =>
    ehrClient.post<ConsultationTemplate>(`/consultation-templates/save-from-consultation/`, data),
  recordUsage: (id: string) =>
    ehrClient.post<{ usage_count: number; last_used_at: string }>(
      `/consultation-templates/${id}/record-usage/`, {},
    ),
};

export const prescriptionTemplatesApi = {
  list: (params?: { specialty?: string; is_active?: boolean; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<PrescriptionTemplate>>(`/prescription-templates/${toQs(params)}`),
  get: (id: string) => ehrClient.get<PrescriptionTemplate>(`/prescription-templates/${id}/`),
  create: (data: Partial<PrescriptionTemplate>) =>
    ehrClient.post<PrescriptionTemplate>("/prescription-templates/", data),
  update: (id: string, data: Partial<PrescriptionTemplate>) =>
    ehrClient.patch<PrescriptionTemplate>(`/prescription-templates/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/prescription-templates/${id}/`),
};

export const investigationTemplatesApi = {
  list: (params?: { specialty?: string; is_active?: boolean; limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<InvestigationTemplate>>(`/investigation-templates/${toQs(params)}`),
  get: (id: string) => ehrClient.get<InvestigationTemplate>(`/investigation-templates/${id}/`),
  create: (data: Partial<InvestigationTemplate>) =>
    ehrClient.post<InvestigationTemplate>("/investigation-templates/", data),
  update: (id: string, data: Partial<InvestigationTemplate>) =>
    ehrClient.patch<InvestigationTemplate>(`/investigation-templates/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/investigation-templates/${id}/`),
};

export const consultationLifecycleApi = {
  start: (encounterId: string) =>
    ehrClient.post<{ detail: string; status: string }>(`/consultations/${encounterId}/start/`, {}),
  complete: (encounterId: string) =>
    ehrClient.post<{ detail: string; status: string }>(`/consultations/${encounterId}/complete/`, {}),
  reopen: (encounterId: string, reason?: string) =>
    ehrClient.post<{ detail: string; status: string; edit_log_id: string }>(
      `/consultations/${encounterId}/reopen/`, { reason: reason || '' }
    ),
  /**
   * The doctor's SECOND exit from a consultation, alongside `complete`:
   * hold the visit open until investigation results are back. The encounter
   * stays `in_consultation` — only the flag changes.
   */
  pendingReview: (encounterId: string, note?: string) =>
    ehrClient.post<{ detail: string; status: string; pending_review: boolean; pending_review_note: string }>(
      `/consultations/${encounterId}/pending-review/`, { note: note || '' }
    ),
  /** Results are in — put the visit back in the doctor's active list. */
  resumeReview: (encounterId: string) =>
    ehrClient.post<{ detail: string; status: string; pending_review: boolean }>(
      `/consultations/${encounterId}/resume-review/`, {}
    ),
  summary: (encounterId: string) =>
    ehrClient.get<ConsultationSummary>(`/consultations/${encounterId}/summary/`),
};

export const patientClinicalHistoryApi = {
  get: (patientId: string, params?: { limit?: number }) =>
    ehrClient.get<PatientClinicalHistory>(`/patients/${patientId}/clinical-history/${toQs(params)}`),
};

// ============================================================================
// Consultation Records (ASR Clinical Scribe)
// ============================================================================

import type {
  ConsultationRecord as ConsultationRecordType,
  ConsultationRecordListItem,
} from "@/types/ehr";

export const consultationRecordsApi = {
  save: (formData: FormData) =>
    ehrClient.postFormData<ConsultationRecordType>(`/consultation-records/save/`, formData),
  history: (patientId: string, params?: { limit?: number; offset?: number }) =>
    ehrClient.get<PaginatedResponse<ConsultationRecordListItem>>(
      `/consultation-records/history/${patientId}/${toQs(params)}`,
    ),
  detail: (recordId: string) =>
    ehrClient.get<ConsultationRecordType>(`/consultation-records/${recordId}/`),
};

// ============================================================================
// Module Activations API
// ============================================================================

import type {
  HospitalModuleActivation,
  DiagnosticOrder,
  DiagnosticOrderListItem,
  CreateDiagnosticOrderPayload,
  VacutainerType,
  LabParameter,
  LabParameterListItem,
  LabOrderGrouped,
  LabOrderGroupedListItem,
  SampleCalculationResult,
  LabSection,
  SpecimenType,
  LabTest,
  LabTestListItem,
  LabPanel,
  LabSample,
  LabSampleListItem,
  LabSampleTest,
  LabSettings,
  LabLookup,
  LabLookupCategory,
  LabAcceptanceCheck,
  LabResult,
  LabResultListItem,
  LabResultValue,
  LabReport,
  RadModality,
  RadRoom,
  RadExamType,
  RadSchedule,
  RadReportTemplate,
  RadDefaultTemplateForOrder,
  RadReport,
  RadReportListItem,
  RadReportWorklistItem,
  RadWorklistSummary,
  RadLookup,
  RadAcquisition,
  LabMethod,
  LabMethodListItem,
  TestMethodConfig,
  TestSampleRequirement,
  ResultHistory,
  HL7MessageLog,
  HL7MessageLogListItem,
  LabOrderProfile,
} from "@/types/ehr";

export const moduleActivationsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<HospitalModuleActivation[]>(`/module-activations/${qs}`);
  },
  myModules: () =>
    ehrClient.get<HospitalModuleActivation[]>("/module-activations/my-modules/"),
  update: (id: string, data: { is_active: boolean; config?: Record<string, unknown> }) =>
    ehrClient.patch<HospitalModuleActivation>(`/module-activations/${id}/`, data),
};

// ── Module subscription (per-hospital + per-location entitlement) ────────────

export interface ModuleSubscriptionRegistryItem {
  code: string;
  label: string;
  category: string;
  core: boolean;
  addon: boolean;
  /** RBAC anchor codes this SKU governs (top-level or dotted). */
  rbac: string[];
}

export interface ModuleSubscriptionAccess {
  /** ``{sku_code: enabled}`` — every SKU resolved to an explicit boolean. */
  modules: Record<string, boolean>;
  registry: ModuleSubscriptionRegistryItem[];
}

export const moduleSubscriptionApi = {
  /**
   * Fetch the effective module-subscription map for the caller's hospital and
   * the given location (by id or URL slug). Used by the sidebar / app launcher /
   * role matrix to hide unsubscribed modules. Default-OFF: a subscribable module
   * reads as enabled only when explicitly granted. The backend APIs enforce the
   * same map with a 403, so this is purely a UX layer.
   */
  get: (opts?: { locationId?: string; locationSlug?: string }) => {
    const params = new URLSearchParams();
    if (opts?.locationId) params.set("location", opts.locationId);
    else if (opts?.locationSlug) params.set("location_slug", opts.locationSlug);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return ehrClient.get<ModuleSubscriptionAccess>(`/module-subscription/${qs}`);
  },
};

// ============================================================================
// Diagnostic Orders API
// ============================================================================

export const diagnosticOrdersApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<DiagnosticOrderListItem>>(`/diagnostic-orders/${qs}`);
  },
  get: (id: string) => ehrClient.get<DiagnosticOrder>(`/diagnostic-orders/${id}/`),
  create: (data: CreateDiagnosticOrderPayload) =>
    ehrClient.post<DiagnosticOrder>("/diagnostic-orders/", data),
  accept: (id: string) =>
    ehrClient.post<DiagnosticOrder>(`/diagnostic-orders/${id}/accept/`, {}),
  reject: (id: string, data: { rejection_reason: string }) =>
    ehrClient.post<DiagnosticOrder>(`/diagnostic-orders/${id}/reject/`, data),
  cancel: (id: string, data: { cancellation_reason: string }) =>
    ehrClient.post<DiagnosticOrder>(`/diagnostic-orders/${id}/cancel/`, data),
  start: (id: string) =>
    ehrClient.post<DiagnosticOrder>(`/diagnostic-orders/${id}/start/`, {}),
  complete: (id: string) =>
    ehrClient.post<DiagnosticOrder>(`/diagnostic-orders/${id}/complete/`, {}),
  patientHistory: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<DiagnosticOrderListItem>>(`/diagnostic-orders/patient-history/${qs}`);
  },
};

// ============================================================================
// Laboratory API
// ============================================================================

export const laboratoryApi = {
  // Vacutainer Types
  vacutainerTypes: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<VacutainerType>>(`/laboratory/vacutainer-types/${qs}`);
    },
    get: (id: string) => ehrClient.get<VacutainerType>(`/laboratory/vacutainer-types/${id}/`),
    create: (data: Partial<VacutainerType>) => ehrClient.post<VacutainerType>("/laboratory/vacutainer-types/", data),
    update: (id: string, data: Partial<VacutainerType>) => ehrClient.patch<VacutainerType>(`/laboratory/vacutainer-types/${id}/`, data),
    deactivate: (id: string) => ehrClient.delete<void>(`/laboratory/vacutainer-types/${id}/`),
  },
  // Independent Parameters
  parameters: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabParameterListItem>>(`/laboratory/parameters/${qs}`);
    },
    get: (id: string) => ehrClient.get<LabParameter>(`/laboratory/parameters/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<LabParameter>("/laboratory/parameters/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<LabParameter>(`/laboratory/parameters/${id}/`, data),
    deactivate: (id: string) => ehrClient.delete<void>(`/laboratory/parameters/${id}/`),
    searchUnlinked: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabParameterListItem>>(`/laboratory/parameters/search_unlinked/${qs}`);
    },
  },
  // Lab Orders (grouped)
  labOrders: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabOrderGroupedListItem>>(`/laboratory/lab-orders/${qs}`);
    },
    get: (id: string) => ehrClient.get<LabOrderGrouped>(`/laboratory/lab-orders/${id}/`),
    calculateSamples: (id: string) => ehrClient.get<SampleCalculationResult>(`/laboratory/lab-orders/${id}/calculate_samples/`),
    collectSamples: (id: string, data: { collection_point?: string; order_ids?: string[] }) =>
      /**
       * `unconfigured_tests` lists tests that could NOT be drawn because their
       * Service has no LabTest at this hospital — usually another location's
       * copy of the service. Surface it: silently ignoring it is what left
       * orders stuck in Pending Collection with a "collected" toast.
       * When NOTHING is collectable the endpoint returns 400 instead.
       */
      ehrClient.post<{ lab_order_number: string; samples_collected: number; samples: LabSample[]; unconfigured_tests?: string[] }>(`/laboratory/lab-orders/${id}/collect_samples/`, data),
    addSample: (id: string, data: { specimen_type?: string; vacutainer_type?: string; collection_point?: string }) =>
      ehrClient.post<LabSample>(`/laboratory/lab-orders/${id}/add_sample/`, data),
    /** Stamp patient arrival at the phlebotomy queue. Idempotent server-side. */
    markArrived: (id: string) =>
      ehrClient.post<LabOrderGrouped>(`/laboratory/lab-orders/${id}/mark-arrived/`, {}),
    /** Printable dispatch manifest (PDF) that travels with an outsourced package. */
    dispatchManifestUrl: (id: string) =>
      `/api/laboratory/lab-orders/${id}/dispatch-manifest/`,
    dispatchToLab: (id: string, data: { dispatch_notes?: string }) =>
      ehrClient.post<LabOrderGrouped>(`/laboratory/lab-orders/${id}/dispatch_to_lab/`, data),
    receiveResults: (id: string) =>
      ehrClient.post<LabOrderGrouped>(`/laboratory/lab-orders/${id}/receive-results/`, {}),
    uploadExternalReport: async (id: string, file: File) => {
      const formData = new FormData();
      formData.append('report_file', file);
      const token = typeof window !== 'undefined' ? localStorage.getItem('ehr_access_token') : null;
      const res = await fetch(`/api/laboratory/lab-orders/${id}/upload-external-report/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      return res.json() as Promise<LabOrderGrouped>;
    },
    samples: (id: string) => ehrClient.get<LabSample[]>(`/laboratory/lab-orders/${id}/samples/`),
    deliverExternalReport: (id: string, data: { delivery_method?: string }) =>
      ehrClient.post<LabOrderGrouped>(`/laboratory/lab-orders/${id}/deliver-external-report/`, data),
  },
  // Masters
  sections: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabSection>>(`/laboratory/sections/${qs}`);
    },
    create: (data: Partial<LabSection>) => ehrClient.post<LabSection>("/laboratory/sections/", data),
    update: (id: string, data: Partial<LabSection>) => ehrClient.patch<LabSection>(`/laboratory/sections/${id}/`, data),
  },
  specimenTypes: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<SpecimenType>>(`/laboratory/specimen-types/${qs}`);
    },
    create: (data: Partial<SpecimenType>) => ehrClient.post<SpecimenType>("/laboratory/specimen-types/", data),
    update: (id: string, data: Partial<SpecimenType>) => ehrClient.patch<SpecimenType>(`/laboratory/specimen-types/${id}/`, data),
  },
  tests: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabTestListItem>>(`/laboratory/tests/${qs}`);
    },
    get: (id: string) => ehrClient.get<LabTest>(`/laboratory/tests/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<LabTest>("/laboratory/tests/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<LabTest>(`/laboratory/tests/${id}/`, data),
    deactivate: (id: string) => ehrClient.delete<void>(`/laboratory/tests/${id}/`),
  },
  panels: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabPanel>>(`/laboratory/panels/${qs}`);
    },
    get: (id: string) => ehrClient.get<LabPanel>(`/laboratory/panels/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<LabPanel>("/laboratory/panels/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<LabPanel>(`/laboratory/panels/${id}/`, data),
  },
  // Worklists
  worklist: {
    orders: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabOrderListItem>>(`/laboratory/worklist/orders/${qs}`);
    },
    summary: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<LabOrderSummary>(`/laboratory/worklist/summary/${qs}`);
    },
    collection: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<DiagnosticOrderListItem>>(`/laboratory/worklist/collection/${qs}`);
    },
    processing: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabSampleListItem>>(`/laboratory/worklist/processing/${qs}`);
    },
    verification: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabResultListItem>>(`/laboratory/worklist/verification/${qs}`);
    },
  },
  // Samples
  samples: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabSampleListItem>>(`/laboratory/samples/${qs}`);
    },
    get: (id: string) => ehrClient.get<LabSample>(`/laboratory/samples/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<LabSample>("/laboratory/samples/", data),
    /** The configured pre-analytical checklist — identical for every sample. */
    acceptanceChecklist: () =>
      ehrClient.get<LabAcceptanceCheck[]>(`/laboratory/samples/acceptance-checklist/`),
    /**
     * Accept a sample into the lab. `acceptance_checks` maps check code -> passed.
     * Failing a check flagged `blocking` returns 400 — the sample must be
     * rejected instead, which is the point of the step.
     */
    receive: (id: string, data?: { acceptance_checks?: Record<string, boolean>; acceptance_notes?: string }) =>
      ehrClient.post<LabSample>(`/laboratory/samples/${id}/receive/`, data ?? {}),
    /**
     * Reject a sample. `rejection_reason_code` (from the Lab Lookups master) and
     * `rejection_notified_to` are both required — the reason drives the NABL
     * audit register and the notification is what lets the ward re-draw.
     * `rejection_reason` is an optional free-text supplement, never a substitute.
     */
    reject: (id: string, data: {
      rejection_reason_code: string;
      rejection_notified_to: string;
      rejection_reason?: string;
    }) => ehrClient.post<LabSample>(`/laboratory/samples/${id}/reject/`, data),
    submitResults: (id: string) => ehrClient.post<LabSample>(`/laboratory/samples/${id}/submit-results/`, {}),
    recollect: (id: string) => ehrClient.post<LabSample>(`/laboratory/samples/${id}/recollect/`, {}),
    barcodeUrl: (id: string) => `/api/laboratory/samples/${id}/barcode/`,
    barcodeLabelUrl: (id: string) => `/api/laboratory/samples/${id}/barcode-label/`,
    bulkBarcodeLabelsUrl: () => `/api/laboratory/samples/bulk-barcode-labels/`,
  },
  // Results
  results: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabResultListItem>>(`/laboratory/results/${qs}`);
    },
    get: (id: string) => ehrClient.get<LabResult>(`/laboratory/results/${id}/`),
    enterResults: (id: string, data: { values: { parameter: string; value_numeric?: string; value_text?: string }[] }) =>
      ehrClient.post<LabResult>(`/laboratory/results/${id}/enter-results/`, data),
    verify: (id: string, data: { comments?: string; verified_by_id?: string }) =>
      ehrClient.post<LabResult>(`/laboratory/results/${id}/verify/`, data),
    amend: (id: string, data: { values: { parameter: string; value_numeric?: string; value_text?: string; amendment_reason: string }[] }) =>
      ehrClient.post<LabResult>(`/laboratory/results/${id}/amend/`, data),
    cancel: (id: string, data: { reason: string }) =>
      ehrClient.post<LabResult>(`/laboratory/results/${id}/cancel/`, data),
    unverify: (id: string, data: { reason: string }) =>
      ehrClient.post<LabResult>(`/laboratory/results/${id}/unverify/`, data),
    sendToRecheck: (id: string, data: { reason: string }) =>
      ehrClient.post<LabResult>(`/laboratory/results/${id}/send-to-recheck/`, data),
    /**
     * Log the panic call for a critical result (NABH). `read_back_confirmed`
     * must be true — the recipient repeats the value back before the call
     * counts. The caller ID is taken from the session, never posted.
     */
    logCriticalNotification: (id: string, data: {
      notified_to: string;
      notified_role?: string;
      notified_at?: string;
      notify_method?: string;
      read_back_confirmed: boolean;
      notes?: string;
    }) => ehrClient.post<LabResult>(`/laboratory/results/${id}/log-critical-notification/`, data),
  },
  // Configurable lab dropdowns/checklists — Lab Masters → Lab Lookups
  lookups: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabLookup>>(`/laboratory/lookups/${qs}`);
    },
    categories: () =>
      ehrClient.get<{ value: LabLookupCategory; label: string }[]>(`/laboratory/lookups/categories/`),
    create: (data: Partial<LabLookup>) => ehrClient.post<LabLookup>("/laboratory/lookups/", data),
    update: (id: string, data: Partial<LabLookup>) =>
      ehrClient.patch<LabLookup>(`/laboratory/lookups/${id}/`, data),
    /** Soft delete — retired rows still resolve on historical records. */
    remove: (id: string) => ehrClient.delete<void>(`/laboratory/lookups/${id}/`),
  },
  // Per-hospital lab settings (singleton) — Lab Masters → Lab Settings
  settings: {
    get: () => ehrClient.get<LabSettings>(`/laboratory/settings/`),
    update: (data: Partial<Pick<LabSettings, "deverify_window_minutes" | "require_critical_notification_before_verify">>) =>
      ehrClient.patch<LabSettings>(`/laboratory/settings/`, data),
  },
  // Reports
  reports: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabReport>>(`/laboratory/reports/${qs}`);
    },
    get: (id: string) => ehrClient.get<LabReport>(`/laboratory/reports/${id}/`),
    deliver: (id: string, data: { delivery_method: string }) =>
      ehrClient.post<LabReport>(`/laboratory/reports/${id}/deliver/`, data),
    undeliver: (id: string, data: { reason: string }) =>
      ehrClient.post<LabReport>(`/laboratory/reports/${id}/undeliver/`, data),
    pdfUrl: (id: string) => `/api/laboratory/reports/${id}/pdf/`,
    // Fetches the report PDF as a Blob WITH auth + token-refresh (unlike a raw
    // fetch of pdfUrl, which 401s once the access token expires).
    getPdfBlob: (id: string) => ehrClient.getBlob(`/laboratory/reports/${id}/pdf/`),
  },
  // Methods & Instruments
  methods: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabMethodListItem>>(`/laboratory/methods/${qs}`);
    },
    get: (id: string) => ehrClient.get<LabMethod>(`/laboratory/methods/${id}/`),
    create: (data: Partial<LabMethod>) => ehrClient.post<LabMethod>("/laboratory/methods/", data),
    update: (id: string, data: Partial<LabMethod>) => ehrClient.patch<LabMethod>(`/laboratory/methods/${id}/`, data),
    deactivate: (id: string) => ehrClient.delete<void>(`/laboratory/methods/${id}/`),
  },
  // Test Method Configs
  testMethodConfigs: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<TestMethodConfig>>(`/laboratory/test-method-configs/${qs}`);
    },
    create: (data: Record<string, unknown>) => ehrClient.post<TestMethodConfig>("/laboratory/test-method-configs/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<TestMethodConfig>(`/laboratory/test-method-configs/${id}/`, data),
  },
  // Test Sample Requirements
  sampleRequirements: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<TestSampleRequirement>>(`/laboratory/sample-requirements/${qs}`);
    },
    create: (data: Record<string, unknown>) => ehrClient.post<TestSampleRequirement>("/laboratory/sample-requirements/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<TestSampleRequirement>(`/laboratory/sample-requirements/${id}/`, data),
  },
  // HL7 Message Log
  hl7Messages: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<HL7MessageLogListItem>>(`/laboratory/hl7-messages/${qs}`);
    },
    get: (id: string) => ehrClient.get<HL7MessageLog>(`/laboratory/hl7-messages/${id}/`),
  },
  // Order Profiles (HL7 OBR mapping)
  orderProfiles: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<LabOrderProfile>>(`/laboratory/order-profiles/${qs}`);
    },
    create: (data: Record<string, unknown>) => ehrClient.post<LabOrderProfile>("/laboratory/order-profiles/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<LabOrderProfile>(`/laboratory/order-profiles/${id}/`, data),
  },
  // Result Amendment History
  resultHistory: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<ResultHistory>>(`/laboratory/result-history/${qs}`);
    },
    get: (id: string) => ehrClient.get<ResultHistory>(`/laboratory/result-history/${id}/`),
  },
};

// ============================================================================
// Radiology API
// ============================================================================

export const radiologyApi = {
  // Masters
  modalities: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<RadModality>>(`/radiology/modalities/${qs}`);
    },
    get: (id: string) => ehrClient.get<RadModality>(`/radiology/modalities/${id}/`),
    create: (data: Partial<RadModality>) => ehrClient.post<RadModality>("/radiology/modalities/", data),
    update: (id: string, data: Partial<RadModality>) => ehrClient.patch<RadModality>(`/radiology/modalities/${id}/`, data),
    deactivate: (id: string) => ehrClient.delete<void>(`/radiology/modalities/${id}/`),
  },
  rooms: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<RadRoom>>(`/radiology/rooms/${qs}`);
    },
    get: (id: string) => ehrClient.get<RadRoom>(`/radiology/rooms/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<RadRoom>("/radiology/rooms/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<RadRoom>(`/radiology/rooms/${id}/`, data),
  },
  examTypes: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<RadExamType>>(`/radiology/exam-types/${qs}`);
    },
    get: (id: string) => ehrClient.get<RadExamType>(`/radiology/exam-types/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<RadExamType>("/radiology/exam-types/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<RadExamType>(`/radiology/exam-types/${id}/`, data),
  },
  // Worklists
  worklist: {
    pending: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<DiagnosticOrderListItem>>(`/radiology/worklist/pending/${qs}`);
    },
    reporting: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<DiagnosticOrderListItem>>(`/radiology/worklist/reporting/${qs}`);
    },
    allReports: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<RadReportWorklistItem>>(`/radiology/worklist/all-reports/${qs}`);
    },
    summary: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<RadWorklistSummary>(`/radiology/worklist/summary/${qs}`);
    },
  },
  // Schedules
  schedules: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<RadSchedule>>(`/radiology/schedules/${qs}`);
    },
    get: (id: string) => ehrClient.get<RadSchedule>(`/radiology/schedules/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<RadSchedule>("/radiology/schedules/", data),
    availability: (params: Record<string, string>) => {
      const qs = "?" + new URLSearchParams(params).toString();
      return ehrClient.get<{ time: string; available: boolean }[]>(`/radiology/schedules/availability/${qs}`);
    },
    arrive: (id: string) => ehrClient.post<RadSchedule>(`/radiology/schedules/${id}/arrive/`, {}),
    // url_path on the backend actions is hyphenated (`start-exam` /
    // `complete-exam`); the underscored variants 404'd, so the Start Exam and
    // Complete buttons silently did nothing. See BUG-107.
    startExam: (id: string) => ehrClient.post<RadSchedule>(`/radiology/schedules/${id}/start-exam/`, {}),
    completeExam: (id: string) => ehrClient.post<RadSchedule>(`/radiology/schedules/${id}/complete-exam/`, {}),
  },
  // Report Templates
  reportTemplates: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<RadReportTemplate>>(`/radiology/report-templates/${qs}`);
    },
    get: (id: string) => ehrClient.get<RadReportTemplate>(`/radiology/report-templates/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<RadReportTemplate>("/radiology/report-templates/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<RadReportTemplate>(`/radiology/report-templates/${id}/`, data),
    // Which template should open by default for this order's exam?
    // Always 200 — an unmapped service or an exam with no default is normal,
    // not an error; `reason` says which link is missing.
    // `radiologist` decides WHOSE wording is returned. Omit it and the server
    // falls back to the caller — which yields nothing when a non-radiologist
    // (an admin, a transcriptionist) opens the report, because every template
    // belongs to a specific doctor.
    forOrder: (orderId: string, radiologistId?: string) =>
      ehrClient.get<RadDefaultTemplateForOrder>(
        `/radiology/report-templates/for-order/?order=${encodeURIComponent(orderId)}` +
          (radiologistId ? `&radiologist=${encodeURIComponent(radiologistId)}` : ""),
      ),
  },
  // Reports
  reports: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<RadReportListItem>>(`/radiology/reports/${qs}`);
    },
    get: (id: string) => ehrClient.get<RadReport>(`/radiology/reports/${id}/`),
    create: (data: Record<string, unknown>) => ehrClient.post<RadReport>("/radiology/reports/", data),
    update: (id: string, data: Record<string, unknown>) => ehrClient.patch<RadReport>(`/radiology/reports/${id}/`, data),
    finalize: (id: string) => ehrClient.post<RadReport>(`/radiology/reports/${id}/finalize/`, {}),
    addendum: (id: string, data: { findings?: string; impressions?: string; recommendations?: string }) =>
      ehrClient.post<RadReport>(`/radiology/reports/${id}/addendum/`, data),
    reject: (id: string, data: { rejection_reason: string }) =>
      ehrClient.post<RadReport>(`/radiology/reports/${id}/reject/`, data),
    resubmit: (id: string) => ehrClient.post<RadReport>(`/radiology/reports/${id}/resubmit/`, {}),
    deliver: (id: string) => ehrClient.post<RadReport>(`/radiology/reports/${id}/deliver/`, {}),
  },
  // Acquisition dropdown masters. Reads are satisfied by any radiology grant,
  // so a technician holding only `radiology.worklist` can populate the dialog.
  lookups: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<RadLookup>>(`/radiology/lookups/${qs}`);
    },
    create: (data: Record<string, unknown>) => ehrClient.post<RadLookup>("/radiology/lookups/", data),
    update: (id: string, data: Record<string, unknown>) =>
      ehrClient.patch<RadLookup>(`/radiology/lookups/${id}/`, data),
    remove: (id: string) => ehrClient.delete<void>(`/radiology/lookups/${id}/`),
  },
  // What actually happened during the exam.
  acquisitions: {
    /**
     * Records the acquisition AND moves the order to `resulted`, atomically.
     * Replaces a bare `diagnosticOrdersApi.complete()` for radiology — that
     * endpoint still works and is still used by anything that has no dialog.
     *
     * Send the WHOLE form every time: the backend treats this as a full-form
     * upsert, not a partial merge, so an omitted field is "not provided" rather
     * than "keep the previous value".
     */
    complete: (data: Record<string, unknown>) =>
      ehrClient.post<RadAcquisition>("/radiology/acquisitions/complete/", data),
    /** Returns 204 (parsed as null) when nothing was recorded for the order. */
    byOrder: (orderId: string) =>
      ehrClient.get<RadAcquisition | null>(
        `/radiology/acquisitions/by-order/?order=${orderId}`
      ),
  },
};

// ============================================================================
// Named API functions — Dashboard (CEO KPIs)
// ============================================================================

export interface DashboardData {
  start_date: string;
  end_date: string;
  is_single_day: boolean;
  is_today_view: boolean;
  generated_at: string;

  total_patients: number;
  new_patients: number;
  period_patients: number;
  period_appointments: number;
  period_completed_appointments: number;

  appointment_summary: {
    total: number;
    booked: number;
    checked_in: number;
    in_consultation: number;
    completed: number;
    cancelled: number;
    no_show: number;
  };
  encounter_summary: {
    total: number;
    checked_in: number;
    vitals_taken: number;
    in_consultation: number;
    completed: number;
  };
  booking_type_breakdown: {
    scheduled: number;
    walk_in: number;
    follow_up: number;
    emergency: number;
  };
  encounter_type_breakdown: {
    opd: number;
    emergency: number;
    follow_up: number;
  };

  registration_trend: { date: string; count: number }[];
  appointment_trend: { date: string; total: number; completed: number; cancelled: number; no_show: number }[];
  hourly_appointments: { hour: number; count: number }[] | null;

  department_performance: { name: string; total: number; completed: number; in_progress: number }[];
  top_doctors_opd: { name: string; total: number; completed: number; in_progress: number; no_show: number }[];
  top_doctors_ipd: { name: string; total: number; completed: number; in_progress: number; no_show: number }[];

  revenue: {
    total: number;
    bills: number;
    trend: { date: string; total: number; count: number }[];
    department_revenue: { name: string; total: number; count: number }[];
    collected_period: number;
    collection_efficiency: number;
    outstanding_total: number;
    ar_aging: { "0_30": number; "31_60": number; "61_90": number; "90_plus": number };
    collections_by_mode: { mode: string; total: number; count: number }[];
    payor_vs_patient: { payor: number; patient: number };
  } | null;

  bed_occupancy: {
    total_beds: number;
    occupied: number;
    vacant: number;
    reserved: number;
    maintenance: number;
    occupancy_rate: number;
    active_admissions: number;
  } | null;
  ward_occupancy: {
    name: string;
    total: number;
    occupied: number;
    vacant: number;
    reserved: number;
    occupancy_rate: number;
  }[];
  patient_flow: {
    granularity: "hour" | "day";
    data: { label: string; admissions: number; discharges: number }[];
    total_admissions: number;
    total_discharges: number;
  } | null;
  alos: { avg_days: number; discharged_count: number } | null;

  diagnostics_summary: {
    laboratory: { total: number; pending: number; completed: number; cancelled: number };
    radiology: { total: number; pending: number; completed: number; cancelled: number };
  } | null;
  lab_tat: { avg_minutes: number; completed: number } | null;
  radiology_tat: { avg_minutes: number; completed: number } | null;

  ot_summary: {
    total_scheduled: number;
    completed: number;
    active: number;
    scheduled: number;
    cancelled: number;
    postponed: number;
    avg_duration_minutes: number;
  } | null;
  emergency_summary: {
    total: number;
    waiting: number;
    triaged: number;
    admitted: number;
    discharged: number;
    transferred: number;
    lama: number;
    live_in_er: number;
    avg_wait_minutes: number;
    triage_breakdown: { level: string; label: string; count: number }[];
  } | null;
  pharmacy_summary: {
    total_revenue: number;
    total_collected: number;
    total_sales: number;
    by_type: { type: string; total: number; count: number }[];
  } | null;
  inventory_summary: {
    stock_value: number;
    total_units: number;
    expiring_30d: number;
    expired: number;
  } | null;
  insurance_summary: {
    total: number;
    approved: number;
    rejected: number;
    pending: number;
    cancelled: number;
    approved_amount: number;
  } | null;

  recent_activity: { type: string; label: string; at: string }[];

  staff_summary: {
    total: number;
    doctors: number;
    nurses: number;
    receptionists: number;
    admins: number;
    other: number;
  };
}

export const dashboardApi = {
  get: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<DashboardData>(`/dashboard/${qs}`);
  },
};

// ============================================================================
// Inventory Report Analytics
// ============================================================================

import type {
  InventoryReportDashboard,
  StockReportItem,
  StockAnalytics,
  IndentReportItem,
  IndentAnalytics,
  PRReportItem,
  PRAnalytics,
  POReportItem,
  POAnalytics,
  GRNReportItem,
  GRNAnalytics,
  StockLedgerResponse,
} from "@/types/ehr";

export const inventoryReportsApi = {
  dashboard: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<InventoryReportDashboard>(`/inventory-reports/dashboard/${qs}`);
  },
  stockAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<StockAnalytics>(`/inventory-reports/stock-analytics/${qs}`);
  },
  stockReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<StockReportItem>>(`/inventory-reports/stock/${qs}`);
  },
  indentAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<IndentAnalytics>(`/inventory-reports/indent-analytics/${qs}`);
  },
  indentReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<IndentReportItem>>(`/inventory-reports/indents/${qs}`);
  },
  prAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PRAnalytics>(`/inventory-reports/pr-analytics/${qs}`);
  },
  prReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PRReportItem>>(`/inventory-reports/purchase-requisitions/${qs}`);
  },
  poAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<POAnalytics>(`/inventory-reports/po-analytics/${qs}`);
  },
  poReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<POReportItem>>(`/inventory-reports/purchase-orders/${qs}`);
  },
  grnAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<GRNAnalytics>(`/inventory-reports/grn-analytics/${qs}`);
  },
  grnReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<GRNReportItem>>(`/inventory-reports/grns/${qs}`);
  },
  stockLedger: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<StockLedgerResponse>(`/inventory-reports/stock-ledger/${qs}`);
  },
};

// ============================================================================
// Named API functions — Pharmacy
// ============================================================================

import type {
  PharmacySettings as PharmacySettingsType,
  PharmacySettingsPayload,
  PharmacySaleListItem,
  PharmacySaleDetail,
  PharmacySaleCreatePayload,
  PharmacySaleItem as PharmacySaleItemType,
  PharmacySaleItemPayload,
  PharmacySaleFinalizePayload,
  PharmacyReturnListItem,
  PharmacyReturnDetail,
  PharmacyReturnCreatePayload,
  PharmacyReturnAcknowledgePayload,
  PharmacyReturnRejectPayload,
  PharmacyDashboardKPIs,
  PharmacyItemSearchResult,
  PharmacyItemStockBreakupRow,
  PharmacySaleBatchRow,
  PharmacyStockCheckFilters,
  PharmacyExternalConfig,
  PharmacyBounceConfig,
  PharmacyBounceReason,
  PharmacyBounceReasonPayload,
  PharmacySaleItemRemovePayload,
} from "@/types/ehr";

export const pharmacySettingsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacySettingsType>>(`/pharmacy-settings/${qs}`);
  },
  get: (id: string) => ehrClient.get<PharmacySettingsType>(`/pharmacy-settings/${id}/`),
  create: (data: PharmacySettingsPayload) =>
    ehrClient.post<PharmacySettingsType>("/pharmacy-settings/", data),
  update: (id: string, data: Partial<PharmacySettingsPayload>) =>
    ehrClient.patch<PharmacySettingsType>(`/pharmacy-settings/${id}/`, data),
};

export const pharmacySalesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacySaleListItem>>(`/pharmacy-sales/${qs}`);
  },
  get: (id: string) => ehrClient.get<PharmacySaleDetail>(`/pharmacy-sales/${id}/`),
  create: (data: PharmacySaleCreatePayload) =>
    ehrClient.post<PharmacySaleDetail>("/pharmacy-sales/", data),
  update: (id: string, data: Partial<PharmacySaleCreatePayload>) =>
    ehrClient.patch<PharmacySaleDetail>(`/pharmacy-sales/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/pharmacy-sales/${id}/`),
  addItem: (saleId: string, data: PharmacySaleItemPayload) =>
    ehrClient.post<PharmacySaleItemType>(`/pharmacy-sales/${saleId}/items/add/`, data),
  /**
   * Remove one line, optionally saying why.
   *
   * POST, not DELETE: the removal reason travels in the body and
   * `EhrApiClient.delete` sends none. The server still accepts DELETE, so
   * nothing that has not been migrated breaks — but every caller routes here,
   * and an empty body behaves exactly as the old DELETE did.
   */
  removeItem: (saleId: string, itemId: string, data?: PharmacySaleItemRemovePayload) =>
    ehrClient.post<void>(
      `/pharmacy-sales/${saleId}/items/${itemId}/remove/`,
      data ?? {},
    ),
  updateItem: (saleId: string, itemId: string, data: Partial<PharmacySaleItemPayload>) =>
    ehrClient.patch<PharmacySaleItemType>(`/pharmacy-sales/${saleId}/items/${itemId}/update/`, data),
  finalize: (saleId: string, data: PharmacySaleFinalizePayload) =>
    ehrClient.post<PharmacySaleDetail>(`/pharmacy-sales/${saleId}/finalize/`, data),
  /** Re-derive line prices from current stock (FEFO) before billing. Returns the fresh sale. */
  recalculate: (saleId: string) =>
    ehrClient.post<PharmacySaleDetail>(`/pharmacy-sales/${saleId}/recalculate/`, {}),
  dashboard: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PharmacyDashboardKPIs>(`/pharmacy-sales/dashboard/${qs}`);
  },
  itemSearch: (params: Record<string, string>) => {
    const qs = "?" + new URLSearchParams(params).toString();
    return ehrClient.get<PharmacyItemSearchResult[]>(`/pharmacy-sales/item-search/${qs}`);
  },
  /**
   * Batches of one item in this sale's store, each carrying the price the
   * sale's payer would actually be charged. Use this for the batch picker
   * instead of `storeInventoryApi.list`, whose `unit_cost` is the raw stock
   * column and can differ from what the line will bill at.
   */
  saleBatches: (saleId: string, params: Record<string, string>) => {
    const qs = "?" + new URLSearchParams(params).toString();
    return ehrClient.get<PharmacySaleBatchRow[]>(
      `/pharmacy-sales/${saleId}/sale-batches/${qs}`
    );
  },
  /** Per-store stock breakup for one item, scoped to the login store's location. */
  itemStockBreakup: (params: Record<string, string>) => {
    const qs = "?" + new URLSearchParams(params).toString();
    return ehrClient.get<PharmacyItemStockBreakupRow[]>(
      `/pharmacy-sales/item-stock-breakup/${qs}`
    );
  },
  /** Category + sub-category options for the Stock Check dialog (drugs & medicines only). */
  stockCheckFilters: () =>
    ehrClient.get<PharmacyStockCheckFilters>(`/pharmacy-sales/stock-check-filters/`),
  /**
   * Whether this store may sell to an unregistered walk-in, and how to capture
   * them (fields, UHID prefix, title/lookup options).
   *
   * Lives on pharmacy-sales, not on /pharmacy-settings/ or /op-billing-master/,
   * because a pharmacist holds neither `pharmacy.settings` nor
   * `billing.op_master` — reading either from the sale screen would 403 and the
   * feature would never render for the role that uses it.
   */
  /**
   * Whether this store demands a reason when a prescription line is removed or
   * short-dispensed, plus the reason lists to offer. Served from here rather
   * than `/pharmacy-settings/` because a pharmacist does not hold
   * `pharmacy.settings`.
   */
  bounceConfig: (params: Record<string, string>) => {
    const qs = "?" + new URLSearchParams(params).toString();
    return ehrClient.get<PharmacyBounceConfig>(
      `/pharmacy-sales/bounce-config/${qs}`
    );
  },
  externalConfig: (params: Record<string, string>) => {
    const qs = "?" + new URLSearchParams(params).toString();
    return ehrClient.get<PharmacyExternalConfig>(
      `/pharmacy-sales/external-config/${qs}`
    );
  },
};

// ── IPD Picklist (Pharmacy → Settings → IPD Picklist Config) ────────────────
import type {
  IPDPicklistConfigType,
  IPDPicklistConfigPayload,
  PicklistGenerationLogType,
} from "@/types/ehr";

export const ipdPicklistConfigsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<IPDPicklistConfigType>>(`/ipd-picklist-configs/${qs}`);
  },
  get: (id: string) => ehrClient.get<IPDPicklistConfigType>(`/ipd-picklist-configs/${id}/`),
  create: (data: IPDPicklistConfigPayload) =>
    ehrClient.post<IPDPicklistConfigType>("/ipd-picklist-configs/", data),
  update: (id: string, data: Partial<IPDPicklistConfigPayload>) =>
    ehrClient.patch<IPDPicklistConfigType>(`/ipd-picklist-configs/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/ipd-picklist-configs/${id}/`),
  forceGenerate: (id: string, admissionId: string) =>
    ehrClient.post<PicklistGenerationLogType>(
      `/ipd-picklist-configs/${id}/force-generate/`,
      { admission_id: admissionId },
    ),
};

export const picklistGenerationLogsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PicklistGenerationLogType>>(`/picklist-generation-logs/${qs}`);
  },
  get: (id: string) => ehrClient.get<PicklistGenerationLogType>(`/picklist-generation-logs/${id}/`),
};

export const pharmacyReturnsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacyReturnListItem>>(`/pharmacy-returns/${qs}`);
  },
  get: (id: string) => ehrClient.get<PharmacyReturnDetail>(`/pharmacy-returns/${id}/`),
  create: (data: PharmacyReturnCreatePayload) =>
    ehrClient.post<PharmacyReturnDetail>("/pharmacy-returns/", data),
  approve: (id: string) =>
    ehrClient.post<PharmacyReturnDetail>(`/pharmacy-returns/${id}/approve/`, {}),
  complete: (id: string) =>
    ehrClient.post<PharmacyReturnDetail>(`/pharmacy-returns/${id}/complete/`, {}),
  reject: (id: string, payload?: PharmacyReturnRejectPayload) =>
    ehrClient.post<PharmacyReturnDetail>(`/pharmacy-returns/${id}/reject/`, payload ?? {}),
  acknowledge: (id: string, payload: PharmacyReturnAcknowledgePayload) =>
    ehrClient.post<PharmacyReturnDetail>(`/pharmacy-returns/${id}/acknowledge/`, payload),
};

// ── Pharmacy Reports ────────────────────────────────────────────────────────

import type {
  PharmacySaleAnalytics,
  PharmacySaleReportItem,
  PharmacyStockAnalytics,
  PharmacyStockReportItem,
  PharmacyNearExpiryAnalytics,
  PharmacyNearExpiryItem,
  PharmacyReturnAnalytics,
  PharmacyReturnReportItem,
  PharmacyCollectionAnalytics,
  PharmacyCollectionReportItem,
  PharmacyItemwiseAnalytics,
  PharmacyItemwiseSaleItem,
  PharmacyItemwiseDetailItem,
  StockLedgerResponse as StockLedgerResponsePh,
} from "@/types/ehr";

export const pharmacyReportsApi = {
  saleAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PharmacySaleAnalytics>(`/pharmacy-reports/sale-analytics/${qs}`);
  },
  saleReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacySaleReportItem>>(`/pharmacy-reports/sales/${qs}`);
  },
  pharmacyStockAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PharmacyStockAnalytics>(`/pharmacy-reports/pharmacy-stock-analytics/${qs}`);
  },
  pharmacyStockReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacyStockReportItem>>(`/pharmacy-reports/pharmacy-stock/${qs}`);
  },
  stockLedger: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<StockLedgerResponsePh>(`/pharmacy-reports/stock-ledger/${qs}`);
  },
  nearExpiryAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PharmacyNearExpiryAnalytics>(`/pharmacy-reports/near-expiry-analytics/${qs}`);
  },
  nearExpiryReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacyNearExpiryItem>>(`/pharmacy-reports/near-expiry/${qs}`);
  },
  returnAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PharmacyReturnAnalytics>(`/pharmacy-reports/return-analytics/${qs}`);
  },
  returnReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacyReturnReportItem>>(`/pharmacy-reports/returns/${qs}`);
  },
  collectionAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PharmacyCollectionAnalytics>(`/pharmacy-reports/collection-analytics/${qs}`);
  },
  collectionReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacyCollectionReportItem>>(`/pharmacy-reports/collections/${qs}`);
  },
  itemwiseAnalytics: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PharmacyItemwiseAnalytics>(`/pharmacy-reports/itemwise-analytics/${qs}`);
  },
  itemwiseSaleReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacyItemwiseSaleItem>>(`/pharmacy-reports/itemwise-sales/${qs}`);
  },
  itemwiseDetailReport: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacyItemwiseDetailItem>>(`/pharmacy-reports/itemwise-detail/${qs}`);
  },
};

// ── Sales Report (cross-module) ─────────────────────────────────────────────

import type {
  SalesReportEligibleUser,
  SalesReportResponse,
} from "@/types/ehr";

export const salesReportApi = {
  eligibleUsers: () =>
    ehrClient.get<SalesReportEligibleUser[]>(`/sales-report/eligible-users/`),
  generate: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<SalesReportResponse>(`/sales-report/report/${qs}`);
  },
};

// ── DICOM / PACS ────────────────────────────────────────────────────────────

import type {
  DicomConfig,
  DicomToken,
  DicomStudy,
  DicomStudyDetail,
} from "@/types/ehr";

// ============================================================================
// IPD Doctor Rounds
// ============================================================================
import type { DoctorRound, DoctorRoundSummary, IPDWorklistResponse, EmergencyWorklistResponse, EmergencyDischarge, EmergencyTransfer, EmergencyDischargeInitiatePayload, EmergencyTransferRaisePayload } from "@/types/ehr";

export const ipdWorklistApi = {
  get: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<IPDWorklistResponse>(`/ipd-worklist/${qs}`);
  },
};

export const emergencyWorklistApi = {
  get: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<EmergencyWorklistResponse>(`/emergency-worklist/${qs}`);
  },
};

// 3-step ER Discharge workflow
export const emergencyDischargesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<EmergencyDischarge>>(`/emergency-discharges/${qs}`);
  },
  get: (id: string) => ehrClient.get<EmergencyDischarge>(`/emergency-discharges/${id}/`),
  initiate: (data: EmergencyDischargeInitiatePayload) =>
    ehrClient.post<EmergencyDischarge>("/emergency-discharges/", data),
  nursingClearance: (id: string, notes: string = "") =>
    ehrClient.post<EmergencyDischarge>(`/emergency-discharges/${id}/nursing-clearance/`, { notes }),
  cancelNursingClearance: (id: string) =>
    ehrClient.post<EmergencyDischarge>(`/emergency-discharges/${id}/cancel-nursing-clearance/`, {}),
  // Billing clearance is automatic — driven by the Bill post_save signal on the
  // backend. There is no manual endpoint. Generate a non-draft bill and the
  // workflow's `bill_cleared` flips to true; void it (before discharge) and it
  // reverts.
  complete: (id: string) =>
    ehrClient.post<EmergencyDischarge>(`/emergency-discharges/${id}/complete/`, {}),
  cancelIntimation: (id: string, reason: string = "") =>
    ehrClient.post<EmergencyDischarge>(`/emergency-discharges/${id}/cancel-intimation/`, { reason }),
};

// 3-step ER → IP Transfer workflow (Request → Nursing → AdmissionRequest).
// Nursing clearance auto-marks the transfer billing-cleared with
// `billing_option='transfer_to_ip'`; the service rebind runs from the
// `post_save(Admission)` signal once the IP Admission is created.
export const emergencyTransfersApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<EmergencyTransfer>>(`/emergency-transfers/${qs}`);
  },
  get: (id: string) => ehrClient.get<EmergencyTransfer>(`/emergency-transfers/${id}/`),
  raise: (data: EmergencyTransferRaisePayload) =>
    ehrClient.post<EmergencyTransfer>("/emergency-transfers/", data),
  nursingClearance: (id: string, notes: string = "") =>
    ehrClient.post<EmergencyTransfer>(`/emergency-transfers/${id}/nursing-clearance/`, { notes }),
  cancel: (id: string, reason: string = "") =>
    ehrClient.post<EmergencyTransfer>(`/emergency-transfers/${id}/cancel/`, { reason }),
};

export const doctorRoundsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<DoctorRound>>(`/doctor-rounds/${qs}`);
  },
  get: (id: string) => ehrClient.get<DoctorRound>(`/doctor-rounds/${id}/`),
  create: (data: { admission_id: string; round_type?: string }) =>
    ehrClient.post<DoctorRound>("/doctor-rounds/", data),
  update: (id: string, data: Partial<DoctorRound>) =>
    ehrClient.patch<DoctorRound>(`/doctor-rounds/${id}/`, data),
  complete: (id: string) =>
    ehrClient.post<DoctorRound>(`/doctor-rounds/${id}/complete/`, {}),
  summary: (id: string) =>
    ehrClient.get<DoctorRoundSummary>(`/doctor-rounds/${id}/summary/`),
  lastVitals: (admissionId: string) =>
    ehrClient.get<{ vitals: Record<string, unknown> | null }>(
      `/doctor-rounds/last-vitals/?admission_id=${admissionId}`
    ),
};

export const dicomApi = {
  config: {
    get: () => ehrClient.get<DicomConfig>("/dicom/config/my-config/"),
    create: (data: Partial<DicomConfig>) =>
      ehrClient.post<DicomConfig>("/dicom/config/", data),
    update: (id: string, data: Partial<DicomConfig>) =>
      ehrClient.patch<DicomConfig>(`/dicom/config/${id}/`, data),
  },
  token: () => ehrClient.get<DicomToken>("/dicom/token/"),
  studies: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<{ count: number; results: DicomStudy[] }>(
        `/dicom/studies/${qs}`
      );
    },
    get: (studyUID: string) =>
      ehrClient.get<DicomStudyDetail>(`/dicom/studies/${studyUID}/`),
  },
};

// ============================================================================
// Operation Theatre (OT) API
// ============================================================================

import type {
  OTRoomType, OTRoom, SurgeryRequestListItem, SurgeryRequest, CreateSurgeryRequestPayload,
  SurgeryScheduleListItem, SurgerySchedule, CreateSurgerySchedulePayload, SurgicalTeamMember,
  PACListItem, PreAnesthesiaCheckup, SurgicalConsent, SurgicalSafetyChecklist,
  AnesthesiaRecord, IntraOperativeVital, IntraOperativeDrug, FluidRecord, FluidOutput,
  OperativeNote, OperativeNoteTemplate, SurgicalCount, SpecimenRecord, ImplantRecord, DrainRecord,
  SurgicalComplication, OTConsumableUsage, PACURecordListItem, PACURecord, PACUVital,
  SurgeonPreferenceCardListItem, SurgeonPreferenceCard, InstrumentSet, SterilizationCycle,
  OTDashboardStats, OTDirectorDashboard, SurgeryCaseRow, SurgeryCaseSummary, ConsentSectionType, ConsentTemplate,
} from "@/types/ehr";

const otQs = (params?: Record<string, string>) =>
  params ? "?" + new URLSearchParams(params).toString() : "";

export const otRoomTypesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<OTRoomType>>(`/ot-room-types/${otQs(params)}`),
  get: (id: string) => ehrClient.get<OTRoomType>(`/ot-room-types/${id}/`),
  create: (data: Partial<OTRoomType>) =>
    ehrClient.post<OTRoomType>("/ot-room-types/", data),
  update: (id: string, data: Partial<OTRoomType>) =>
    ehrClient.patch<OTRoomType>(`/ot-room-types/${id}/`, data),
};

export const otRoomsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<OTRoom>>(`/ot-rooms/${otQs(params)}`),
  get: (id: string) => ehrClient.get<OTRoom>(`/ot-rooms/${id}/`),
  create: (data: Partial<OTRoom>) => ehrClient.post<OTRoom>("/ot-rooms/", data),
  update: (id: string, data: Partial<OTRoom>) =>
    ehrClient.patch<OTRoom>(`/ot-rooms/${id}/`, data),
  changeStatus: (id: string, data: { status: string; notes?: string }) =>
    ehrClient.post<OTRoom>(`/ot-rooms/${id}/change-status/`, data),
  schedule: (id: string, params?: Record<string, string>) =>
    ehrClient.get<SurgeryScheduleListItem[]>(`/ot-rooms/${id}/schedule/${otQs(params)}`),
};

export const surgeryRequestsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgeryRequestListItem>>(`/surgery-requests/${otQs(params)}`),
  get: (id: string) => ehrClient.get<SurgeryRequest>(`/surgery-requests/${id}/`),
  create: (data: CreateSurgeryRequestPayload) =>
    ehrClient.post<SurgeryRequest>("/surgery-requests/", data),
  update: (id: string, data: Partial<SurgeryRequest>) =>
    ehrClient.patch<SurgeryRequest>(`/surgery-requests/${id}/`, data),
  cancel: (id: string, data: { reason: string }) =>
    ehrClient.post<SurgeryRequest>(`/surgery-requests/${id}/cancel/`, data),
  billingClearance: (id: string, notes?: string) =>
    ehrClient.post<SurgeryRequest>(`/surgery-requests/${id}/billing-clearance/`, { notes: notes ?? "" }),
  cancelBillingClearance: (id: string) =>
    ehrClient.post<SurgeryRequest>(`/surgery-requests/${id}/cancel-billing-clearance/`, {}),
  postProcedureToBill: (id: string) =>
    ehrClient.post<SurgeryRequest>(`/surgery-requests/${id}/post-procedure-to-bill/`, {}),
};

export const surgerySchedulesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgeryScheduleListItem>>(`/surgery-schedules/${otQs(params)}`),
  get: (id: string) => ehrClient.get<SurgerySchedule>(`/surgery-schedules/${id}/`),
  create: (data: CreateSurgerySchedulePayload) =>
    ehrClient.post<SurgerySchedule>("/surgery-schedules/", data),
  update: (id: string, data: Partial<SurgerySchedule>) =>
    ehrClient.patch<SurgerySchedule>(`/surgery-schedules/${id}/`, data),
  today: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgeryScheduleListItem>>(`/surgery-schedules/today/${otQs(params)}`),
  calendar: (params?: Record<string, string>) =>
    ehrClient.get<SurgeryScheduleListItem[]>(`/surgery-schedules/calendar/${otQs(params)}`),
  availability: (params: Record<string, string>) =>
    ehrClient.get<{ available: boolean; conflicts: unknown[] }>(`/surgery-schedules/availability/${otQs(params)}`),
  shiftToOt: (id: string) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/shift-to-ot/`, {}),
  startSurgery: (id: string) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/start/`, {}),
  completeSurgery: (id: string) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/complete-surgery/`, {}),
  shiftToPacu: (id: string) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/shift-to-pacu/`, {}),
  shiftToWard: (id: string) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/shift-to-ward/`, {}),
  markCompleted: (id: string) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/mark-completed/`, {}),
  cancel: (id: string, data: { reason: string }) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/cancel/`, data),
  postpone: (id: string, data: { reason: string; postponed_to: string }) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/postpone/`, data),
  refreshGates: (id: string) =>
    ehrClient.post<SurgerySchedule>(`/surgery-schedules/${id}/refresh-gates/`, {}),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  report: (id: string) => ehrClient.get<any>(`/surgery-schedules/${id}/report/`),
};

export const surgeryCasesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgeryCaseRow>>(`/surgery-cases/${otQs(params)}`),
  summary: (params?: Record<string, string>) =>
    ehrClient.get<SurgeryCaseSummary>(`/surgery-cases/summary/${otQs(params)}`),
};

export const surgicalTeamApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgicalTeamMember>>(`/surgical-team-members/${otQs(params)}`),
  create: (data: Partial<SurgicalTeamMember>) =>
    ehrClient.post<SurgicalTeamMember>("/surgical-team-members/", data),
  update: (id: string, data: Partial<SurgicalTeamMember>) =>
    ehrClient.patch<SurgicalTeamMember>(`/surgical-team-members/${id}/`, data),
  delete: (id: string) => ehrClient.delete(`/surgical-team-members/${id}/`),
};

export const pacApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<PACListItem>>(`/pac/${otQs(params)}`),
  get: (id: string) => ehrClient.get<PreAnesthesiaCheckup>(`/pac/${id}/`),
  create: (data: Partial<PreAnesthesiaCheckup>) =>
    ehrClient.post<PreAnesthesiaCheckup>("/pac/", data),
  update: (id: string, data: Partial<PreAnesthesiaCheckup>) =>
    ehrClient.patch<PreAnesthesiaCheckup>(`/pac/${id}/`, data),
  clear: (id: string, data: { pac_status: string; conditions?: string }) =>
    ehrClient.post<PreAnesthesiaCheckup>(`/pac/${id}/clear/`, data),
};

export const surgicalConsentsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgicalConsent>>(`/surgical-consents/${otQs(params)}`),
  get: (id: string) => ehrClient.get<SurgicalConsent>(`/surgical-consents/${id}/`),
  create: (data: Partial<SurgicalConsent>) =>
    ehrClient.post<SurgicalConsent>("/surgical-consents/", data),
  withdraw: (id: string, data: { reason: string }) =>
    ehrClient.post<SurgicalConsent>(`/surgical-consents/${id}/withdraw/`, data),
};

export const consentSectionTypesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<ConsentSectionType>>(`/consent-section-types/${otQs(params)}`),
  get: (id: string) => ehrClient.get<ConsentSectionType>(`/consent-section-types/${id}/`),
  create: (data: Partial<ConsentSectionType>) =>
    ehrClient.post<ConsentSectionType>("/consent-section-types/", data),
  update: (id: string, data: Partial<ConsentSectionType>) =>
    ehrClient.patch<ConsentSectionType>(`/consent-section-types/${id}/`, data),
};

export const consentTemplatesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<ConsentTemplate>>(`/consent-templates/${otQs(params)}`),
  get: (id: string) => ehrClient.get<ConsentTemplate>(`/consent-templates/${id}/`),
  create: (data: Partial<ConsentTemplate>) =>
    ehrClient.post<ConsentTemplate>("/consent-templates/", data),
  update: (id: string, data: Partial<ConsentTemplate>) =>
    ehrClient.patch<ConsentTemplate>(`/consent-templates/${id}/`, data),
};

export const safetyChecklistApi = {
  get: async (scheduleId: string): Promise<SurgicalSafetyChecklist> => {
    const res = await ehrClient.get<PaginatedResponse<SurgicalSafetyChecklist>>(
      `/surgical-safety-checklists/?surgery_schedule=${scheduleId}&limit=1`,
    );
    if (!res.results?.length) throw new Error("Checklist not found");
    return res.results[0];
  },
  update: (id: string, data: Partial<SurgicalSafetyChecklist>) =>
    ehrClient.patch<SurgicalSafetyChecklist>(`/surgical-safety-checklists/${id}/`, data),
  completeSignIn: (id: string) =>
    ehrClient.post<SurgicalSafetyChecklist>(`/surgical-safety-checklists/${id}/complete-sign-in/`, {}),
  completeTimeOut: (id: string) =>
    ehrClient.post<SurgicalSafetyChecklist>(`/surgical-safety-checklists/${id}/complete-time-out/`, {}),
  completeSignOut: (id: string) =>
    ehrClient.post<SurgicalSafetyChecklist>(`/surgical-safety-checklists/${id}/complete-sign-out/`, {}),
  syncStatus: (id: string) =>
    ehrClient.post<SurgicalSafetyChecklist>(`/surgical-safety-checklists/${id}/sync-status/`, {}),
};

export const anesthesiaRecordsApi = {
  get: async (scheduleId: string): Promise<AnesthesiaRecord> => {
    const res = await ehrClient.get<PaginatedResponse<AnesthesiaRecord>>(
      `/anesthesia-records/?surgery_schedule=${scheduleId}&limit=1`,
    );
    if (!res.results?.length) throw new Error("Anesthesia record not found");
    return res.results[0];
  },
  create: (data: Partial<AnesthesiaRecord>) =>
    ehrClient.post<AnesthesiaRecord>("/anesthesia-records/", data),
  update: (id: string, data: Partial<AnesthesiaRecord>) =>
    ehrClient.patch<AnesthesiaRecord>(`/anesthesia-records/${id}/`, data),
};

export const intraOpVitalsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<IntraOperativeVital>>(`/intra-op-vitals/${otQs(params)}`),
  create: (data: Partial<IntraOperativeVital>) =>
    ehrClient.post<IntraOperativeVital>("/intra-op-vitals/", data),
};

export const intraOpDrugsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<IntraOperativeDrug>>(`/intra-op-drugs/${otQs(params)}`),
  create: (data: Partial<IntraOperativeDrug>) =>
    ehrClient.post<IntraOperativeDrug>("/intra-op-drugs/", data),
  update: (id: string, data: Partial<IntraOperativeDrug>) =>
    ehrClient.patch<IntraOperativeDrug>(`/intra-op-drugs/${id}/`, data),
};

export const fluidRecordsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<FluidRecord>>(`/fluid-records/${otQs(params)}`),
  create: (data: Partial<FluidRecord>) =>
    ehrClient.post<FluidRecord>("/fluid-records/", data),
};

export const fluidOutputsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<FluidOutput>>(`/fluid-outputs/${otQs(params)}`),
  create: (data: Partial<FluidOutput>) =>
    ehrClient.post<FluidOutput>("/fluid-outputs/", data),
};

export const operativeNotesApi = {
  get: async (scheduleId: string): Promise<OperativeNote> => {
    const res = await ehrClient.get<PaginatedResponse<OperativeNote>>(
      `/operative-notes/?surgery_schedule=${scheduleId}&limit=1`,
    );
    if (!res.results?.length) throw new Error("Operative note not found");
    return res.results[0];
  },
  create: (data: Partial<OperativeNote>) =>
    ehrClient.post<OperativeNote>("/operative-notes/", data),
  update: (id: string, data: Partial<OperativeNote>) =>
    ehrClient.patch<OperativeNote>(`/operative-notes/${id}/`, data),
  verify: (id: string) =>
    ehrClient.post<OperativeNote>(`/operative-notes/${id}/verify/`, {}),
};

export const operativeNoteTemplatesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<OperativeNoteTemplate>>(`/operative-note-templates/${otQs(params)}`),
  get: (id: string) => ehrClient.get<OperativeNoteTemplate>(`/operative-note-templates/${id}/`),
  create: (data: Partial<OperativeNoteTemplate>) =>
    ehrClient.post<OperativeNoteTemplate>("/operative-note-templates/", data),
  update: (id: string, data: Partial<OperativeNoteTemplate>) =>
    ehrClient.patch<OperativeNoteTemplate>(`/operative-note-templates/${id}/`, data),
};

export const surgicalCountsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgicalCount>>(`/surgical-counts/${otQs(params)}`),
  create: (data: Partial<SurgicalCount>) =>
    ehrClient.post<SurgicalCount>("/surgical-counts/", data),
};

export const specimenRecordsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SpecimenRecord>>(`/specimen-records/${otQs(params)}`),
  create: (data: Partial<SpecimenRecord>) =>
    ehrClient.post<SpecimenRecord>("/specimen-records/", data),
};

export const implantRecordsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<ImplantRecord>>(`/implant-records/${otQs(params)}`),
  create: (data: Partial<ImplantRecord>) =>
    ehrClient.post<ImplantRecord & { warnings?: string[] }>("/implant-records/", data),
  remove: (id: string) => ehrClient.delete<void>(`/implant-records/${id}/`),
};

export const drainRecordsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<DrainRecord>>(`/drain-records/${otQs(params)}`),
  create: (data: Partial<DrainRecord>) =>
    ehrClient.post<DrainRecord>("/drain-records/", data),
  update: (id: string, data: Partial<DrainRecord>) =>
    ehrClient.patch<DrainRecord>(`/drain-records/${id}/`, data),
};

export const surgicalComplicationsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgicalComplication>>(`/surgical-complications/${otQs(params)}`),
  create: (data: Partial<SurgicalComplication>) =>
    ehrClient.post<SurgicalComplication>("/surgical-complications/", data),
};

export const otConsumablesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<OTConsumableUsage>>(`/ot-consumables/${otQs(params)}`),
  create: (data: Partial<OTConsumableUsage> & { store_inventory_id?: string | null }) =>
    ehrClient.post<OTConsumableUsage & { warnings?: string[] }>("/ot-consumables/", data),
  remove: (id: string) => ehrClient.delete<void>(`/ot-consumables/${id}/`),
};

// ─── OT Master Search — used by the ServiceItemPicker ─────────────────────
export type OTMasterServiceResult = {
  type: "service";
  id: string;
  name: string;
  code: string;
  service_category: string;
  service_cost: string;
  inventory_item_id: string | null;
  inventory_item_name: string | null;
  batches?: {
    store_inventory_id: string;
    item_id: string;
    batch_no: string;
    expiry_date: string | null;
    quantity_on_hand: number;
    unit_cost: string;
  }[];
  store_warning?: string;
};

export type OTMasterItemResult = {
  type: "item";
  id: string;
  name: string;
  code: string;
  category_name: string | null;
  consumable: string;
};

export const otMasterSearchApi = {
  search: (params: {
    type: "implant" | "consumable" | "instrument";
    search?: string;
    surgery_schedule?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set("type", params.type);
    if (params.search) qs.set("search", params.search);
    if (params.surgery_schedule) qs.set("surgery_schedule", params.surgery_schedule);
    if (params.limit) qs.set("limit", String(params.limit));
    return ehrClient.get<{ results: (OTMasterServiceResult | OTMasterItemResult)[] }>(
      `/ot-master-search/?${qs.toString()}`
    );
  },
};

export const pacuRecordsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<PACURecordListItem>>(`/pacu-records/${otQs(params)}`),
  get: (id: string) => ehrClient.get<PACURecord>(`/pacu-records/${id}/`),
  create: (data: Partial<PACURecord>) =>
    ehrClient.post<PACURecord>("/pacu-records/", data),
  update: (id: string, data: Partial<PACURecord>) =>
    ehrClient.patch<PACURecord>(`/pacu-records/${id}/`, data),
  discharge: (id: string, data: { discharge_destination: string; discharge_notes?: string }) =>
    ehrClient.post<PACURecord>(`/pacu-records/${id}/discharge/`, data),
};

export const pacuVitalsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<PACUVital>>(`/pacu-vitals/${otQs(params)}`),
  create: (data: Partial<PACUVital>) =>
    ehrClient.post<PACUVital>("/pacu-vitals/", data),
};

export const surgeonPreferenceCardsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SurgeonPreferenceCardListItem>>(`/surgeon-preference-cards/${otQs(params)}`),
  get: (id: string) => ehrClient.get<SurgeonPreferenceCard>(`/surgeon-preference-cards/${id}/`),
  create: (data: Partial<SurgeonPreferenceCard>) =>
    ehrClient.post<SurgeonPreferenceCard>("/surgeon-preference-cards/", data),
  update: (id: string, data: Partial<SurgeonPreferenceCard>) =>
    ehrClient.patch<SurgeonPreferenceCard>(`/surgeon-preference-cards/${id}/`, data),
};

export const instrumentSetsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<InstrumentSet>>(`/instrument-sets/${otQs(params)}`),
  get: (id: string) => ehrClient.get<InstrumentSet>(`/instrument-sets/${id}/`),
  create: (data: Partial<InstrumentSet>) =>
    ehrClient.post<InstrumentSet>("/instrument-sets/", data),
  update: (id: string, data: Partial<InstrumentSet>) =>
    ehrClient.patch<InstrumentSet>(`/instrument-sets/${id}/`, data),
};

export const sterilizationCyclesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SterilizationCycle>>(`/sterilization-cycles/${otQs(params)}`),
  create: (data: Partial<SterilizationCycle>) =>
    ehrClient.post<SterilizationCycle>("/sterilization-cycles/", data),
  qcRelease: (id: string) =>
    ehrClient.post<SterilizationCycle>(`/sterilization-cycles/${id}/qc-release/`, {}),
};

export const otDashboardApi = {
  stats: () => ehrClient.get<OTDashboardStats>("/ot-dashboard/stats/"),
  director: (params?: Record<string, string>) =>
    ehrClient.get<OTDirectorDashboard>(`/ot-dashboard/director/${otQs(params)}`),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  instrumentUsage: (params?: Record<string, string>) =>
    ehrClient.get<any>(`/ot-dashboard/instrument-usage/${otQs(params)}`),
};

export const otReportsApi = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  utilization: (params?: Record<string, string>) =>
    ehrClient.get<any>(`/ot-reports/utilization/${otQs(params)}`),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  surgeonPerformance: (params?: Record<string, string>) =>
    ehrClient.get<any>(`/ot-reports/surgeon-performance/${otQs(params)}`),
};

// ============================================================================
// OPD Refund Workflow
// ============================================================================

import type {
  RefundReason,
  RefundRequest,
  RefundRequestListItem,
  CreateRefundPayload,
  ApproveRefundPayload,
  RejectRefundPayload,
  AcknowledgeRefundPayload,
  BillRefundLookupResponse,
  BillSearchResult,
  RefundApproverOption,
} from "@/types/ehr";

import type {
  BillDiscount,
  BillDiscountListItem,
  DiscountCap,
  DiscountPolicy,
  DiscountPreview,
  DiscountReason,
} from "@/types/ehr";

/**
 * Manual discounts (2026-09-09).
 *
 * ⚠️ `create` does not choose between applying and queueing — the SERVER does,
 * from the caller's cap. Read `response.discount.status` ("applied" vs
 * "pending") to know what happened, and `preview().needs_approval` to label the
 * button before it is pressed.
 */
export const discountsApi = {
  myCap: () => ehrClient.get<DiscountCap>("/discount-policies/my-cap/"),

  policies: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<DiscountPolicy>>(`/discount-policies/${qs}`);
    },
    create: (data: Partial<DiscountPolicy>) =>
      ehrClient.post<DiscountPolicy>("/discount-policies/", data),
    update: (id: string, data: Partial<DiscountPolicy>) =>
      ehrClient.patch<DiscountPolicy>(`/discount-policies/${id}/`, data),
    remove: (id: string) => ehrClient.delete<void>(`/discount-policies/${id}/`),
  },

  reasons: {
    list: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return ehrClient.get<PaginatedResponse<DiscountReason>>(`/discount-reasons/${qs}`);
    },
    create: (data: Partial<DiscountReason>) =>
      ehrClient.post<DiscountReason>("/discount-reasons/", data),
    update: (id: string, data: Partial<DiscountReason>) =>
      ehrClient.patch<DiscountReason>(`/discount-reasons/${id}/`, data),
    remove: (id: string) => ehrClient.delete<void>(`/discount-reasons/${id}/`),
  },

  preview: (data: { bill_id: string; pct: string; bill_item_ids?: string[] }) =>
    ehrClient.post<DiscountPreview>("/bill-discounts/preview/", data),

  create: (data: {
    bill_id: string;
    pct: string;
    reason_id: string;
    /** Empty or omitted = the whole bill. */
    bill_item_ids?: string[];
    remark?: string;
  }) => ehrClient.post<{ discount: BillDiscount; bill: Bill }>("/bill-discounts/", data),

  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<BillDiscountListItem>>(`/bill-discounts/${qs}`);
  },
  get: (id: string) => ehrClient.get<BillDiscount>(`/bill-discounts/${id}/`),
  approve: (id: string, data: { approver_remark?: string }) =>
    ehrClient.post<BillDiscount>(`/bill-discounts/${id}/approve/`, data),
  reject: (id: string, data: { rejection_reason: string }) =>
    ehrClient.post<BillDiscount>(`/bill-discounts/${id}/reject/`, data),
  cancel: (id: string) => ehrClient.post<BillDiscount>(`/bill-discounts/${id}/cancel/`, {}),
  revert: (id: string) => ehrClient.post<BillDiscount>(`/bill-discounts/${id}/revert/`, {}),
};

export const refundsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<RefundRequestListItem>>(
      `/refunds/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<RefundRequest>(`/refunds/${id}/`),
  create: (data: CreateRefundPayload) =>
    ehrClient.post<RefundRequest>("/refunds/", data),
  submit: (id: string) =>
    ehrClient.post<RefundRequest>(`/refunds/${id}/submit/`, {}),
  approve: (id: string, data: ApproveRefundPayload = {}) =>
    ehrClient.post<RefundRequest>(`/refunds/${id}/approve/`, data),
  reject: (id: string, data: RejectRefundPayload) =>
    ehrClient.post<RefundRequest>(`/refunds/${id}/reject/`, data),
  acknowledge: (id: string, data: AcknowledgeRefundPayload) =>
    ehrClient.post<RefundRequest>(`/refunds/${id}/acknowledge/`, data),
  cancel: (id: string) =>
    ehrClient.post<RefundRequest>(`/refunds/${id}/cancel/`, {}),
  billLookup: (params: { bill_number?: string; bill_id?: string }) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
      ) as Record<string, string>
    ).toString();
    return ehrClient.get<BillRefundLookupResponse>(
      `/refunds/bill-lookup/?${qs}`
    );
  },
  billSearch: (params: Record<string, string>) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v)) as Record<
        string,
        string
      >
    ).toString();
    return ehrClient.get<{ results: BillSearchResult[] }>(
      `/refunds/bill-search/?${qs}`
    );
  },
  approvers: () =>
    ehrClient.get<{ results: RefundApproverOption[] }>(`/refunds/approvers/`),
};

/**
 * Reason master behind the OP prescription-bounce capture. RBAC
 * `pharmacy.settings` — edited on the Pharmacy Settings screen, never read by
 * the sale screen (see `pharmacySalesApi.bounceConfig`).
 *
 * No `delete`: a dispensed sale line points at the reason it was bounced
 * under, so retirement is `is_active: false`.
 */
export const pharmacyBounceReasonsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<PharmacyBounceReason>>(
      `/pharmacy-bounce-reasons/${qs}`
    );
  },
  create: (data: PharmacyBounceReasonPayload) =>
    ehrClient.post<PharmacyBounceReason>("/pharmacy-bounce-reasons/", data),
  update: (id: string, data: Partial<PharmacyBounceReasonPayload>) =>
    ehrClient.patch<PharmacyBounceReason>(`/pharmacy-bounce-reasons/${id}/`, data),
};

export const refundReasonsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return ehrClient.get<PaginatedResponse<RefundReason>>(
      `/refund-reasons/${qs}`
    );
  },
  get: (id: string) => ehrClient.get<RefundReason>(`/refund-reasons/${id}/`),
  create: (data: Partial<RefundReason>) =>
    ehrClient.post<RefundReason>("/refund-reasons/", data),
  update: (id: string, data: Partial<RefundReason>) =>
    ehrClient.patch<RefundReason>(`/refund-reasons/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/refund-reasons/${id}/`),
};

// ============================================================================
// Credit Bill Register — TPA / Corporate / Insurer receivables
// ============================================================================

import type {
  CreditBillRegisterRow,
  CreditPaymentAdvice,
  CreditPaymentAdviceCreatePayload,
  CreditSettlement,
  CreditWriteOff,
  CreditWriteOffCreatePayload,
  CreditReversal,
  CreditReversalCreatePayload,
  CreditFollowUp,
  CreditFollowUpCreatePayload,
  CreditFollowUpAssignment,
  CreditWriteOffReason,
  CreditRegisterSettings,
  CreditLocationOverride,
  CreditBillLedger,
  CreditSOAPayload,
  CreditReceivablesDashboard,
  CreditPaymentAdviceDraft,
  PatientBillRegisterRow,
  PatientBillLedger,
  PaymentReceipt,
  PaymentReceiptCreatePayload,
  PatientReceiptDraft,
  PatientBillWriteOff,
  PatientBillWriteOffCreatePayload,
  PatientReversal,
  PatientReversalCreatePayload,
  PatientFollowUp,
  PatientFollowUpCreatePayload,
  PatientFollowUpAssignment,
  PatientWriteOffReason,
  PatientRegisterSettings,
  PatientLocationOverride,
  PatientSOAPayload,
  PatientReceivablesDashboard,
} from "@/types/ehr";

const crQs = (params?: Record<string, string | string[] | undefined>) => {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === null) continue;
    if (Array.isArray(v)) {
      v.forEach((vv) => sp.append(k, vv));
    } else {
      sp.append(k, v);
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

export const creditRegisterApi = {
  // Story 1 — outstanding register
  bills: {
    list: (params?: Record<string, string | string[]>) =>
      ehrClient.get<PaginatedResponse<CreditBillRegisterRow>>(
        `/credit-register/bills/${crQs(params)}`
      ),
    get: (id: string) =>
      ehrClient.get<CreditBillRegisterRow>(`/credit-register/bills/${id}/`),
    ledger: (id: string) =>
      ehrClient.get<CreditBillLedger>(`/credit-register/bills/${id}/ledger/`),
  },

  // Stories 2 & 3 — Settle Payment + Bulk Settle
  paymentAdvices: {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<CreditPaymentAdvice>>(
        `/credit-register/payment-advices/${crQs(params)}`
      ),
    get: (id: string) =>
      ehrClient.get<CreditPaymentAdvice>(`/credit-register/payment-advices/${id}/`),
    create: (data: CreditPaymentAdviceCreatePayload) =>
      ehrClient.post<CreditPaymentAdvice>(
        `/credit-register/payment-advices/create-advice/`,
        data
      ),
  },
  paymentAdviceDrafts: {
    list: () =>
      ehrClient.get<PaginatedResponse<CreditPaymentAdviceDraft>>(
        `/credit-register/payment-advice-drafts/`
      ),
    save: (payload: Record<string, unknown>) =>
      ehrClient.post<CreditPaymentAdviceDraft>(
        `/credit-register/payment-advice-drafts/`,
        { payload }
      ),
    delete: (id: string) =>
      ehrClient.delete<void>(`/credit-register/payment-advice-drafts/${id}/`),
  },

  // Story 4 — Write-off (maker-checker)
  writeOffs: {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<CreditWriteOff>>(
        `/credit-register/writeoffs/${crQs(params)}`
      ),
    get: (id: string) =>
      ehrClient.get<CreditWriteOff>(`/credit-register/writeoffs/${id}/`),
    raise: (data: CreditWriteOffCreatePayload) =>
      ehrClient.post<CreditWriteOff>(`/credit-register/writeoffs/raise/`, data),
    approve: (id: string, approver_remark = "") =>
      ehrClient.post<CreditWriteOff>(
        `/credit-register/writeoffs/${id}/approve/`,
        { approver_remark }
      ),
    reject: (id: string, rejection_reason: string) =>
      ehrClient.post<CreditWriteOff>(
        `/credit-register/writeoffs/${id}/reject/`,
        { rejection_reason }
      ),
  },

  // Story 5 — Reversal
  reversals: {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<CreditReversal>>(
        `/credit-register/reversals/${crQs(params)}`
      ),
    raise: (data: CreditReversalCreatePayload) =>
      ehrClient.post<CreditReversal>(`/credit-register/reversals/raise/`, data),
    approve: (id: string) =>
      ehrClient.post<CreditReversal>(
        `/credit-register/reversals/${id}/approve/`,
        {}
      ),
    reject: (id: string, rejection_reason: string) =>
      ehrClient.post<CreditReversal>(
        `/credit-register/reversals/${id}/reject/`,
        { rejection_reason }
      ),
  },

  // Story 6 — Follow-up
  followUps: {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<CreditFollowUp>>(
        `/credit-register/followups/${crQs(params)}`
      ),
    log: (data: CreditFollowUpCreatePayload) =>
      ehrClient.post<CreditFollowUp>(`/credit-register/followups/log/`, data),
    dueToday: () =>
      ehrClient.get<{ results: CreditFollowUp[] }>(
        `/credit-register/followups/due-today/`
      ),
    assignments: {
      list: () =>
        ehrClient.get<PaginatedResponse<CreditFollowUpAssignment>>(
          `/credit-register/followup-assignments/`
        ),
      create: (data: { payor?: string; bill?: string; assigned_to: string }) =>
        ehrClient.post<CreditFollowUpAssignment>(
          `/credit-register/followup-assignments/`,
          data
        ),
      delete: (id: string) =>
        ehrClient.delete<void>(`/credit-register/followup-assignments/${id}/`),
    },
  },

  // Story 7 — Statement of Account
  soa: {
    generate: (params: { payor: string; date_from: string; date_to: string; location?: string[] }) =>
      ehrClient.get<CreditSOAPayload>(`/credit-register/soa/${crQs(params)}`),
  },

  // Story 8 — Receivables dashboard
  dashboard: {
    summary: (params: { period: string; date_from?: string; date_to?: string; location?: string[] }) =>
      ehrClient.get<CreditReceivablesDashboard>(
        `/credit-register/dashboard/${crQs(params)}`
      ),
  },

  // Story 9 — Masters
  masters: {
    reasons: {
      list: () =>
        ehrClient.get<PaginatedResponse<CreditWriteOffReason>>(
          `/credit-register/writeoff-reasons/`
        ),
      create: (data: Partial<CreditWriteOffReason>) =>
        ehrClient.post<CreditWriteOffReason>(
          `/credit-register/writeoff-reasons/`,
          data
        ),
      update: (id: string, data: Partial<CreditWriteOffReason>) =>
        ehrClient.patch<CreditWriteOffReason>(
          `/credit-register/writeoff-reasons/${id}/`,
          data
        ),
      delete: (id: string) =>
        ehrClient.delete<void>(`/credit-register/writeoff-reasons/${id}/`),
    },
    settings: {
      get: () =>
        ehrClient.get<CreditRegisterSettings>(
          `/credit-register/settings/me/`
        ),
      update: (data: Partial<CreditRegisterSettings>) =>
        ehrClient.patch<CreditRegisterSettings>(
          `/credit-register/settings/me/`,
          data
        ),
    },
    locationOverrides: {
      list: () =>
        ehrClient.get<PaginatedResponse<CreditLocationOverride>>(
          `/credit-register/location-overrides/`
        ),
      create: (data: Partial<CreditLocationOverride>) =>
        ehrClient.post<CreditLocationOverride>(
          `/credit-register/location-overrides/`,
          data
        ),
      update: (id: string, data: Partial<CreditLocationOverride>) =>
        ehrClient.patch<CreditLocationOverride>(
          `/credit-register/location-overrides/${id}/`,
          data
        ),
      delete: (id: string) =>
        ehrClient.delete<void>(`/credit-register/location-overrides/${id}/`),
    },
  },
};

// ============================================================================
// Patient Outstanding Register (mirror of credit_register, patient-side)
// ============================================================================

export const patientRegisterApi = {
  // Story 1 — outstanding register
  bills: {
    list: (params?: Record<string, string | string[]>) =>
      ehrClient.get<PaginatedResponse<PatientBillRegisterRow>>(
        `/patient-register/bills/${crQs(params)}`
      ),
    get: (id: string) =>
      ehrClient.get<PatientBillRegisterRow>(`/patient-register/bills/${id}/`),
    ledger: (id: string) =>
      ehrClient.get<PatientBillLedger>(`/patient-register/bills/${id}/ledger/`),
  },

  // Stories 2 & 3 — Settle (single + bulk)
  receipts: {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<PaymentReceipt>>(
        `/patient-register/receipts/${crQs(params)}`
      ),
    get: (id: string) =>
      ehrClient.get<PaymentReceipt>(`/patient-register/receipts/${id}/`),
    create: (data: PaymentReceiptCreatePayload) =>
      ehrClient.post<PaymentReceipt>(
        `/patient-register/receipts/create-receipt/`,
        data
      ),
  },
  receiptDrafts: {
    list: () =>
      ehrClient.get<PaginatedResponse<PatientReceiptDraft>>(
        `/patient-register/receipt-drafts/`
      ),
    save: (payload: Record<string, unknown>) =>
      ehrClient.post<PatientReceiptDraft>(
        `/patient-register/receipt-drafts/`,
        { payload }
      ),
    delete: (id: string) =>
      ehrClient.delete<void>(`/patient-register/receipt-drafts/${id}/`),
  },

  // Story 4 — Write-off (Phase 3)
  writeOffs: {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<PatientBillWriteOff>>(
        `/patient-register/writeoffs/${crQs(params)}`
      ),
    get: (id: string) =>
      ehrClient.get<PatientBillWriteOff>(`/patient-register/writeoffs/${id}/`),
    raise: (data: PatientBillWriteOffCreatePayload) =>
      ehrClient.post<PatientBillWriteOff>(`/patient-register/writeoffs/raise/`, data),
    approve: (id: string, approver_remark = "") =>
      ehrClient.post<PatientBillWriteOff>(
        `/patient-register/writeoffs/${id}/approve/`,
        { approver_remark }
      ),
    reject: (id: string, rejection_reason: string) =>
      ehrClient.post<PatientBillWriteOff>(
        `/patient-register/writeoffs/${id}/reject/`,
        { rejection_reason }
      ),
  },

  // Story 5 — Reversal (Phase 3)
  reversals: {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<PatientReversal>>(
        `/patient-register/reversals/${crQs(params)}`
      ),
    raise: (data: PatientReversalCreatePayload) =>
      ehrClient.post<PatientReversal>(`/patient-register/reversals/raise/`, data),
    approve: (id: string) =>
      ehrClient.post<PatientReversal>(
        `/patient-register/reversals/${id}/approve/`,
        {}
      ),
    reject: (id: string, rejection_reason: string) =>
      ehrClient.post<PatientReversal>(
        `/patient-register/reversals/${id}/reject/`,
        { rejection_reason }
      ),
  },

  // Story 6 — Follow-up (Phase 2)
  followUps: {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<PatientFollowUp>>(
        `/patient-register/followups/${crQs(params)}`
      ),
    log: (data: PatientFollowUpCreatePayload) =>
      ehrClient.post<PatientFollowUp>(`/patient-register/followups/log/`, data),
    dueToday: () =>
      ehrClient.get<{ results: PatientFollowUp[] }>(
        `/patient-register/followups/due-today/`
      ),
    assignments: {
      list: () =>
        ehrClient.get<PaginatedResponse<PatientFollowUpAssignment>>(
          `/patient-register/followup-assignments/`
        ),
      create: (data: { patient?: string; bill?: string; assigned_to: string }) =>
        ehrClient.post<PatientFollowUpAssignment>(
          `/patient-register/followup-assignments/`,
          data
        ),
      delete: (id: string) =>
        ehrClient.delete<void>(`/patient-register/followup-assignments/${id}/`),
    },
  },

  // Story 7 — Statement of Account (Phase 2)
  soa: {
    generate: (params: { patient: string; date_from: string; date_to: string; location?: string[] }) =>
      ehrClient.get<PatientSOAPayload>(`/patient-register/soa/${crQs(params)}`),
  },

  // Story 8 — Receivables dashboard (Phase 2)
  dashboard: {
    summary: (params: { period: string; date_from?: string; date_to?: string; location?: string[] }) =>
      ehrClient.get<PatientReceivablesDashboard>(
        `/patient-register/dashboard/${crQs(params)}`
      ),
  },

  // Story 9 — Masters (Phase 3 surfaces; backend ready)
  masters: {
    reasons: {
      list: () =>
        ehrClient.get<PaginatedResponse<PatientWriteOffReason>>(
          `/patient-register/writeoff-reasons/`
        ),
      create: (data: Partial<PatientWriteOffReason>) =>
        ehrClient.post<PatientWriteOffReason>(
          `/patient-register/writeoff-reasons/`,
          data
        ),
      update: (id: string, data: Partial<PatientWriteOffReason>) =>
        ehrClient.patch<PatientWriteOffReason>(
          `/patient-register/writeoff-reasons/${id}/`,
          data
        ),
      delete: (id: string) =>
        ehrClient.delete<void>(`/patient-register/writeoff-reasons/${id}/`),
    },
    settings: {
      get: () =>
        ehrClient.get<PatientRegisterSettings>(`/patient-register/settings/me/`),
      update: (data: Partial<PatientRegisterSettings>) =>
        ehrClient.patch<PatientRegisterSettings>(
          `/patient-register/settings/me/`,
          data
        ),
    },
    locationOverrides: {
      list: () =>
        ehrClient.get<PaginatedResponse<PatientLocationOverride>>(
          `/patient-register/location-overrides/`
        ),
      create: (data: Partial<PatientLocationOverride>) =>
        ehrClient.post<PatientLocationOverride>(
          `/patient-register/location-overrides/`,
          data
        ),
      update: (id: string, data: Partial<PatientLocationOverride>) =>
        ehrClient.patch<PatientLocationOverride>(
          `/patient-register/location-overrides/${id}/`,
          data
        ),
      delete: (id: string) =>
        ehrClient.delete<void>(`/patient-register/location-overrides/${id}/`),
    },
  },
};

// ============================================================================
// Accounts & Bookkeeping
// ============================================================================

import type {
  AccountGroup,
  AccountGroupTreeNode,
  Ledger,
  LedgerListItem,
  LedgerStatement,
  LedgerOpeningBalance,
  FiscalYear,
  AccountingPeriod,
  CostCentre,
  VoucherSeries,
  VoucherListItem,
  VoucherDetail,
  CreateVoucherPayload,
  TrialBalanceResponse,
  DayBookResponse,
  AccountsDashboard,
  AccountsSettings,
} from "@/types/ehr";

const qs = (params?: Record<string, string | number | boolean | undefined>) => {
  if (!params) return "";
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== "" && v !== null
  );
  if (entries.length === 0) return "";
  return (
    "?" + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()
  );
};

export const accountGroupsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<AccountGroup>>(`/accounts/groups/${qs(params)}`),
  tree: () => ehrClient.get<AccountGroupTreeNode[]>(`/accounts/groups/tree/`),
  get: (id: string) => ehrClient.get<AccountGroup>(`/accounts/groups/${id}/`),
  create: (data: Partial<AccountGroup>) =>
    ehrClient.post<AccountGroup>("/accounts/groups/", data),
  update: (id: string, data: Partial<AccountGroup>) =>
    ehrClient.patch<AccountGroup>(`/accounts/groups/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/accounts/groups/${id}/`),
};

export const accountLedgersApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<LedgerListItem>>(`/accounts/ledgers/${qs(params)}`),
  get: (id: string) => ehrClient.get<Ledger>(`/accounts/ledgers/${id}/`),
  create: (data: Partial<Ledger>) =>
    ehrClient.post<Ledger>("/accounts/ledgers/", data),
  update: (id: string, data: Partial<Ledger>) =>
    ehrClient.patch<Ledger>(`/accounts/ledgers/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/accounts/ledgers/${id}/`),
  statement: (id: string, params?: { date_from?: string; date_to?: string }) =>
    ehrClient.get<LedgerStatement>(`/accounts/ledgers/${id}/statement/${qs(params)}`),
  setOpeningBalance: (
    id: string,
    data: { fiscal_year: string; opening_dr?: string; opening_cr?: string }
  ) =>
    ehrClient.post<LedgerOpeningBalance>(
      `/accounts/ledgers/${id}/opening-balance/`,
      data
    ),
};

export const fiscalYearsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<FiscalYear>>(`/accounts/fiscal-years/${qs(params)}`),
  current: () => ehrClient.get<FiscalYear>("/accounts/fiscal-years/current/"),
};

export const accountingPeriodsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<AccountingPeriod>>(
      `/accounts/periods/${qs(params)}`
    ),
};

export const costCentresApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<CostCentre>>(`/accounts/cost-centres/${qs(params)}`),
  get: (id: string) => ehrClient.get<CostCentre>(`/accounts/cost-centres/${id}/`),
  create: (data: Partial<CostCentre>) =>
    ehrClient.post<CostCentre>("/accounts/cost-centres/", data),
  update: (id: string, data: Partial<CostCentre>) =>
    ehrClient.patch<CostCentre>(`/accounts/cost-centres/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/accounts/cost-centres/${id}/`),
};

export const voucherSeriesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<VoucherSeries>>(
      `/accounts/voucher-series/${qs(params)}`
    ),
  create: (data: Partial<VoucherSeries>) =>
    ehrClient.post<VoucherSeries>("/accounts/voucher-series/", data),
  update: (id: string, data: Partial<VoucherSeries>) =>
    ehrClient.patch<VoucherSeries>(`/accounts/voucher-series/${id}/`, data),
};

export const vouchersApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<VoucherListItem>>(`/accounts/vouchers/${qs(params)}`),
  get: (id: string) => ehrClient.get<VoucherDetail>(`/accounts/vouchers/${id}/`),
  create: (data: CreateVoucherPayload) =>
    ehrClient.post<VoucherDetail>("/accounts/vouchers/", data),
  void: (id: string, reason: string) =>
    ehrClient.post<VoucherDetail>(`/accounts/vouchers/${id}/void/`, { reason }),
};

export const accountsReportsApi = {
  dayBook: (params: { date_from: string; date_to: string; voucher_type?: string }) =>
    ehrClient.get<DayBookResponse>(`/accounts/reports/day-book/${qs(params)}`),
  trialBalance: (params: { as_of_date: string }) =>
    ehrClient.get<TrialBalanceResponse>(
      `/accounts/reports/trial-balance/${qs(params)}`
    ),
  cashBook: (params: { date_from: string; date_to: string }) =>
    ehrClient.get<LedgerStatement>(`/accounts/reports/cash-book/${qs(params)}`),
  bankBook: (params: {
    date_from: string;
    date_to: string;
    bank_ledger_id?: string;
  }) => ehrClient.get<LedgerStatement>(`/accounts/reports/bank-book/${qs(params)}`),
  dashboard: () => ehrClient.get<AccountsDashboard>(`/accounts/reports/dashboard/`),
};

export const accountsSettingsApi = {
  current: () =>
    ehrClient.get<AccountsSettings>("/accounts/settings/current/"),
  update: (data: Partial<AccountsSettings>) =>
    ehrClient.patch<AccountsSettings>("/accounts/settings/current/", data),
};

// ============================================================================
// Phase 3+4 — Supplier Invoices + Payments + Reports
// ============================================================================

import type {
  SupplierInvoice,
  SupplierInvoiceListItem,
  SupplierPayment,
  PnLResponse,
  BalanceSheetResponse,
  CashFlowResponse,
  AgeingResponse,
} from "@/types/ehr";

export const supplierInvoicesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SupplierInvoiceListItem>>(
      `/accounts/supplier-invoices/${qs(params)}`,
    ),
  get: (id: string) => ehrClient.get<SupplierInvoice>(`/accounts/supplier-invoices/${id}/`),
  create: (data: Partial<SupplierInvoice>) =>
    ehrClient.post<SupplierInvoice>("/accounts/supplier-invoices/", data),
  update: (id: string, data: Partial<SupplierInvoice>) =>
    ehrClient.patch<SupplierInvoice>(`/accounts/supplier-invoices/${id}/`, data),
  book: (id: string) =>
    ehrClient.post<SupplierInvoice>(`/accounts/supplier-invoices/${id}/book/`, {}),
  cancel: (id: string, reason: string) =>
    ehrClient.post<SupplierInvoice>(`/accounts/supplier-invoices/${id}/cancel/`, { reason }),
  outstanding: (supplier_id: string) =>
    ehrClient.get<SupplierInvoiceListItem[]>(
      `/accounts/supplier-invoices/outstanding/${qs({ supplier: supplier_id })}`,
    ),
};

export const supplierPaymentsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<SupplierPayment>>(
      `/accounts/supplier-payments/${qs(params)}`,
    ),
  get: (id: string) => ehrClient.get<SupplierPayment>(`/accounts/supplier-payments/${id}/`),
  create: (data: Partial<SupplierPayment>) =>
    ehrClient.post<SupplierPayment>("/accounts/supplier-payments/", data),
  post: (id: string) =>
    ehrClient.post<SupplierPayment>(`/accounts/supplier-payments/${id}/post/`, {}),
};

export const accountsReportsPhase4Api = {
  pnl: (params: { date_from?: string; date_to?: string; cost_centre?: string }) =>
    ehrClient.get<PnLResponse>(`/accounts/reports/pnl/${qs(params)}`),
  balanceSheet: (params: { as_of_date?: string }) =>
    ehrClient.get<BalanceSheetResponse>(`/accounts/reports/balance-sheet/${qs(params)}`),
  cashFlow: (params: { date_from?: string; date_to?: string }) =>
    ehrClient.get<CashFlowResponse>(`/accounts/reports/cash-flow/${qs(params)}`),
  creditorsAgeing: (params: { as_of_date?: string }) =>
    ehrClient.get<AgeingResponse>(`/accounts/reports/creditors-ageing/${qs(params)}`),
  debtorsAgeing: (params: { as_of_date?: string }) =>
    ehrClient.get<AgeingResponse>(`/accounts/reports/debtors-ageing/${qs(params)}`),
};

// ============================================================================
// Phase 5 — GST Compliance
// ============================================================================

import type {
  StateRow,
  GSTR1Response,
  GSTR3BResponse,
  EInvoiceRecord,
  EInvoiceEligibleRow,
} from "@/types/ehr";

export const complianceApi = {
  states: () => ehrClient.get<StateRow[]>("/accounts/compliance/states/"),

  gstr1: (period: string) =>
    ehrClient.get<GSTR1Response>(`/accounts/compliance/gstr1/${qs({ period })}`),
  gstr1DownloadUrl: (period: string) =>
    `/accounts/compliance/gstr1/download/${qs({ period })}`,

  gstr3b: (period: string) =>
    ehrClient.get<GSTR3BResponse>(`/accounts/compliance/gstr3b/${qs({ period })}`),
  gstr3bDownloadUrl: (period: string) =>
    `/accounts/compliance/gstr3b/download/${qs({ period })}`,

  einvoicesList: (params?: Record<string, string>) =>
    ehrClient.get<{ count: number; results: EInvoiceRecord[] }>(
      `/accounts/compliance/einvoices/${qs(params)}`,
    ),
  einvoicesEligible: () =>
    ehrClient.get<EInvoiceEligibleRow[]>("/accounts/compliance/einvoices-eligible/"),
  einvoiceGenerate: (voucherId: string) =>
    ehrClient.post<EInvoiceRecord>(
      `/accounts/compliance/einvoices/${voucherId}/generate/`, {}),
  einvoiceCancel: (recordId: string, reason: string, remarks?: string) =>
    ehrClient.post<EInvoiceRecord>(
      `/accounts/compliance/einvoices/${recordId}/cancel/`, { reason, remarks: remarks || "" }),
};

// ============================================================================
// Phase 6 — TDS / Fixed Assets / Bank Reco / Tally / Period Close
// ============================================================================

import type {
  TDSSection,
  TDSDeduction,
  TDSChallan,
  TDS26QResponse,
  Form16AResponse,
  AssetClass,
  FixedAsset,
  DepreciationScheduleRow,
  DepreciationRunResponse,
  BankStatement,
  BankStatementLine,
  BankRecoSnapshot,
  YearClosePreview,
} from "@/types/ehr";

export const tdsSectionsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<TDSSection>>(`/accounts/tds/sections/${qs(params)}`),
  create: (data: Partial<TDSSection>) => ehrClient.post<TDSSection>("/accounts/tds/sections/", data),
  update: (id: string, data: Partial<TDSSection>) =>
    ehrClient.patch<TDSSection>(`/accounts/tds/sections/${id}/`, data),
};

export const tdsDeductionsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<TDSDeduction>>(`/accounts/tds/deductions/${qs(params)}`),
  get: (id: string) => ehrClient.get<TDSDeduction>(`/accounts/tds/deductions/${id}/`),
};

export const tdsChallansApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<TDSChallan>>(`/accounts/tds/challans/${qs(params)}`),
  create: (data: Partial<TDSChallan>) => ehrClient.post<TDSChallan>("/accounts/tds/challans/", data),
  allocate: (id: string, deductionIds: string[]) =>
    ehrClient.post<{ allocated_count: number }>(`/accounts/tds/challans/${id}/allocate/`, { deduction_ids: deductionIds }),
};

export const tdsReturnsApi = {
  gen26q: (params: { fy: string; quarter: string }) =>
    ehrClient.get<TDS26QResponse>(`/accounts/tds/returns/26q/${qs(params)}`),
  download26qUrl: (params: { fy: string; quarter: string }) =>
    `/accounts/tds/returns/26q/download/${qs(params)}`,
  form16a: (params: { fy: string; deductee: string; quarter?: string }) =>
    ehrClient.get<Form16AResponse>(`/accounts/tds/returns/form16a/${qs(params)}`),
};

export const assetClassesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<AssetClass>>(`/accounts/asset-classes/${qs(params)}`),
  create: (data: Partial<AssetClass>) =>
    ehrClient.post<AssetClass>("/accounts/asset-classes/", data),
  update: (id: string, data: Partial<AssetClass>) =>
    ehrClient.patch<AssetClass>(`/accounts/asset-classes/${id}/`, data),
};

export const fixedAssetsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<FixedAsset>>(`/accounts/assets/${qs(params)}`),
  get: (id: string) => ehrClient.get<FixedAsset>(`/accounts/assets/${id}/`),
  create: (data: Partial<FixedAsset>) => ehrClient.post<FixedAsset>("/accounts/assets/", data),
  update: (id: string, data: Partial<FixedAsset>) =>
    ehrClient.patch<FixedAsset>(`/accounts/assets/${id}/`, data),
  schedule: (id: string) =>
    ehrClient.get<DepreciationScheduleRow[]>(`/accounts/assets/${id}/schedule/`),
  dispose: (id: string, data: { disposal_date: string; disposal_type: string; proceeds: string; buyer_ledger_id?: string }) =>
    ehrClient.post<{ disposal_id: string; voucher_id: string | null }>(`/accounts/assets/${id}/dispose/`, data),
};

export const depreciationApi = {
  runPreview: (period: string) =>
    ehrClient.post<DepreciationRunResponse>("/accounts/depreciation/run-preview/", { period }),
  runCommit: (period: string) =>
    ehrClient.post<DepreciationRunResponse>("/accounts/depreciation/run-commit/", { period }),
};

export const bankStatementsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<BankStatement>>(`/accounts/bank-statements/${qs(params)}`),
  get: (id: string) => ehrClient.get<BankStatement>(`/accounts/bank-statements/${id}/`),
  create: (data: Partial<BankStatement>) =>
    ehrClient.post<BankStatement>("/accounts/bank-statements/", data),
  lines: (id: string, match_status?: string) =>
    ehrClient.get<BankStatementLine[]>(`/accounts/bank-statements/${id}/lines/${qs(match_status ? { match_status } : {})}`),
  uploadCsv: (id: string, file: File) => {
    const fd = new FormData(); fd.append("file", file);
    return ehrClient.postFormData<{ imported_count: number }>(`/accounts/bank-statements/${id}/upload-csv/`, fd);
  },
  candidates: (id: string, lineId: string) =>
    ehrClient.get<Array<{ voucher_entry_id: string; voucher_number: string; voucher_date: string; narration: string; dr_amount: string; cr_amount: string }>>(
      `/accounts/bank-statements/${id}/candidates/${qs({ line_id: lineId })}`,
    ),
  match: (id: string, lineId: string, voucherEntryId: string) =>
    ehrClient.post<BankStatementLine>(`/accounts/bank-statements/${id}/match/`, { line_id: lineId, voucher_entry_id: voucherEntryId }),
  unmatch: (id: string, lineId: string) =>
    ehrClient.post<BankStatementLine>(`/accounts/bank-statements/${id}/unmatch/`, { line_id: lineId }),
  ignore: (id: string, lineId: string) =>
    ehrClient.post<BankStatementLine>(`/accounts/bank-statements/${id}/ignore/`, { line_id: lineId }),
};

export const bankRecoApi = {
  compute: (params: { bank_ledger: string; as_of?: string }) =>
    ehrClient.get<BankRecoSnapshot>(`/accounts/bank-recos/compute/${qs(params)}`),
  finalise: (bank_ledger: string, as_of_date: string, notes?: string) =>
    ehrClient.post<{ reconciliation_id: string; difference: string; is_finalised: boolean }>(
      "/accounts/bank-recos/finalise/", { bank_ledger, as_of_date, notes: notes || "" },
    ),
};

export const tallyExportApi = {
  vouchersUrl: (from: string, to: string) =>
    `/accounts/tally-export/vouchers/${qs({ from, to })}`,
  mastersUrl: () => "/accounts/tally-export/masters/",
};

export const periodCloseApi = {
  closePeriod: (periodId: string, lock = false) =>
    ehrClient.post<{ status: string; closed_at: string }>(
      `/accounts/period-close/periods/${periodId}/close/`, { lock },
    ),
  reopenPeriod: (periodId: string) =>
    ehrClient.post<{ status: string }>(`/accounts/period-close/periods/${periodId}/reopen/`, {}),
  fyPreview: (fyId: string) =>
    ehrClient.get<YearClosePreview>(`/accounts/period-close/fy/${fyId}/preview/`),
  fyClose: (fyId: string) =>
    ehrClient.post<{ voucher_id: string; voucher_number: string; fy_status: string }>(
      `/accounts/period-close/fy/${fyId}/close/`, {},
    ),
};

// ============================================================================
// Insurance — TPA Master, Pre-Auth Lifecycle
// ============================================================================

import type {
  TPA,
  TPAListItem,
  CreateTPAPayload,
  UpdateTPAPayload,
  PayorTPAMapping,
  CreatePayorTPAMappingPayload,
  PatientInsurance,
  PreAuthDocument,
  PreAuthDocumentCategory,
  PreAuthRequest,
  PreAuthRequestListItem,
  PreAuthCreatePayload,
  PreAuthUpdatePayload,
  PreAuthAgingSummary,
  PaginatedResponse as InsurancePaginatedResponse,
} from "@/types/ehr";

const insuranceQs = (params?: Record<string, string | number | undefined>) => {
  if (!params) return "";
  const cleaned: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    cleaned[k] = String(v);
  }
  const qs = new URLSearchParams(cleaned).toString();
  return qs ? `?${qs}` : "";
};

export const tpaApi = {
  list: (params?: Record<string, string | number | undefined>) =>
    ehrClient.get<InsurancePaginatedResponse<TPAListItem>>(`/tpas/${insuranceQs(params)}`),
  get: (id: string) => ehrClient.get<TPA>(`/tpas/${id}/`),
  create: (data: CreateTPAPayload) => ehrClient.post<TPA>("/tpas/", data),
  update: (id: string, data: UpdateTPAPayload) =>
    ehrClient.patch<TPA>(`/tpas/${id}/`, data),
  deactivate: (id: string) => ehrClient.delete<void>(`/tpas/${id}/`),
  payors: (id: string) =>
    ehrClient.get<PayorTPAMapping[]>(`/tpas/${id}/payors/`),
};

export const payorTpaMappingsApi = {
  list: (params?: Record<string, string | number | undefined>) =>
    ehrClient.get<InsurancePaginatedResponse<PayorTPAMapping> | PayorTPAMapping[]>(
      `/payor-tpa-mappings/${insuranceQs(params)}`,
    ),
  create: (data: CreatePayorTPAMappingPayload) =>
    ehrClient.post<PayorTPAMapping>("/payor-tpa-mappings/", data),
  update: (id: string, data: { is_default?: boolean; is_active?: boolean }) =>
    ehrClient.patch<PayorTPAMapping>(`/payor-tpa-mappings/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/payor-tpa-mappings/${id}/`),
  setDefault: (id: string) =>
    ehrClient.post<PayorTPAMapping>(`/payor-tpa-mappings/${id}/set-default/`, {}),
};

export const patientInsuranceApi = {
  list: (params?: Record<string, string | number | undefined>) =>
    ehrClient.get<InsurancePaginatedResponse<PatientInsurance>>(
      `/patient-insurance/${insuranceQs(params)}`,
    ),
  get: (id: string) => ehrClient.get<PatientInsurance>(`/patient-insurance/${id}/`),
  byAdmissionRequest: (admissionRequestId: string) =>
    ehrClient.get<PatientInsurance>(
      `/patient-insurance/by-admission-request/${admissionRequestId}/`,
    ),
  create: (data: Partial<PatientInsurance>) =>
    ehrClient.post<PatientInsurance>("/patient-insurance/", data),
  update: (id: string, data: Partial<PatientInsurance>) =>
    ehrClient.patch<PatientInsurance>(`/patient-insurance/${id}/`, data),
};

export const preauthDocCategoriesApi = {
  list: () =>
    ehrClient.get<InsurancePaginatedResponse<PreAuthDocumentCategory> | PreAuthDocumentCategory[]>(
      "/preauth-doc-categories/",
    ),
};

export const preauthApi = {
  list: (params?: Record<string, string | number | undefined>) =>
    ehrClient.get<InsurancePaginatedResponse<PreAuthRequestListItem>>(
      `/preauth-requests/${insuranceQs(params)}`,
    ),
  get: (id: string) => ehrClient.get<PreAuthRequest>(`/preauth-requests/${id}/`),
  create: (data: PreAuthCreatePayload) =>
    ehrClient.post<PreAuthRequest>("/preauth-requests/", data),
  update: (id: string, data: PreAuthUpdatePayload) =>
    ehrClient.patch<PreAuthRequest>(`/preauth-requests/${id}/`, data),
  cancel: (id: string, reason = "") =>
    ehrClient.post<void>(`/preauth-requests/${id}/cancel/`, { reason }),
  sendToTpa: (
    id: string,
    payload: { notes?: string; tpa_claim_number?: string } | string = "",
  ) => {
    const body = typeof payload === "string" ? { notes: payload } : payload;
    return ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/send-to-tpa/`, body);
  },
  setClaimNumber: (id: string, tpaClaimNumber: string) =>
    ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/set-claim-number/`, {
      tpa_claim_number: tpaClaimNumber,
    }),
  raiseQuery: (id: string, notes = "") =>
    ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/raise-query/`, { notes }),
  respondQuery: (id: string, notes = "") =>
    ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/respond-query/`, { notes }),
  approve: (
    id: string,
    payload: {
      auth_number: string;
      approved_amount: string;
      approval_validity_till?: string | null;
      remarks?: string;
    },
  ) => ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/approve/`, payload),
  partialApprove: (
    id: string,
    payload: {
      auth_number: string;
      approved_amount: string;
      approval_validity_till?: string | null;
      co_pay_amount?: string | null;
      co_pay_pct?: string | null;
      deductible_amount?: string | null;
      non_payable_items?: Array<Record<string, unknown>>;
      remarks?: string;
    },
  ) => ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/partial-approve/`, payload),
  reject: (id: string, payload: { rejection_reason: string; remarks?: string }) =>
    ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/reject/`, payload),
  createEnhancement: (id: string, payload: Partial<PreAuthCreatePayload>) =>
    ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/create-enhancement/`, payload),
  createFinal: (id: string, payload: Partial<PreAuthCreatePayload>) =>
    ehrClient.post<PreAuthRequest>(`/preauth-requests/${id}/create-final/`, payload),
  agingSummary: () =>
    ehrClient.get<PreAuthAgingSummary>("/preauth-requests/aging-summary/"),

  documents: {
    list: (preauthId: string) =>
      ehrClient.get<InsurancePaginatedResponse<PreAuthDocument> | PreAuthDocument[]>(
        `/preauth-documents/?preauth_request=${preauthId}`,
      ),
    upload: (formData: FormData) =>
      ehrClient.postFormData<PreAuthDocument>("/preauth-documents/upload/", formData),
    delete: (id: string) => ehrClient.delete<void>(`/preauth-documents/${id}/`),
  },
};

// ============================================================================
// Action Center — proactive insights API
// ============================================================================

import type {
  ActionCenterSummary,
  InsightDetail,
  InsightListItem,
  LLMConnectionDetail,
  LLMConnectionListItem,
  LLMConnectionTestResult,
  LLMConnectionWritePayload,
} from "@/types/ehr";

const acQs = (params?: Record<string, string | number | undefined>) => {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "" && v !== null) sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

export const actionCenterApi = {
  list: (params?: {
    location?: string;
    severity?: string;
    module?: string;
    state?: string;
    include_hidden?: 0 | 1;
    limit?: number;
    offset?: number;
  }) =>
    ehrClient.get<PaginatedResponse<InsightListItem>>(
      `/action-center/insights/${acQs(params)}`,
    ),

  get: (id: string) =>
    ehrClient.get<InsightDetail>(`/action-center/insights/${id}/`),

  acknowledge: (id: string) =>
    ehrClient.post<InsightDetail>(`/action-center/insights/${id}/acknowledge/`, {}),

  snooze: (id: string, days = 7) =>
    ehrClient.post<InsightDetail>(`/action-center/insights/${id}/snooze/`, { days }),

  dismiss: (id: string, reason = "") =>
    ehrClient.post<InsightDetail>(`/action-center/insights/${id}/dismiss/`, { reason }),

  takeAction: (
    id: string,
    payload: { action_key: string; payload?: Record<string, unknown>; note?: string },
  ) =>
    ehrClient.post<{ insight: InsightDetail; deep_link: string }>(
      `/action-center/insights/${id}/take-action/`,
      payload,
    ),

  resolve: (id: string) =>
    ehrClient.post<InsightDetail>(`/action-center/insights/${id}/resolve/`, {}),

  regenerate: (id: string) =>
    ehrClient.post<InsightDetail>(`/action-center/insights/${id}/regenerate/`, {}),

  regenerateBulk: (params?: { insight_ids?: string[]; location?: string; severity?: string; module?: string; state?: string }) => {
    const { insight_ids, ...filters } = params ?? {};
    return ehrClient.post<{ total: number; succeeded: number; failed: number; errors: { id: string; error: string }[] }>(
      `/action-center/insights/regenerate-bulk/${acQs(filters)}`,
      { insight_ids: insight_ids ?? [] },
    );
  },

  summary: (params?: { location?: string }) =>
    ehrClient.get<ActionCenterSummary>(`/action-center/summary/${acQs(params)}`),
};

// ============================================================================
// LLM Connections — generic LLM connector admin (super-admin only)
// ============================================================================

export const llmConnectionsApi = {
  list: () =>
    ehrClient.get<PaginatedResponse<LLMConnectionListItem>>("/llm/connections/"),

  get: (id: string) => ehrClient.get<LLMConnectionDetail>(`/llm/connections/${id}/`),

  create: (data: LLMConnectionWritePayload) =>
    ehrClient.post<LLMConnectionDetail>("/llm/connections/", data),

  update: (id: string, data: Partial<LLMConnectionWritePayload>) =>
    ehrClient.patch<LLMConnectionDetail>(`/llm/connections/${id}/`, data),

  delete: (id: string) => ehrClient.delete<void>(`/llm/connections/${id}/`),

  test: (id: string) =>
    ehrClient.post<LLMConnectionTestResult>(`/llm/connections/${id}/test/`, {}),

  setDefault: (id: string) =>
    ehrClient.post<LLMConnectionDetail>(`/llm/connections/${id}/set-default/`, {}),
};

// ============================================================================
// eMAR — Medicine Administration
// ============================================================================

import type {
  EmarConfig,
  HighAlertDrugClass,
  HighAlertDrugMapping,
  EmarReason,
  MedicationOrder,
  MedicationSlot,
  MedicationSlotListItem,
  MedicationAdministration,
  AdministerPayload,
  NonAdministerPayload,
  CorrectionPayload,
  EmarSafetyBundle,
  EmarWardSummary,
  NarcoticRegisterEntry,
} from "@/types/emar";

const emarQs = (params?: Record<string, string | undefined>) => {
  if (!params) return "";
  const filtered = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
  ) as Record<string, string>;
  const qs = new URLSearchParams(filtered).toString();
  return qs ? `?${qs}` : "";
};

export const emarConfigsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<EmarConfig>>(`/emar/configs/${emarQs(params)}`),
  get: (id: string) => ehrClient.get<EmarConfig>(`/emar/configs/${id}/`),
  create: (data: Partial<EmarConfig>) =>
    ehrClient.post<EmarConfig>(`/emar/configs/`, data),
  update: (id: string, data: Partial<EmarConfig>) =>
    ehrClient.patch<EmarConfig>(`/emar/configs/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/emar/configs/${id}/`),
};

export const emarHighAlertClassesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<HighAlertDrugClass>>(`/emar/high-alert-classes/${emarQs(params)}`),
  get: (id: string) => ehrClient.get<HighAlertDrugClass>(`/emar/high-alert-classes/${id}/`),
  create: (data: Partial<HighAlertDrugClass>) =>
    ehrClient.post<HighAlertDrugClass>(`/emar/high-alert-classes/`, data),
  update: (id: string, data: Partial<HighAlertDrugClass>) =>
    ehrClient.patch<HighAlertDrugClass>(`/emar/high-alert-classes/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/emar/high-alert-classes/${id}/`),
};

export const emarHighAlertMappingsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<HighAlertDrugMapping>>(`/emar/high-alert-mappings/${emarQs(params)}`),
  get: (id: string) => ehrClient.get<HighAlertDrugMapping>(`/emar/high-alert-mappings/${id}/`),
  create: (data: Partial<HighAlertDrugMapping>) =>
    ehrClient.post<HighAlertDrugMapping>(`/emar/high-alert-mappings/`, data),
  update: (id: string, data: Partial<HighAlertDrugMapping>) =>
    ehrClient.patch<HighAlertDrugMapping>(`/emar/high-alert-mappings/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/emar/high-alert-mappings/${id}/`),
};

export const emarReasonsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<EmarReason>>(`/emar/reasons/${emarQs(params)}`),
  get: (id: string) => ehrClient.get<EmarReason>(`/emar/reasons/${id}/`),
  create: (data: Partial<EmarReason>) =>
    ehrClient.post<EmarReason>(`/emar/reasons/`, data),
  update: (id: string, data: Partial<EmarReason>) =>
    ehrClient.patch<EmarReason>(`/emar/reasons/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/emar/reasons/${id}/`),
};

export const emarOrdersApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<MedicationOrder>>(`/emar/orders/${emarQs(params)}`),
  get: (id: string) => ehrClient.get<MedicationOrder>(`/emar/orders/${id}/`),
  safetyBundle: (id: string) =>
    ehrClient.get<EmarSafetyBundle>(`/emar/orders/${id}/safety-bundle/`),
  pause: (id: string, reason: string, resume_at?: string | null) =>
    ehrClient.post<MedicationOrder>(`/emar/orders/${id}/pause/`, { reason, resume_at: resume_at ?? null }),
  resume: (id: string) =>
    ehrClient.post<MedicationOrder>(`/emar/orders/${id}/resume/`, {}),
  discontinue: (id: string, reason: string) =>
    ehrClient.post<MedicationOrder>(`/emar/orders/${id}/discontinue/`, { reason }),
};

export const emarSlotsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<MedicationSlotListItem>>(`/emar/slots/${emarQs(params)}`),
  get: (id: string) => ehrClient.get<MedicationSlot>(`/emar/slots/${id}/`),
  administer: (id: string, payload: AdministerPayload) =>
    ehrClient.post<MedicationAdministration>(`/emar/slots/${id}/administer/`, payload),
  recordNonAdministered: (id: string, payload: NonAdministerPayload) =>
    ehrClient.post<MedicationAdministration>(`/emar/slots/${id}/record-non-administered/`, payload),
  correct: (id: string, payload: CorrectionPayload) =>
    ehrClient.post<MedicationAdministration>(`/emar/slots/${id}/correct/`, payload),
  wardSummary: (params: {
    nursing_unit?: string;
    // Single-day mode (bedside /emar). Mutually exclusive with date_from/date_to.
    date?: string;
    // Range mode (eMAR supervision). Both must be supplied; capped at 31 days
    // server-side.
    date_from?: string;
    date_to?: string;
  }) =>
    ehrClient.get<EmarWardSummary>(`/emar/slots/ward-summary/${emarQs(params)}`),
  createPrnSlot: (medication_order: string) =>
    ehrClient.post<MedicationSlot>(`/emar/slots/create-prn-slot/`, { medication_order }),
  regenerate: (id: string) =>
    ehrClient.post<{ slots_created: number }>(`/emar/slots/${id}/regenerate/`, {}),
};

export const emarAdministrationsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<MedicationAdministration>>(`/emar/administrations/${emarQs(params)}`),
  get: (id: string) => ehrClient.get<MedicationAdministration>(`/emar/administrations/${id}/`),
};

export const emarNarcoticRegisterApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<NarcoticRegisterEntry>>(`/emar/narcotic-register/${emarQs(params)}`),
  get: (id: string) => ehrClient.get<NarcoticRegisterEntry>(`/emar/narcotic-register/${id}/`),
};

// ────────────────────────────────────────────────────────────────────────────
// ABDM / ABHA Registration
// ────────────────────────────────────────────────────────────────────────────

import type {
  AbhaCardResponse,
  AbhaDlEnrolPayload,
  AbhaDlEnrolResponse,
  AbhaDlSendOtpPayload,
  AbhaDlSendOtpResponse,
  AbhaDlVerifyOtpPayload,
  AbhaDlVerifyOtpResponse,
  AbhaEnrolment,
  AbhaFaceEnrolPayload,
  AbhaFaceEnrolResponse,
  AbhaFaceInitResponse,
  AbhaFaceStatusPayload,
  AbhaFaceStatusResponse,
  AbhaLinkPatientResponse,
  AbhaProfile,
  AbhaSendOtpPayload,
  AbhaSendOtpResponse,
  AbhaVerifyAadhaarSendOtpPayload,
  AbhaVerifyAadhaarVerifyOtpResponse,
  AbhaVerifyFaceInitPayload,
  AbhaVerifyFaceInitResponse,
  AbhaVerifyFaceVerifyPayload,
  AbhaVerifyFaceVerifyResponse,
  AbhaVerifyIdSendOtpResponse,
  AbhaVerifyLoginOtpPayload,
  AbhaVerifyLoginOtpResponse,
  AbhaVerifyMobileSendOtpPayload,
  AbhaVerifyMobileVerifyOtpResponse,
  AbhaVerifyOtpPayload,
  AbhaVerifyOtpResponse,
  AbhaVerifySelectUserPayload,
  AbhaVerifySelectUserResponse,
  AbhaVerifySendOtpPayload,
  AbhaVerifySendOtpResponse,
  AbhaProfileActionResponse,
  AbhaProfileSendOtpResponse,
  AbhaAccountActionSendOtpResponse,
  AbhaFindSearchResponse,
  AbhaFindSendOtpResponse,
  AbhaReactivateVerifyResponse,
} from "@/types/ehr";

const abhaQs = (params?: Record<string, string | number | undefined>): string => {
  if (!params) return "";
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join("&")}` : "";
};

export const abhaApi = {
  list: (params?: Record<string, string | number | undefined>) =>
    ehrClient.get<PaginatedResponse<AbhaEnrolment>>(`/abdm/abha/${abhaQs(params)}`),
  get: (id: string) => ehrClient.get<AbhaEnrolment>(`/abdm/abha/${id}/`),
  sendOtp: (data: AbhaSendOtpPayload) =>
    ehrClient.post<AbhaSendOtpResponse>(`/abdm/abha/send-otp/`, data),
  verifyOtp: (data: AbhaVerifyOtpPayload) =>
    ehrClient.post<AbhaVerifyOtpResponse>(`/abdm/abha/verify-otp/`, data),
  // CRT_ABHA_112 — ABHA address suggestions + set a custom/preferred address
  abhaAddressSuggestions: (enrolment_id: string) =>
    ehrClient.post<{ suggestions: string[]; current: string }>(
      `/abdm/abha/abha-address/suggestions/`,
      { enrolment_id },
    ),
  abhaAddressSet: (enrolment_id: string, abha_address: string) =>
    ehrClient.post<{ abha_address: string }>(
      `/abdm/abha/abha-address/set/`,
      { enrolment_id, abha_address },
    ),
  // CRT_ABHA_109 — verify a communication mobile that differs from the Aadhaar mobile
  commMobileSendOtp: (mobile: string) =>
    ehrClient.post<{ txn_id: string; message: string }>(
      `/abdm/abha/comm-mobile/send-otp/`,
      { mobile },
    ),
  commMobileVerifyOtp: (txn_id: string, otp: string) =>
    ehrClient.post<{ verified: boolean }>(
      `/abdm/abha/comm-mobile/verify-otp/`,
      { txn_id, otp },
    ),
  getCard: (id: string) => ehrClient.get<AbhaCardResponse>(`/abdm/abha/${id}/card/`),
  getQr: (id: string) =>
    ehrClient.get<{ qr_base64: string; mime_type: string }>(`/abdm/abha/${id}/qr/`),
  getRemoteProfile: (id: string) =>
    ehrClient.get<{
      enrolment_id: string;
      abha_number: string;
      abha_address: string;
      abha_profile: AbhaProfile;
    }>(`/abdm/abha/${id}/profile/`),
  linkPatient: (id: string, patient_id: string) =>
    ehrClient.post<AbhaLinkPatientResponse>(`/abdm/abha/${id}/link-patient/`, { patient_id }),
  createPatient: (id: string, location_id?: string | null) =>
    ehrClient.post<AbhaLinkPatientResponse>(
      `/abdm/abha/${id}/create-patient/`,
      location_id ? { location_id } : {},
    ),
  reverifySendOtp: (patient_id: string, aadhaar?: string) =>
    ehrClient.post<{
      enrolment_id: string;
      patient_id: string;
      aadhaar_masked: string;
      txn_id: string;
      masked_mobile: string;
      message: string;
    }>(`/abdm/abha/reverify-send-otp/`, aadhaar ? { patient_id, aadhaar } : { patient_id }),

  // ── ABHA via Driving Licence (DL) flow ───────────────────────────────────
  dlSendOtp: (data: AbhaDlSendOtpPayload) =>
    ehrClient.post<AbhaDlSendOtpResponse>(`/abdm/abha/dl/send-otp/`, data),
  dlVerifyOtp: (data: AbhaDlVerifyOtpPayload) =>
    ehrClient.post<AbhaDlVerifyOtpResponse>(`/abdm/abha/dl/verify-otp/`, data),
  dlEnrol: (data: AbhaDlEnrolPayload) =>
    ehrClient.post<AbhaDlEnrolResponse>(`/abdm/abha/dl/enrol/`, data),

  // ── ABHA via Face Auth (QR) flow ─────────────────────────────────────────
  // 1) faceInit: ABDM generates a txnId; we render the QR PNG server-side.
  // 2) faceStatus: poll capturePID every 5–10s. PENDING → VERIFIED → COMPLETE.
  // 3) faceEnrol: submit Aadhaar + mobile once status === COMPLETE; ABDM finalizes
  //    the ABHA using the cached face PID it received from the ABHA app.
  faceInit: () => ehrClient.post<AbhaFaceInitResponse>(`/abdm/abha/face/init/`, {}),
  faceStatus: (data: AbhaFaceStatusPayload) =>
    ehrClient.post<AbhaFaceStatusResponse>(`/abdm/abha/face/status/`, data),
  faceEnrol: (data: AbhaFaceEnrolPayload) =>
    ehrClient.post<AbhaFaceEnrolResponse>(`/abdm/abha/face/enrol/`, data),

  // ── ABHA Verification (log in an existing ABHA by ABHA Number) ────────────
  verifySendOtp: (data: AbhaVerifySendOtpPayload) =>
    ehrClient.post<AbhaVerifySendOtpResponse>(`/abdm/abha/verify/send-otp/`, data),
  verifyLoginOtp: (data: AbhaVerifyLoginOtpPayload) =>
    ehrClient.post<AbhaVerifyLoginOtpResponse>(`/abdm/abha/verify/verify-otp/`, data),
  // Face-based verification: init renders a QR; verify finalizes once the
  // patient has done the on-device face capture with the ABHA app.
  verifyFaceInit: (data: AbhaVerifyFaceInitPayload) =>
    ehrClient.post<AbhaVerifyFaceInitResponse>(`/abdm/abha/verify/face/init/`, data),
  verifyFaceVerify: (data: AbhaVerifyFaceVerifyPayload) =>
    ehrClient.post<AbhaVerifyFaceVerifyResponse>(`/abdm/abha/verify/face/verify/`, data),
  // Verify by Mobile Number — 3 steps: send-otp → verify-otp (lists accounts)
  // → select-user (pick one ABHA, finalize).
  verifyMobileSendOtp: (data: AbhaVerifyMobileSendOtpPayload) =>
    ehrClient.post<AbhaVerifyIdSendOtpResponse>(`/abdm/abha/verify/mobile/send-otp/`, data),
  verifyMobileVerifyOtp: (data: AbhaVerifyLoginOtpPayload) =>
    ehrClient.post<AbhaVerifyMobileVerifyOtpResponse>(`/abdm/abha/verify/mobile/verify-otp/`, data),
  verifyMobileSelectUser: (data: AbhaVerifySelectUserPayload) =>
    ehrClient.post<AbhaVerifySelectUserResponse>(`/abdm/abha/verify/mobile/select-user/`, data),
  // Verify by Aadhaar Number — 2 steps.
  verifyAadhaarSendOtp: (data: AbhaVerifyAadhaarSendOtpPayload) =>
    ehrClient.post<AbhaVerifyIdSendOtpResponse>(`/abdm/abha/verify/aadhaar/send-otp/`, data),
  verifyAadhaarVerifyOtp: (data: AbhaVerifyLoginOtpPayload) =>
    ehrClient.post<AbhaVerifyAadhaarVerifyOtpResponse>(`/abdm/abha/verify/aadhaar/verify-otp/`, data),

  // ── ABHA Profile management (edit a verified ABHA — uses its X-token) ─────
  updatePhoto: (id: string, photo: string) =>
    ehrClient.post<AbhaProfileActionResponse>(`/abdm/abha/${id}/profile/photo/`, { photo }),
  updateEmailSendOtp: (id: string, email: string) =>
    ehrClient.post<AbhaProfileSendOtpResponse>(`/abdm/abha/${id}/profile/email/send-otp/`, { email }),
  updateEmailVerifyOtp: (id: string, otp: string) =>
    ehrClient.post<AbhaProfileActionResponse>(`/abdm/abha/${id}/profile/email/verify-otp/`, { otp }),
  updateMobileSendOtp: (id: string, mobile: string) =>
    ehrClient.post<AbhaProfileSendOtpResponse>(`/abdm/abha/${id}/profile/mobile/send-otp/`, { mobile }),
  updateMobileVerifyOtp: (id: string, otp: string) =>
    ehrClient.post<AbhaProfileActionResponse>(`/abdm/abha/${id}/profile/mobile/verify-otp/`, { otp }),
  setPassword: (id: string, newPassword: string, oldPassword?: string) =>
    ehrClient.post<AbhaProfileActionResponse>(`/abdm/abha/${id}/profile/set-password/`, {
      new_password: newPassword,
      ...(oldPassword ? { old_password: oldPassword } : {}),
    }),

  // ── ABHA Profile page — Find ABHA, Deactivate, Delete, Reactivate ────────
  findSearch: (mobile: string) =>
    ehrClient.post<AbhaFindSearchResponse>(`/abdm/abha/find/search/`, { mobile }),
  findSendOtp: (enrolmentId: string, index: number) =>
    ehrClient.post<AbhaFindSendOtpResponse>(`/abdm/abha/find/send-otp/`, {
      enrolment_id: enrolmentId,
      index,
    }),
  findVerifyOtp: (enrolmentId: string, otp: string) =>
    ehrClient.post<AbhaVerifyOtpResponse>(`/abdm/abha/find/verify-otp/`, {
      enrolment_id: enrolmentId,
      otp,
    }),
  deactivateSendOtp: (id: string) =>
    ehrClient.post<AbhaAccountActionSendOtpResponse>(`/abdm/abha/${id}/deactivate/send-otp/`, {}),
  deactivateVerifyOtp: (id: string, otp: string, reason?: string) =>
    ehrClient.post<AbhaProfileActionResponse>(`/abdm/abha/${id}/deactivate/verify-otp/`, {
      otp,
      ...(reason ? { reason } : {}),
    }),
  deleteSendOtp: (id: string) =>
    ehrClient.post<AbhaAccountActionSendOtpResponse>(`/abdm/abha/${id}/delete/send-otp/`, {}),
  deleteVerifyOtp: (id: string, otp: string, reason?: string) =>
    ehrClient.post<AbhaProfileActionResponse>(`/abdm/abha/${id}/delete/verify-otp/`, {
      otp,
      ...(reason ? { reason } : {}),
    }),
  reactivateSendOtp: (abhaNumber: string) =>
    ehrClient.post<AbhaFindSendOtpResponse>(`/abdm/abha/reactivate/send-otp/`, {
      abha_number: abhaNumber,
    }),
  reactivateVerifyOtp: (enrolmentId: string, otp: string) =>
    ehrClient.post<AbhaReactivateVerifyResponse>(`/abdm/abha/reactivate/verify-otp/`, {
      enrolment_id: enrolmentId,
      otp,
    }),
};

// ── ABDM HIE — per-hospital Milestone 2 configuration ───────────────────────
export const abdmHieApi = {
  getConfig: () => ehrClient.get<AbdmFacilityConfig>(`/abdm-hie/facility-config/`),
  updateConfig: (data: AbdmFacilityConfigUpdate) =>
    ehrClient.patch<AbdmFacilityConfig>(`/abdm-hie/facility-config/`, data),

  testSession: () =>
    ehrClient.post<AbdmTestSessionResponse>(`/abdm-hie/facility-config/test-session/`, {}),
  registerBridgeUrl: () =>
    ehrClient.post<AbdmRegisterBridgeUrlResponse>(
      `/abdm-hie/facility-config/register-bridge-url/`,
      {},
    ),
  registerFacility: () =>
    ehrClient.post<AbdmRegisterFacilityResponse>(
      `/abdm-hie/facility-config/register-facility/`,
      {},
    ),
  markFacilityRegistered: (note?: string) =>
    ehrClient.post<AbdmMarkFacilityRegisteredResponse>(
      `/abdm-hie/facility-config/mark-facility-registered/`,
      note ? { note } : {},
    ),

  // ── HIP-Initiated Linking test rig (Step 2) ──────────────────────────────
  generateLinkToken: (data: AbdmGenerateLinkTokenPayload) =>
    ehrClient.post<AbdmGenerateLinkTokenResponse>(
      `/abdm-hie/hip-test/generate-link-token/`,
      data,
    ),
  getCachedLinkToken: (abha_address: string) =>
    ehrClient.get<AbdmLinkTokenCacheResponse>(
      `/abdm-hie/hip-test/link-token-cache/?abha_address=${encodeURIComponent(abha_address)}`,
    ),
  linkCareContexts: (data: AbdmLinkCareContextsPayload) =>
    ehrClient.post<AbdmLinkCareContextsResponse>(
      `/abdm-hie/hip-test/link-care-contexts/`,
      data,
    ),
  listInboundEvents: (limit = 20) =>
    ehrClient.get<AbdmInboundEventsResponse>(
      `/abdm-hie/hip-test/inbound-events/?limit=${limit}`,
    ),
  findSavedAbha: (mobile: string) =>
    ehrClient.get<AbdmSavedAbhaResponse>(
      `/abdm-hie/hip-test/saved-abha/?mobile=${encodeURIComponent(mobile)}`,
    ),
  findPatientByAbha: (abha_address?: string, abha_number?: string) => {
    const qs = new URLSearchParams();
    if (abha_address) qs.set("abha_address", abha_address);
    if (abha_number) qs.set("abha_number", abha_number);
    return ehrClient.get<AbdmPatientByAbhaResponse>(
      `/abdm-hie/hip-test/patient-by-abha/?${qs.toString()}`,
    );
  },

  // ── MSG91 SMS gateway config (per-hospital) ──────────────────────────────
  getMsg91Config: () => ehrClient.get<Msg91Config>(`/abdm-hie/msg91/config/`),
  updateMsg91Config: (data: Msg91ConfigUpdate) =>
    ehrClient.patch<Msg91Config>(`/abdm-hie/msg91/config/`, data),
  msg91TestSend: (mobile: string) =>
    ehrClient.post<Msg91TestSendResponse>(`/abdm-hie/msg91/test-send/`, { mobile }),

  // ── Step 3 — User-Initiated Linking live view ─────────────────────────────
  userLinkingChain: (limit = 20) =>
    ehrClient.get<AbdmUserLinkingChainResponse>(
      `/abdm-hie/user-linking/chain/?limit=${limit}`,
    ),
  devOtpLog: (limit = 10) =>
    ehrClient.get<AbdmDevOtpLogResponse>(
      `/abdm-hie/user-linking/dev-otp-log/?limit=${limit}`,
    ),

  // ── Step 5/6 — Scan & Share / Running Token ─────────────────────────────
  opdCounters: () =>
    ehrClient.get<{ counters: AbdmOpdCounter[] }>(`/abdm-hie/opd-counters/`),
  createOpdCounter: (data: AbdmOpdCounterCreate) =>
    ehrClient.post<AbdmOpdCounter>(`/abdm-hie/opd-counters/`, data),
  updateOpdCounter: (id: string, data: Partial<AbdmOpdCounterCreate>) =>
    ehrClient.patch<AbdmOpdCounter>(`/abdm-hie/opd-counters/${id}/`, data),
  deleteOpdCounter: (id: string) =>
    ehrClient.delete<void>(`/abdm-hie/opd-counters/${id}/`),
  serveNextToken: (id: string) =>
    ehrClient.post<AbdmOpdCounter>(`/abdm-hie/opd-counters/${id}/serve-next/`, {}),
  shareSessions: (counter_id?: string, limit = 20) => {
    const qs = new URLSearchParams();
    if (counter_id) qs.set("counter", counter_id);
    qs.set("limit", String(limit));
    return ehrClient.get<{ sessions: AbdmShareSession[] }>(
      `/abdm-hie/share-sessions/?${qs.toString()}`,
    );
  },

  // ── Step 4 — Consent + Data Flow live view ──────────────────────────────
  consentArtefacts: (opts?: { limit?: number; status?: string; abha?: string }) => {
    const qs = new URLSearchParams();
    if (opts?.limit) qs.set("limit", String(opts.limit));
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.abha) qs.set("abha", opts.abha);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return ehrClient.get<AbdmConsentArtefactListResponse>(
      `/abdm-hie/consent/artefacts/${suffix}`,
    );
  },
  consentArtefactDetail: (consent_id: string) =>
    ehrClient.get<AbdmConsentArtefactDetailResponse>(
      `/abdm-hie/consent/artefacts/${encodeURIComponent(consent_id)}/`,
    ),
  simulateConsentNotify: (data: AbdmSimulateConsentNotifyPayload = {}) =>
    ehrClient.post<AbdmSimulateConsentNotifyResponse>(
      `/abdm-hie/consent/simulate-notify/`,
      data,
    ),

  // ── Step 4c+4d — HI Request inbound + Data Transfer push (admin live view) ─
  hiRequests: (limit = 50) =>
    ehrClient.get<AbdmHIRequestListResponse>(
      `/abdm-hie/data-flow/hi-requests/?limit=${limit}`,
    ),
  hiRequestDetail: (transaction_id: string) =>
    ehrClient.get<AbdmHIRequestDetailResponse>(
      `/abdm-hie/data-flow/hi-requests/${encodeURIComponent(transaction_id)}/`,
    ),
  dataTransfers: (limit = 50) =>
    ehrClient.get<AbdmDataTransferListResponse>(
      `/abdm-hie/data-flow/transfers/?limit=${limit}`,
    ),
  simulateHIRequest: (data: AbdmSimulateHIRequestPayload) =>
    ehrClient.post<AbdmSimulateHIRequestResponse>(
      `/abdm-hie/data-flow/simulate-hi-request/`,
      data,
    ),

  // ── Step 4b extension — CareContext data payload editor ────────────────────
  listCareContexts: (patient_id?: string, limit = 100) => {
    const qs = new URLSearchParams();
    if (patient_id) qs.set("patient_id", patient_id);
    qs.set("limit", String(limit));
    return ehrClient.get<AbdmCareContextListResponse>(
      `/abdm-hie/care-contexts/?${qs.toString()}`,
    );
  },
  getCareContext: (id: string) =>
    ehrClient.get<AbdmCareContextDetailResponse>(
      `/abdm-hie/care-contexts/${id}/`,
    ),
  updateCareContext: (id: string, data: AbdmCareContextUpdatePayload) =>
    ehrClient.patch<AbdmCareContextDetailResponse>(
      `/abdm-hie/care-contexts/${id}/`,
      data,
    ),

  // ── Milestone 3 — HIU "Request Records" flow ──────────────────────────────
  hiuConsentRequests: (limit = 30) =>
    ehrClient.get<AbdmHiuConsentRequestListResponse>(
      `/abdm-hie/hiu/consent-requests/?limit=${limit}`,
    ),
  hiuConsentRequestDetail: (id: string) =>
    ehrClient.get<AbdmHiuConsentRequestDetail>(
      `/abdm-hie/hiu/consent-requests/${id}/`,
    ),
  hiuConsentRequestCreate: (data: AbdmHiuCreatePayload) =>
    ehrClient.post<AbdmHiuCreateResponse>(
      `/abdm-hie/hiu/consent-requests/`, data,
    ),
  hiuPullData: (id: string) =>
    ehrClient.post<AbdmHiuPullDataResponse>(
      `/abdm-hie/hiu/consent-requests/${id}/pull-data/`,
      {},
    ),
  hiuCheckStatus: (id: string) =>
    ehrClient.post<AbdmHiuActionResponse>(
      `/abdm-hie/hiu/consent-requests/${id}/check-status/`,
      {},
    ),
  hiuFetchArtefact: (id: string) =>
    ehrClient.post<AbdmHiuActionResponse>(
      `/abdm-hie/hiu/consent-requests/${id}/fetch-artefact/`,
      {},
    ),
  hiuPatientSearch: (q: string) =>
    ehrClient.get<AbdmHiuPatientSearchResponse>(
      `/abdm-hie/hiu/patient-search/?q=${encodeURIComponent(q)}`,
    ),
};

// ============================================================================
// Collection / Scroll-Lock — cashier cash-handover reconciliation
// ============================================================================

import type {
  Scroll,
  ScrollDetail,
  ScrollSummary,
  PendingReceiptsResponse,
} from "@/types/ehr";

export const collectionApi = {
  scrolls: {
    list: (params?: Record<string, string | string[] | undefined>) =>
      ehrClient.get<PaginatedResponse<Scroll>>(
        `/collection/scrolls/${crQs(params)}`
      ),
    get: (id: string) =>
      ehrClient.get<ScrollDetail>(`/collection/scrolls/${id}/`),
    pending: (params?: Record<string, string | string[] | undefined>) =>
      ehrClient.get<PendingReceiptsResponse>(
        `/collection/scrolls/pending/${crQs(params)}`
      ),
    lock: (location?: string) =>
      ehrClient.post<ScrollDetail>(`/collection/scrolls/lock/`, location ? { location } : {}),
    acknowledge: (id: string) =>
      ehrClient.post<Scroll>(`/collection/scrolls/${id}/acknowledge/`, {}),
    reopen: (id: string, reason = "") =>
      ehrClient.post<Scroll>(`/collection/scrolls/${id}/reopen/`, { reason }),
    summary: (params?: Record<string, string | string[] | undefined>) =>
      ehrClient.get<ScrollSummary>(`/collection/scrolls/summary/${crQs(params)}`),
  },
};


// ── Appointment Requests ───────────────────────────────────────────────────
export const appointmentRequestsApi = {
  list: (params?: {
    status?: string; open?: string; location?: string;
    requested_doctor?: string; department?: string; patient?: string;
    priority?: string; search?: string; limit?: string; offset?: string;
    ordering?: string;
  }) =>
    ehrClient.get<PaginatedResponse<AppointmentRequest>>(
      `/appointment-requests/${toQs(params)}`
    ),
  get: (id: string) =>
    ehrClient.get<AppointmentRequest>(`/appointment-requests/${id}/`),
  create: (data: AppointmentRequestPayload) =>
    ehrClient.post<AppointmentRequest>("/appointment-requests/", data),
  update: (id: string, data: Partial<AppointmentRequestPayload>) =>
    ehrClient.patch<AppointmentRequest>(`/appointment-requests/${id}/`, data),

  /** Open-request counts, for the sidebar badge. */
  summary: (params?: { location?: string }) =>
    ehrClient.get<AppointmentRequestSummary>(
      `/appointment-requests/summary/${toQs(params)}`
    ),

  /** Patient has been rung — stops a second caller repeating it. */
  markContacted: (id: string) =>
    ehrClient.post<AppointmentRequest>(
      `/appointment-requests/${id}/mark-contacted/`, {}
    ),
  /** Link the booking that was just made and close the request. */
  schedule: (id: string, appointmentId: string) =>
    ehrClient.post<AppointmentRequest>(
      `/appointment-requests/${id}/schedule/`, { appointment: appointmentId }
    ),
  /** Close without booking. Reason is REQUIRED so a request never dies silently. */
  couldNotSchedule: (id: string, reason: string) =>
    ehrClient.post<AppointmentRequest>(
      `/appointment-requests/${id}/could-not-schedule/`, { reason }
    ),
  cancel: (id: string, reason: string) =>
    ehrClient.post<AppointmentRequest>(
      `/appointment-requests/${id}/cancel/`, { reason }
    ),
};


// ── Appointment Lookups (leave type / visit type / priority / cancel reason) ─
export const appointmentLookupsApi = {
  list: (params?: {
    location?: string; category?: string; is_active?: string; limit?: string;
  }) =>
    ehrClient.get<PaginatedResponse<AppointmentLookup>>(
      `/appointment-lookups/${toQs(params)}`),
  create: (data: AppointmentLookupPayload) =>
    ehrClient.post<AppointmentLookup>("/appointment-lookups/", data),
  update: (id: string, data: Partial<AppointmentLookupPayload>) =>
    ehrClient.patch<AppointmentLookup>(`/appointment-lookups/${id}/`, data),
  /** Soft delete — records already store this code and must stay readable. */
  delete: (id: string) => ehrClient.delete<void>(`/appointment-lookups/${id}/`),
};

// ── Appointment Sessions (Morning / Evening) ───────────────────────────────
export const appointmentSessionsApi = {
  list: (params?: { location?: string; is_active?: string; limit?: string }) =>
    ehrClient.get<PaginatedResponse<AppointmentSession>>(`/sessions/${toQs(params)}`),
  create: (data: AppointmentSessionPayload) =>
    ehrClient.post<AppointmentSession>("/sessions/", data),
  update: (id: string, data: Partial<AppointmentSessionPayload>) =>
    ehrClient.patch<AppointmentSession>(`/sessions/${id}/`, data),
  /** Soft delete — rosters keep their label, the session leaves the picker. */
  delete: (id: string) => ehrClient.delete<void>(`/sessions/${id}/`),
};

// ── Queue Types (Management / General token bands) ─────────────────────────
import type {
  BillingCounter,
  BillingCounterChannel,
  BillingCounterMineResponse,
  BillingCounterPayload,
} from "@/types/ehr";

/** Billing counters — the physical desks OP and ER bills are raised at.
 *
 * Billing owns this endpoint; the MODEL lives in the payments gateway, which
 * keeps its own /payments-gateway/counters/ for the card-terminal fields this
 * one deliberately does not expose.
 */
export const billingCountersApi = {
  list: (params?: {
    location?: string;
    channel?: BillingCounterChannel;
    is_active?: string;
    limit?: string;
  }) =>
    ehrClient.get<PaginatedResponse<BillingCounter>>(
      `/billing-counters/${toQs(params)}`
    ),
  create: (data: BillingCounterPayload) =>
    ehrClient.post<BillingCounter>("/billing-counters/", data),
  update: (id: string, data: Partial<BillingCounterPayload>) =>
    ehrClient.patch<BillingCounter>(`/billing-counters/${id}/`, data),
  /** Remove the counter from ONE channel. Soft delete throughout — bills and
   *  payments already point at it and must keep resolving it for ever. A
   *  counter still serving the other tab stays active. */
  removeFromChannel: (id: string, channel: BillingCounterChannel) =>
    ehrClient.delete<void>(`/billing-counters/${id}/?channel=${channel}`),
  /** Is this location using counters here, and which may I use?
   *
   *  One call answers all three questions the billing screen has, including
   *  the blocked state (`configured: true` with an empty `counters`). */
  mine: (params: { location: string; channel: BillingCounterChannel }) =>
    ehrClient.get<BillingCounterMineResponse>(
      `/billing-counters/mine/${toQs(params)}`
    ),
};

export const queueTypesApi = {
  list: (params?: { location?: string; is_active?: string; limit?: string }) =>
    ehrClient.get<PaginatedResponse<QueueType>>(`/queue-types/${toQs(params)}`),
  create: (data: QueueTypePayload) =>
    ehrClient.post<QueueType>("/queue-types/", data),
  update: (id: string, data: Partial<QueueTypePayload>) =>
    ehrClient.patch<QueueType>(`/queue-types/${id}/`, data),
  /** Soft delete — existing appointments still resolve their band. */
  delete: (id: string) => ehrClient.delete<void>(`/queue-types/${id}/`),
};

import type {
  CallNextResult,
  CallPatientResult,
  DisplayScreen,
  DisplayScreenPayload,
  DisplayScreenState,
  WebBookingSettings,
  WebBookingReadiness,
  WebBookingPublishingTree,
  WebBookingFormSchemaResponse,
  WebBookingKey,
  WebBookingRequest,
  WebBookingSummary,
} from "@/types/ehr";

// ── Website Booking (patient self-booking from the hospital's own site) ─────
// Backed by the `public_booking` Django app. The PUBLIC patient endpoints live
// at /api/public/v1/* and are never called from this portal — only the widget
// calls those. Everything here is the authenticated org-admin surface.
export const webBookingApi = {
  getSettings: () => ehrClient.get<WebBookingSettings>("/web-booking/settings/"),
  updateSettings: (data: Partial<WebBookingSettings>) =>
    ehrClient.patch<WebBookingSettings>("/web-booking/settings/", data),

  readiness: () => ehrClient.get<WebBookingReadiness>("/web-booking/readiness/"),

  /** The publishing tree, joined live against the real masters so it cannot drift. */
  publishing: (location?: string) =>
    ehrClient.get<WebBookingPublishingTree>(`/web-booking/publishing/${toQs({ location })}`),
  setPublication: (data: {
    kind: "location" | "doctor" | "schedule";
    ref_id: string;
    is_published: boolean;
    location_id?: string | null;
    display_name?: string;
    public_bio?: string;
    public_address?: string;
    timings_note?: string;
  }) => ehrClient.post<{ ok: boolean }>("/web-booking/publishing/", data),
  bulkPublish: (location_id: string, is_published: boolean) =>
    ehrClient.post<{ updated: number }>("/web-booking/publishing/bulk/", { location_id, is_published }),

  /** The derived form + the hospital's own raw config, so they can compare. */
  formSchema: (location: string) =>
    ehrClient.get<WebBookingFormSchemaResponse>(`/web-booking/form-schema/${toQs({ location })}`),

  listKeys: () => ehrClient.get<WebBookingKey[]>("/web-booking/keys/"),
  createKey: (label: string, origins: string[]) =>
    ehrClient.post<WebBookingKey>("/web-booking/keys/", { label, origins }),
  updateKey: (id: string, origins: string[]) =>
    ehrClient.patch<WebBookingKey>(`/web-booking/keys/${id}/`, { origins }),
  revokeKey: (id: string) => ehrClient.delete<void>(`/web-booking/keys/${id}/`),

  /** Preview credential — reads live data, refuses every write. */
  previewToken: (shareable = false) =>
    ehrClient.post<{ preview_token: string; expires_in: number }>(
      "/web-booking/preview-token/", { shareable }),

  listRequests: (params?: { status?: string; location?: string; limit?: string }) =>
    ehrClient.get<PaginatedResponse<WebBookingRequest>>(`/web-booking/requests/${toQs(params)}`),
  summary: () => ehrClient.get<WebBookingSummary>("/web-booking/requests/summary/"),
  approveRequest: (id: string, patient_choice?: string | null) =>
    ehrClient.post<{ ok: boolean; appointment_number: string; token_number: number | null }>(
      `/web-booking/requests/${id}/approve/`, { patient_choice: patient_choice ?? null }),
  declineRequest: (id: string, reason: string) =>
    ehrClient.post<{ ok: boolean }>(`/web-booking/requests/${id}/decline/`, { reason }),
};

// ── Call Next ───────────────────────────────────────────────────────────────
// Resolved SERVER-side: same billing gate, same ordering as the worklist, no
// page cap and no accidental client filter. Replaces the old "first row of the
// filtered array", which could call a routine patient ahead of an emergency one.
export const consultationCallNextApi = {
  call: (body?: { doctor_id?: string; date?: string }) =>
    ehrClient.post<CallNextResult>("/consultations/call-next/", body ?? {}),
  /**
   * Call THIS patient in, rather than whoever is next — for a patient who
   * returns out of queue order (the same-day review). Status-aware server-side:
   * starts a waiting patient, resumes one that was pending review, or RECALLS a
   * completed visit. Reuses the existing encounter, so it charges nothing.
   */
  callPatient: (encounterId: string) =>
    ehrClient.post<CallPatientResult>(`/consultations/${encounterId}/call/`, {}),
};

// ── Token Screens (waiting-room displays) ───────────────────────────────────
// Backed by the `token_display` Django app. The patient-facing board calls
// /api/public/v1/display/<screen_id>/ directly and is NOT routed through here —
// it must work with no auth token at all.
export const displayScreensApi = {
  list: (params?: { location?: string; is_active?: string; limit?: string }) =>
    ehrClient.get<PaginatedResponse<DisplayScreen>>(`/display-screens/${toQs(params)}`),
  create: (data: DisplayScreenPayload) =>
    ehrClient.post<DisplayScreen>("/display-screens/", data),
  update: (id: string, data: Partial<DisplayScreenPayload>) =>
    ehrClient.patch<DisplayScreen>(`/display-screens/${id}/`, data),
  /** Soft delete — the id stays resolvable so its URL 404s deliberately
   *  rather than being reissued to a different room. */
  delete: (id: string) => ehrClient.delete<void>(`/display-screens/${id}/`),
  /** Exactly what that TV is showing right now, without walking to the corridor. */
  preview: (id: string) => ehrClient.get<DisplayScreenState>(`/display-screens/${id}/preview/`),
};

// ============================================================================
// Canteen / F&B
// ============================================================================
import type {
  CanteenCashDenomination,
  CanteenClosingStockReport,
  CanteenCustomerType,
  CanteenDailySalesReport,
  CanteenDashboard,
  CanteenMenuBoardItem,
  CanteenMenuCategory,
  CanteenMenuItem as CanteenMenuItemType,
  CanteenMenuItemListItem,
  CanteenMenuItemPayload,
  CanteenMenuItemRate as CanteenMenuItemRateType,
  CanteenOutlet,
  CanteenOutletType,
  CanteenRatePlan,
  CanteenSale as CanteenSaleType,
  CanteenSaleCompletePayload,
  CanteenSaleCreatePayload,
  CanteenSaleLinePayload,
  CanteenSaleListItem,
  CanteenSettings as CanteenSettingsType,
  CanteenSettlementMode,
  CanteenShiftClosePayload,
  CanteenShiftCountSubmitPayload,
  CanteenShiftDefinition,
  CanteenShiftOpenPayload,
  CanteenShiftSession as CanteenShiftSessionType,
  CanteenShiftSessionListItem,
  CanteenVarianceReport,
} from "@/types/ehr";

const canteenQs = (params?: Record<string, string>) =>
  params ? "?" + new URLSearchParams(params).toString() : "";

/** Generic master CRUD — every canteen master has the identical shape. */
function canteenMasterApi<T>(resource: string) {
  return {
    list: (params?: Record<string, string>) =>
      ehrClient.get<PaginatedResponse<T>>(`/${resource}/${canteenQs(params)}`),
    get: (id: string) => ehrClient.get<T>(`/${resource}/${id}/`),
    create: (data: Partial<T>) => ehrClient.post<T>(`/${resource}/`, data),
    update: (id: string, data: Partial<T>) =>
      ehrClient.patch<T>(`/${resource}/${id}/`, data),
    delete: (id: string) => ehrClient.delete<void>(`/${resource}/${id}/`),
  };
}

export const canteenOutletTypesApi = canteenMasterApi<CanteenOutletType>("canteen-outlet-types");
export const canteenSettlementModesApi = canteenMasterApi<CanteenSettlementMode>("canteen-settlement-modes");
export const canteenCustomerTypesApi = canteenMasterApi<CanteenCustomerType>("canteen-customer-types");
export const canteenMenuCategoriesApi = canteenMasterApi<CanteenMenuCategory>("canteen-menu-categories");
export const canteenCashDenominationsApi = canteenMasterApi<CanteenCashDenomination>("canteen-cash-denominations");
export const canteenShiftDefinitionsApi = canteenMasterApi<CanteenShiftDefinition>("canteen-shift-definitions");

export const canteenOutletsApi = {
  ...canteenMasterApi<CanteenOutlet>("canteen-outlets"),
  /** Creates the backing SUB store + CANTEEN department if absent. */
  provisionStore: (id: string) =>
    ehrClient.post<CanteenOutlet>(`/canteen-outlets/${id}/provision-store/`, {}),
};

export const canteenRatePlansApi = {
  ...canteenMasterApi<CanteenRatePlan>("canteen-rate-plans"),
  /** Materialises ABSOLUTE rates from the parent plan. Rows flagged
   *  is_manually_overridden are protected. */
  generateFromParent: (
    id: string,
    data: { effective_from: string; overwrite_existing?: boolean; dry_run?: boolean },
  ) =>
    ehrClient.post<{
      dry_run: boolean;
      plan: string;
      parent_plan: string;
      discount_pct: string;
      generated: { menu_item: string; parent_rate: string; new_rate: string }[];
      skipped: { menu_item: string; reason: string }[];
      protected_manual_overrides: { menu_item: string; rate: string }[];
      counts: { generated: number; skipped: number; protected: number };
    }>(`/canteen-rate-plans/${id}/generate-from-parent/`, data),
};

export const canteenMenuItemsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<CanteenMenuItemListItem>>(`/canteen-menu-items/${canteenQs(params)}`),
  get: (id: string) => ehrClient.get<CanteenMenuItemType>(`/canteen-menu-items/${id}/`),
  create: (data: CanteenMenuItemPayload) =>
    ehrClient.post<CanteenMenuItemType>(`/canteen-menu-items/`, data),
  update: (id: string, data: Partial<CanteenMenuItemPayload>) =>
    ehrClient.patch<CanteenMenuItemType>(`/canteen-menu-items/${id}/`, data),
  delete: (id: string) => ehrClient.delete<void>(`/canteen-menu-items/${id}/`),
  rates: (id: string) =>
    ehrClient.get<CanteenMenuItemRateType[]>(`/canteen-menu-items/${id}/rates/`),
  setRate: (
    id: string,
    data: { rate_plan: string; outlet?: string | null; rate: string; effective_from: string },
  ) => ehrClient.post<CanteenMenuItemRateType>(`/canteen-menu-items/${id}/rates/`, data),
};

export const canteenSalesApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<CanteenSaleListItem>>(`/canteen-sales/${canteenQs(params)}`),
  get: (id: string) => ehrClient.get<CanteenSaleType>(`/canteen-sales/${id}/`),
  create: (data: CanteenSaleCreatePayload) =>
    ehrClient.post<CanteenSaleType>(`/canteen-sales/`, data),
  /** The POS board. Fetch ONCE per outlet+customer-type and filter client-side. */
  menu: (params: Record<string, string>) =>
    ehrClient.get<CanteenMenuBoardItem[]>(`/canteen-sales/menu/${canteenQs(params)}`),
  addItem: (id: string, data: CanteenSaleLinePayload) =>
    ehrClient.post<CanteenSaleType>(`/canteen-sales/${id}/items/add/`, data),
  updateItem: (id: string, itemId: string, data: { quantity?: string; discount_amount?: string }) =>
    ehrClient.patch<CanteenSaleType>(`/canteen-sales/${id}/items/${itemId}/update/`, data),
  removeItem: (id: string, itemId: string) =>
    ehrClient.delete<CanteenSaleType>(`/canteen-sales/${id}/items/${itemId}/remove/`),
  recalculate: (id: string) =>
    ehrClient.post<CanteenSaleType>(`/canteen-sales/${id}/recalculate/`, {}),
  complete: (id: string, data: CanteenSaleCompletePayload) =>
    ehrClient.post<CanteenSaleType>(`/canteen-sales/${id}/complete/`, data),
  cancel: (id: string, reason: string) =>
    ehrClient.post<CanteenSaleType>(`/canteen-sales/${id}/cancel/`, { reason }),
  dashboard: (params?: Record<string, string>) =>
    ehrClient.get<CanteenDashboard>(`/canteen-sales/dashboard/${canteenQs(params)}`),
};

export const canteenShiftSessionsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<CanteenShiftSessionListItem>>(`/canteen-shift-sessions/${canteenQs(params)}`),
  get: (id: string) => ehrClient.get<CanteenShiftSessionType>(`/canteen-shift-sessions/${id}/`),
  /** 204 when the outlet has no open session. */
  current: (outletId: string) =>
    ehrClient.get<CanteenShiftSessionType>(`/canteen-shift-sessions/current/?outlet=${outletId}`),
  open: (data: CanteenShiftOpenPayload) =>
    ehrClient.post<CanteenShiftSessionType>(`/canteen-shift-sessions/open/`, data),
  register: (id: string) =>
    ehrClient.get<CanteenShiftSessionType>(`/canteen-shift-sessions/${id}/register/`),
  refreshCounts: (id: string) =>
    ehrClient.post<CanteenShiftSessionType>(`/canteen-shift-sessions/${id}/refresh-counts/`, {}),
  submitCounts: (id: string, data: CanteenShiftCountSubmitPayload) =>
    ehrClient.post<CanteenShiftSessionType>(`/canteen-shift-sessions/${id}/count/`, data),
  close: (id: string, data: CanteenShiftClosePayload) =>
    ehrClient.post<CanteenShiftSessionType>(`/canteen-shift-sessions/${id}/close/`, data),
  reopen: (id: string, reason: string) =>
    ehrClient.post<CanteenShiftSessionType>(`/canteen-shift-sessions/${id}/reopen/`, { reason }),
};

export const canteenSettingsApi = {
  list: (params?: Record<string, string>) =>
    ehrClient.get<PaginatedResponse<CanteenSettingsType>>(`/canteen-settings/${canteenQs(params)}`),
  get: (id: string) => ehrClient.get<CanteenSettingsType>(`/canteen-settings/${id}/`),
  create: (data: Partial<CanteenSettingsType>) =>
    ehrClient.post<CanteenSettingsType>(`/canteen-settings/`, data),
  update: (id: string, data: Partial<CanteenSettingsType>) =>
    ehrClient.patch<CanteenSettingsType>(`/canteen-settings/${id}/`, data),
};

export const canteenReportsApi = {
  dailySales: (params?: Record<string, string>) =>
    ehrClient.get<CanteenDailySalesReport>(`/canteen-reports/daily-sales/${canteenQs(params)}`),
  /** The report their previous system got wrong. */
  closingStock: (params?: Record<string, string>) =>
    ehrClient.get<CanteenClosingStockReport>(`/canteen-reports/closing-stock/${canteenQs(params)}`),
  itemWiseSales: (params?: Record<string, string>) =>
    ehrClient.get<{ rows: { menu_item_name_snapshot: string; category_name_snapshot: string; qty: string; amount: string }[]; item_count: number }>(
      `/canteen-reports/item-wise-sales/${canteenQs(params)}`),
  cashSummary: (params?: Record<string, string>) =>
    ehrClient.get<{ rows: Record<string, unknown>[]; totals: Record<string, string> }>(
      `/canteen-reports/cash-summary/${canteenQs(params)}`),
  ipCharges: (params?: Record<string, string>) =>
    ehrClient.get<{ rows: Record<string, unknown>[]; total: string }>(
      `/canteen-reports/ip-charges/${canteenQs(params)}`),
  variance: (params?: Record<string, string>) =>
    ehrClient.get<CanteenVarianceReport>(`/canteen-reports/variance/${canteenQs(params)}`),
  shiftRegister: (params?: Record<string, string>) =>
    ehrClient.get<{ sessions: Record<string, unknown>[] }>(
      `/canteen-reports/shift-register/${canteenQs(params)}`),
  itemMovement: (params?: Record<string, string>) =>
    ehrClient.get<{ rows: Record<string, unknown>[] }>(
      `/canteen-reports/item-movement/${canteenQs(params)}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// Dietary / Clinical Nutrition
// ─────────────────────────────────────────────────────────────────────────────

/** Local query-string helper. NOT a module-scope `qs` — this file already has
 *  several and a fourth would shadow one of them (see the canteen skill). */
function dietQs(params?: Record<string, unknown>): string {
  if (!params) return "";
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return "";
  return "?" + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

/** CRUD for one dietary master. Every master has the same surface, so they are
 *  built from one factory rather than twenty copies. */
function dietMaster<T>(path: string) {
  return {
    list: (params?: Record<string, unknown>) =>
      ehrClient.get<PaginatedResponse<T>>(`/dietary/${path}/${dietQs(params)}`),
    get: (id: string) => ehrClient.get<T>(`/dietary/${path}/${id}/`),
    create: (data: Partial<T>) => ehrClient.post<T>(`/dietary/${path}/`, data),
    update: (id: string, data: Partial<T>) => ehrClient.patch<T>(`/dietary/${path}/${id}/`, data),
    /** Soft delete — the backend deactivates rather than removing, so a diet
     *  type referenced by a year of plans stays readable in their history. */
    deactivate: (id: string) => ehrClient.delete<void>(`/dietary/${path}/${id}/`),
  };
}

export const dietaryApi = {
  masters: {
    dietTypes: dietMaster<DietType>("diet-types"),
    consistencies: dietMaster<DietConsistency>("consistencies"),
    feedingRoutes: dietMaster<FeedingRoute>("feeding-routes"),
    mealSlots: dietMaster<MealSlot>("meal-slots"),
    reviewIntervals: dietMaster<ReviewInterval>("review-intervals"),
    allergens: dietMaster<DietaryMaster>("allergens"),
    preferenceTags: dietMaster<DietPreferenceTag>("preference-tags"),
    giSymptoms: dietMaster<GiSymptom>("gi-symptoms"),
    changeReasons: dietMaster<DietChangeReason>("change-reasons"),
    nutritionDiagnoses: dietMaster<NutritionDiagnosisMaster>("nutrition-diagnoses"),
    sgaGrades: dietMaster<SgaGrade>("sga-grades"),
    functionalStatuses: dietMaster<FunctionalStatus>("functional-statuses"),
    metabolicStressLevels: dietMaster<MetabolicStressLevel>("metabolic-stress-levels"),
    bmiBands: dietMaster<BmiBand>("bmi-bands"),
    measurementUnits: dietMaster<MeasurementUnit>("measurement-units"),
    shifts: dietMaster<DietShift>("shifts"),
    foodItems: dietMaster<FoodItem>("food-items"),
    wardAssignments: dietMaster<DieticianWardAssignment>("ward-assignments"),
  },

  /** Admin settings — the three things that were API-only until 2026-08-25. */
  settings: {
    /** Per-location print language config (CRUD). */
    printConfig: {
      list: (params?: Record<string, string | undefined>) =>
        ehrClient.get<PaginatedResponse<DietaryPrintConfig>>(
          `/dietary/print-config/${dietQs(params ?? {})}`),
      create: (data: Partial<DietaryPrintConfig>) =>
        ehrClient.post<DietaryPrintConfig>("/dietary/print-config/", data),
      update: (id: string, data: Partial<DietaryPrintConfig>) =>
        ehrClient.patch<DietaryPrintConfig>(`/dietary/print-config/${id}/`, data),
      remove: (id: string) => ehrClient.delete<void>(`/dietary/print-config/${id}/`),
      /** What actually applies here after the fallback chain. The composer
       *  reads THIS, never the raw rows — one payload, so the tabs it shows and
       *  the default it preselects cannot disagree with what saves. */
      effective: (locationId?: string) =>
        ehrClient.get<EffectivePrintConfig>(
          `/dietary/print-config/effective/${dietQs({ location: locationId })}`),
    },
    /** Biochemical panel — which analytes pull onto the assessment. */
    labPanels: {
      list: (params?: Record<string, string | boolean | number | undefined>) =>
        ehrClient.get<PaginatedResponse<DieteticLabPanel>>(
          `/dietary/lab-panels/${dietQs(params ?? {})}`),
      get: (id: string) => ehrClient.get<DieteticLabPanel>(`/dietary/lab-panels/${id}/`),
      create: (data: Partial<DieteticLabPanel>) =>
        ehrClient.post<DieteticLabPanel>("/dietary/lab-panels/", data),
      /** Child contract: `parameters` absent = leave alone, `[]` = clear.
       *  Never PATCH before the editor has hydrated. */
      update: (id: string, data: Partial<DieteticLabPanel>) =>
        ehrClient.patch<DieteticLabPanel>(`/dietary/lab-panels/${id}/`, data),
      deactivate: (id: string) => ehrClient.delete<void>(`/dietary/lab-panels/${id}/`),
    },
  },

  exchangeGroups: {
    list: (params?: Record<string, unknown>) =>
      ehrClient.get<PaginatedResponse<ExchangeGroup>>(`/dietary/exchange-groups/${dietQs(params)}`),
    get: (id: string) => ehrClient.get<ExchangeGroup>(`/dietary/exchange-groups/${id}/`),
    create: (data: Partial<ExchangeGroup>) =>
      ehrClient.post<ExchangeGroup>("/dietary/exchange-groups/", data),
    update: (id: string, data: Partial<ExchangeGroup>) =>
      ehrClient.patch<ExchangeGroup>(`/dietary/exchange-groups/${id}/`, data),
  },

  templates: {
    list: (params?: Record<string, unknown>) =>
      ehrClient.get<PaginatedResponse<DietChartTemplateListItem>>(
        `/dietary/chart-templates/${dietQs(params)}`),
    get: (id: string) => ehrClient.get<DietChartTemplate>(`/dietary/chart-templates/${id}/`),
    create: (data: Partial<DietChartTemplate>) =>
      ehrClient.post<DietChartTemplate>("/dietary/chart-templates/", data),
    /** NOTE: omitting `blocks` leaves the chart untouched; sending `[]` clears
     *  it. Never PATCH from a form that has not finished hydrating. */
    update: (id: string, data: Partial<DietChartTemplate>) =>
      ehrClient.patch<DietChartTemplate>(`/dietary/chart-templates/${id}/`, data),
    preview: (id: string, language?: PrintLanguage) =>
      ehrClient.get<ChartPreviewResponse>(
        `/dietary/chart-templates/${id}/preview/${dietQs({ language })}`),
    duplicate: (id: string) =>
      ehrClient.post<DietChartTemplate>(`/dietary/chart-templates/${id}/duplicate/`, {}),
    deactivate: (id: string) => ehrClient.delete<void>(`/dietary/chart-templates/${id}/`),
  },

  assessments: {
    list: (params?: Record<string, unknown>) =>
      ehrClient.get<PaginatedResponse<DieteticAssessmentListItem>>(
        `/dietary/assessments/${dietQs(params)}`),
    get: (id: string) => ehrClient.get<DieteticAssessment>(`/dietary/assessments/${id}/`),
    create: (data: Partial<DieteticAssessment>) =>
      ehrClient.post<DieteticAssessment>("/dietary/assessments/", data),
    update: (id: string, data: Partial<DieteticAssessment>) =>
      ehrClient.patch<DieteticAssessment>(`/dietary/assessments/${id}/`, data),
    /** Signs the assessment, snapshots anthropometry off the vitals row,
     *  advances the encounter and schedules the review. */
    finalize: (id: string) =>
      ehrClient.post<DieteticAssessment>(`/dietary/assessments/${id}/finalize/`, {}),
    /** Partner-lab PDFs for this patient — a document to READ, not values to
     *  pull. Listed so the dietician can open the report instead of not
     *  knowing it exists. */
    externalReports: (patientId: string) =>
      ehrClient.get<DieteticExternalReport[]>(
        `/dietary/assessments/external-reports/${dietQs({ patient: patientId })}`),
    /** Fill blank clinical fields from the doctor's consultation — diet advice,
     *  diagnosis, medications. Blank fields only; never overwrites the
     *  dietician's own wording. Runs automatically on create; this is for when
     *  the doctor writes AFTER the dietician opened the form. */
    pullDoctorContext: (id: string) =>
      ehrClient.post<{ filled: string[]; assessment: DieteticAssessment }>(
        `/dietary/assessments/${id}/pull-doctor-context/`, {}),
    /** Fill the Biochemical section from the patient's OWN verified results.
     *  Linked, not copied — each row keeps a FK to the real LabResultValue.
     *  Hand-typed outside reports are left untouched. */
    pullLabs: (id: string) =>
      ehrClient.post<{ pulled: number; lab_references: DieteticLabReference[] }>(
        `/dietary/assessments/${id}/pull-labs/`, {}),
    /** Live BMI / band / IBW for a height-weight pair. READ-ONLY: writes
     *  nothing, so the assessment's own columns stay a finalize-time snapshot.
     *  Server-side because the BMI formula lives on `Vitals` and the band and
     *  IBW come from per-hospital masters — computing any of it here would
     *  hardcode cutoffs that differ between WHO and Asian-Indian standards. */
    deriveAnthropometry: (params: { height_cm?: string; weight_kg?: string; patient?: string }) =>
      ehrClient.get<DerivedAnthropometry>(
        `/dietary/assessments/derive-anthropometry/${dietQs(params)}`),
    /** The effective form layout. Served by the API so the asterisks the
     *  dietician sees and the rules finalize enforces cannot disagree. */
    formConfig: (formKind?: string) =>
      ehrClient.get<DieteticFormConfig>(
        `/dietary/assessments/form-config/${dietQs({ form_kind: formKind })}`),
    history: (patientId: string) =>
      ehrClient.get<DieteticAssessmentListItem[]>(
        `/dietary/assessments/history/${dietQs({ patient: patientId })}`),
  },

  plans: {
    list: (params?: Record<string, unknown>) =>
      ehrClient.get<PaginatedResponse<DietPlanListItem>>(`/dietary/plans/${dietQs(params)}`),
    get: (id: string) => ehrClient.get<DietPlan>(`/dietary/plans/${id}/`),
    create: (data: Partial<DietPlan>) => ehrClient.post<DietPlan>("/dietary/plans/", data),
    update: (id: string, data: Partial<DietPlan>) =>
      ehrClient.patch<DietPlan>(`/dietary/plans/${id}/`, data),
    /** Both of these REBUILD `document` from the template, discarding any
     *  per-patient customisation. The server refuses with
     *  `code: "chart_customised"` once the chart has been hand-edited; pass
     *  `force` only after the dietician has confirmed losing those edits. */
    applyTemplate: (
      id: string, templateId: string,
      blankValues?: Record<string, string>, force?: boolean,
    ) =>
      ehrClient.post<DietPlan>(`/dietary/plans/${id}/apply-template/`, {
        template: templateId, blank_values: blankValues, force,
      }),
    fillBlanks: (id: string, blankValues: Record<string, string>, force?: boolean) =>
      ehrClient.post<DietPlan>(
        `/dietary/plans/${id}/fill-blanks/`, { blank_values: blankValues, force }),
    /** Store the dietician's hand edits. The client requirement the whole
     *  screen exists for: the pre-made formats "aren't used as-is — they're
     *  modified specifically for that patient." */
    editDocument: (id: string, document: ResolvedChartBlock[]) =>
      ehrClient.post<DietPlan>(`/dietary/plans/${id}/edit-document/`, { document }),
    /** Makes this the plan the ward and kitchen read, and cuts the meal orders. */
    activate: (id: string) => ehrClient.post<DietPlan>(`/dietary/plans/${id}/activate/`, {}),
    revise: (id: string, changeReason?: string, changeNote?: string) =>
      ehrClient.post<DietPlan>(`/dietary/plans/${id}/revise/`, {
        change_reason: changeReason, change_note: changeNote,
      }),
    doctorConfirm: (id: string, note?: string) =>
      ehrClient.post<DietPlan>(`/dietary/plans/${id}/doctor-confirm/`, { note }),
    recordCounselling: (id: string, attendees: string, note?: string) =>
      ehrClient.post<DietPlan>(`/dietary/plans/${id}/record-counselling/`, { attendees, note }),
    markPrinted: (id: string) =>
      ehrClient.post<DietPlan>(`/dietary/plans/${id}/mark-printed/`, {}),
    discontinue: (id: string, reason?: string) =>
      ehrClient.post<DietPlan>(`/dietary/plans/${id}/discontinue/`, { reason }),
  },

  orders: {
    list: (params?: Record<string, unknown>) =>
      ehrClient.get<PaginatedResponse<DietOrder>>(`/dietary/orders/${dietQs(params)}`),
    update: (id: string, data: Partial<DietOrder>) =>
      ehrClient.patch<DietOrder>(`/dietary/orders/${id}/`, data),
    /** The noon checkpoint — a whole ward in one request, because one call per
     *  bed would make the screen slower than the paper list it replaces. */
    confirmBulk: (ids: string[]) =>
      ehrClient.post<{ confirmed: number; requested: number }>(
        "/dietary/orders/confirm-bulk/", { ids }),
    sendToKitchen: (ids: string[]) =>
      ehrClient.post<{ sent: number; requested: number }>(
        "/dietary/orders/send-to-kitchen/", { ids }),
    markServed: (id: string) =>
      ehrClient.post<DietOrder>(`/dietary/orders/${id}/mark-served/`, {}),
    generate: (planId: string) =>
      ehrClient.post<{ created: number }>("/dietary/orders/generate/", { plan: planId }),
  },

  rounds: {
    list: (params?: Record<string, unknown>) =>
      ehrClient.get<PaginatedResponse<DieteticRound>>(`/dietary/rounds/${dietQs(params)}`),
    start: (admissionId: string, opts?: { shift?: string; priority_bucket?: string }) =>
      ehrClient.post<DieteticRound>("/dietary/rounds/start/", {
        admission: admissionId, ...opts,
      }),
    fileReviewed: (id: string) =>
      ehrClient.post<DieteticRound>(`/dietary/rounds/${id}/file-reviewed/`, {}),
    visited: (id: string) => ehrClient.post<DieteticRound>(`/dietary/rounds/${id}/visited/`, {}),
    complete: (id: string, data?: {
      notes?: string; tolerance_note?: string; plan_changed?: boolean;
    }) => ehrClient.post<DieteticRound>(`/dietary/rounds/${id}/complete/`, data ?? {}),
  },

  followups: {
    list: (params?: Record<string, unknown>) =>
      ehrClient.get<PaginatedResponse<DieteticFollowUp>>(`/dietary/followups/${dietQs(params)}`),
    update: (id: string, data: Partial<DieteticFollowUp>) =>
      ehrClient.patch<DieteticFollowUp>(`/dietary/followups/${id}/`, data),
  },

  worklist: (params?: { date?: string; status?: string }) =>
    ehrClient.get<DieticianWorklistResponse>(`/dietary/worklist/${dietQs(params)}`),

  wardRounds: (params?: {
    date?: string; nursing_unit?: string; mine?: string; search?: string;
  }) => ehrClient.get<WardRoundResponse>(`/dietary/ward-rounds/${dietQs(params)}`),

  kitchenSheet: (params?: { date?: string; meal_slot?: string; nursing_unit?: string }) =>
    ehrClient.get<KitchenSheetResponse>(`/dietary/kitchen-sheet/${dietQs(params)}`),

  patientDiet: (admissionId: string) =>
    ehrClient.get<PatientDietSummary>(`/dietary/admissions/${admissionId}/diet/`),
};

// ── WhatsApp Booking — per-hospital Meta connection ─────────────────────────
// No hospital/location in any path: scoping comes from the JWT, same as abdmHieApi.
export const whatsappApi = {
  getConnection: () =>
    ehrClient.get<WhatsAppConnectionResponse>(`/whatsapp/connection/`),

  connect: (data: WhatsAppConnectPayload) =>
    ehrClient.post<WhatsAppConnectionResponse>(`/whatsapp/connect/`, data),

  // DEV MODE ONLY — 404s in production. Lets a tester attach Meta's free
  // WhatsApp test number while Tech Provider approval is pending.
  devConnect: (data: { waba_id: string; phone_number_id: string; access_token: string }) =>
    ehrClient.post<WhatsAppConnectionResponse>(`/whatsapp/dev-connect/`, data),

  signupCancelled: (data: { current_step?: string; error_message?: string }) =>
    ehrClient.post<WhatsAppConnectionResponse>(`/whatsapp/signup-cancelled/`, data),

  disconnect: () =>
    ehrClient.post<WhatsAppConnectionResponse>(`/whatsapp/disconnect/`, {}),

  refresh: () =>
    ehrClient.post<WhatsAppConnectionResponse>(`/whatsapp/refresh/`, {}),

  resubscribeWebhooks: () =>
    ehrClient.post<WhatsAppConnectionResponse>(`/whatsapp/resubscribe-webhooks/`, {}),

  updateNumber: (id: string, data: WhatsAppNumberUpdate) =>
    ehrClient.patch<WhatsAppNumber>(`/whatsapp/numbers/${id}/`, data),

  registerNumber: (id: string) =>
    ehrClient.post<{ success: boolean; number: WhatsAppNumber }>(
      `/whatsapp/numbers/${id}/register/`, {},
    ),

  testSend: (data: { to: string; body?: string; number?: string }) =>
    ehrClient.post<WhatsAppTestSendResponse>(`/whatsapp/test-send/`, data),

  messageLogs: (limit = 20) =>
    ehrClient.get<{ count: number; results: WhatsAppMessageLogEntry[] }>(
      `/whatsapp/message-logs/?limit=${limit}`,
    ),
};
